import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    Handle,
    Position,
    getBezierPath,
    BaseEdge,
    EdgeLabelRenderer,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
    Save, Plus, Zap, Filter, Mail, Clock, GitBranch, Settings,
    Play, Pause, Trash2, Edit2, Eye, Loader, X, CheckCircle,
    Code, Map, AlertCircle, AlertTriangle, CheckCircle2, Rocket, History, TestTube,
    Brain, ChevronLeft, ChevronRight, List, XCircle, RefreshCcw, ChevronDown, Search, Upload, Copy
} from 'lucide-react';
import { apiClient } from '../../config/api';
import { useNavigate, useParams } from 'react-router-dom';
import UnlayerEditor from '../../components/UnlayerEditor';

// Custom Node Components with Add and Delete Buttons (n8n-style, inside the node)
const createNodeWrapper = (NodeContent, bgColor, borderColor) => {
    return ({ data, selected, id }) => {
        const [showButtons, setShowButtons] = useState(false);
        
        return (
            <div 
                className={`relative px-4 py-3 ${bgColor} border-2 rounded-lg shadow-sm min-w-[180px] max-w-[220px] ${
                    selected ? borderColor : borderColor.replace('500', '200')
                }`}
                onMouseEnter={() => setShowButtons(true)}
                onMouseLeave={() => setShowButtons(false)}
            >
                <NodeContent data={data} selected={selected} />
                {/* Add button positioned inside node, top-right corner */}
                {showButtons && data.onAddNode && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (data.onAddNode) {
                                data.onAddNode(id);
                            }
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                        }}
                        className="nodrag nopan absolute top-1 right-1 w-5 h-5 bg-blue-600 text-white rounded flex items-center justify-center shadow-md hover:bg-blue-700 z-20"
                        style={{ pointerEvents: 'auto' }}
                        title="Add new node (automatically connected)"
                    >
                        <Plus size={12} />
                    </button>
                )}
                {/* Delete button positioned inside node, top-left corner */}
                {showButtons && data.onDeleteNode && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (data.onDeleteNode && window.confirm('Delete this node and all its connections?')) {
                                data.onDeleteNode(id);
                            }
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                        }}
                        className="nodrag nopan absolute top-1 left-1 w-5 h-5 bg-red-600 text-white rounded flex items-center justify-center shadow-md hover:bg-red-700 z-20"
                        style={{ pointerEvents: 'auto' }}
                        title="Delete node"
                    >
                        <Trash2 size={12} />
                    </button>
                )}
            </div>
        );
    };
};

const TriggerNodeContent = ({ data, selected }) => {
    return (
        <>
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div className="flex items-center gap-2 mb-1">
                <Zap size={16} className="text-blue-600" />
                <span className="font-semibold text-blue-900">Trigger</span>
            </div>
            <div className="text-sm text-blue-700">{data.label || 'Untitled Trigger'}</div>
            {data.description && (
                <div className="text-xs text-blue-600 mt-1">{data.description}</div>
            )}
        </>
    );
};

const TriggerNode = createNodeWrapper(TriggerNodeContent, 'bg-blue-50', 'border-blue-500');

// Manual Start Trigger Node
const ManualTriggerNodeContent = ({ data, selected }) => {
    return (
        <>
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div className="flex items-center gap-2 mb-1">
                <Play size={16} className="text-blue-600" />
                <span className="font-semibold text-blue-900">Manual Start</span>
            </div>
            <div className="text-sm text-blue-700">{data.label || 'Start Manually'}</div>
            {data.description && (
                <div className="text-xs text-blue-600 mt-1">{data.description}</div>
            )}
        </>
    );
};

const ManualTriggerNode = createNodeWrapper(ManualTriggerNodeContent, 'bg-blue-50', 'border-blue-500');

// Schedule Trigger Node
const ScheduleTriggerNodeContent = ({ data, selected }) => {
    return (
        <>
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div className="flex items-center gap-2 mb-1">
                <Clock size={16} className="text-purple-600" />
                <span className="font-semibold text-purple-900">Schedule</span>
            </div>
            <div className="text-sm text-purple-700">{data.label || 'Scheduled Trigger'}</div>
            {data.cronExpression && (
                <div className="text-xs text-purple-600 mt-1">Cron: {data.cronExpression}</div>
            )}
            {data.scheduleDescription && (
                <div className="text-xs text-purple-600 mt-1">{data.scheduleDescription}</div>
            )}
        </>
    );
};

const ScheduleTriggerNode = createNodeWrapper(ScheduleTriggerNodeContent, 'bg-purple-50', 'border-purple-500');

// Webhook Trigger Node
const WebhookTriggerNodeContent = ({ data, selected }) => {
    const webhookGuid = data.webhookGuid || data.webhook_guid;
    return (
        <>
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div className="flex items-center gap-2 mb-1">
                <Zap size={16} className="text-green-600" />
                <span className="font-semibold text-green-900">Webhook</span>
            </div>
            <div className="text-sm text-green-700">{data.label || 'Webhook Trigger'}</div>
            {webhookGuid && (
                <div className="text-xs text-green-600 mt-1 truncate font-mono" title={webhookGuid}>
                    GUID: {webhookGuid.substring(0, 8)}...
                </div>
            )}
            {data.description && (
                <div className="text-xs text-green-600 mt-1">{data.description}</div>
            )}
        </>
    );
};

const WebhookTriggerNode = createNodeWrapper(WebhookTriggerNodeContent, 'bg-green-50', 'border-green-500');

const FilterNodeContent = ({ data, selected }) => {
    return (
        <>
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div className="flex items-center gap-2 mb-1">
                <Filter size={16} className="text-purple-600" />
                <span className="font-semibold text-purple-900">Filter</span>
            </div>
            <div className="text-sm text-purple-700">{data.label || 'Untitled Filter'}</div>
            {data.sqlFilter && (
                <div className="text-xs text-purple-600 mt-1 truncate">
                    {data.sqlFilter.substring(0, 30)}...
                </div>
            )}
        </>
    );
};

const FilterNode = createNodeWrapper(FilterNodeContent, 'bg-purple-50', 'border-purple-500');

const MessageNodeContent = ({ data, selected }) => {
    return (
        <>
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div className="flex items-center gap-2 mb-1">
                <Mail size={16} className="text-green-600" />
                <span className="font-semibold text-green-900">Message</span>
            </div>
            <div className="text-sm text-green-700">{data.label || 'Untitled Message'}</div>
            {data.messageName && (
                <div className="text-xs text-green-600 mt-1 truncate" title={data.messageName}>{data.messageName}</div>
            )}
        </>
    );
};

const MessageNode = createNodeWrapper(MessageNodeContent, 'bg-green-50', 'border-green-500');

const DelayNodeContent = ({ data, selected }) => {
    return (
        <>
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div className="flex items-center gap-2 mb-1">
                <Clock size={16} className="text-orange-600" />
                <span className="font-semibold text-orange-900">Delay</span>
            </div>
            <div className="text-sm text-orange-700">{data.label || 'Untitled Delay'}</div>
            {data.delayAmount && (
                <div className="text-xs text-orange-600 mt-1">
                    {data.delayAmount} {data.delayUnit || 'days'}
                </div>
            )}
        </>
    );
};

const DelayNode = createNodeWrapper(DelayNodeContent, 'bg-orange-50', 'border-orange-500');

const ConditionNodeContent = ({ data, selected }) => {
    return (
        <>
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle 
                type="source" 
                position={Position.Bottom} 
                id="true" 
                style={{ background: '#22c55e', left: '25%', transform: 'translateX(-50%)' }} 
            />
            <Handle 
                type="source" 
                position={Position.Bottom} 
                id="false" 
                style={{ background: '#ef4444', left: '75%', transform: 'translateX(-50%)' }} 
            />
            <div className="flex items-center gap-2 mb-1">
                <GitBranch size={16} className="text-yellow-600" />
                <span className="font-semibold text-yellow-900">Condition</span>
            </div>
            <div className="text-sm text-yellow-700">{data.label || 'Untitled Condition'}</div>
            <div className="text-xs text-gray-500 mt-1 flex gap-2">
                <span>True (green)</span>
                <span>False (red)</span>
            </div>
        </>
    );
};

const ConditionNode = createNodeWrapper(ConditionNodeContent, 'bg-yellow-50', 'border-yellow-500');

const ActionNodeContent = ({ data, selected }) => {
    const actionType = data.config?.actionType || 'UpdateContact';
    const actionTypeLabels = {
        'UpdateContact': 'Update Contact',
        'NotifyContacts': 'Notify Contacts',
        'LogEvent': 'Log Event'
    };
    
    // Get summary based on action type
    const getActionSummary = () => {
        if (!data.config) return '';
        
        switch (actionType) {
            case 'UpdateContact':
                if (Array.isArray(data.config.changes) && data.config.changes.length > 0) {
                    const fieldNames = data.config.changes.map(c => c.name).filter(Boolean);
                    return fieldNames.length > 0 ? `Fields: ${fieldNames.join(', ')}` : 'Fields configured';
                }
                return 'No fields configured';
            case 'NotifyContacts':
                const type = data.config.notificationType || 'email';
                const recipients = data.config.notificationRecipients || 'Not set';
                return `${type}: ${recipients.substring(0, 20)}${recipients.length > 20 ? '...' : ''}`;
            case 'LogEvent':
                return data.config.eventName || 'No event name';
            default:
                return '';
        }
    };

    return (
        <>
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div className="flex items-center gap-2 mb-1">
                <Settings size={16} className="text-red-600" />
                <span className="font-semibold text-red-900">Action</span>
            </div>
            <div className="text-sm text-red-700 font-medium">{actionTypeLabels[actionType] || 'Action'}</div>
            {getActionSummary() && (
                <div className="text-xs text-red-600 mt-1">{getActionSummary()}</div>
            )}
        </>
    );
};

const ActionNode = createNodeWrapper(ActionNodeContent, 'bg-red-50', 'border-red-500');

const ListSelectorNodeContent = ({ data, selected }) => {
    return (
        <>
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div className="flex items-center gap-2 mb-1">
                <List size={16} className="text-indigo-600" />
                <span className="font-semibold text-indigo-900">List Selector</span>
            </div>
            <div className="text-sm text-indigo-700">{data.label || 'Untitled List Selector'}</div>
            {data.listName && (
                <div className="text-xs text-indigo-600 mt-1">{data.listName}</div>
            )}
        </>
    );
};

const ListSelectorNode = createNodeWrapper(ListSelectorNodeContent, 'bg-indigo-50', 'border-indigo-500');

// Send Via API Node — sends data to an external API integration
const SendViaApiNodeContent = ({ data, selected }) => {
    return (
        <>
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div className="flex items-center gap-2 mb-1">
                <Rocket size={16} className="text-indigo-600" />
                <span className="font-semibold text-indigo-900">Send Via API</span>
            </div>
            <div className="text-sm text-indigo-700">{data.label || 'Untitled API Action'}</div>
            {data.apiDisplayName && (
                <div className="text-xs text-indigo-600 mt-1">
                    API: {data.apiDisplayName}
                </div>
            )}
            {data.locationName && (
                <div className="text-xs text-indigo-500 mt-0.5">
                    Location: {data.locationName}
                </div>
            )}
            {data.endpointDisplayName && (
                <div className="text-xs text-indigo-400 mt-0.5">
                    Section: {data.endpointDisplayName}
                </div>
            )}
        </>
    );
};

const SendViaApiNode = createNodeWrapper(SendViaApiNodeContent, 'bg-indigo-50', 'border-indigo-500');

const nodeTypes = {
    trigger: TriggerNode,
    trigger_manual: ManualTriggerNode,
    trigger_schedule: ScheduleTriggerNode,
    trigger_webhook: WebhookTriggerNode,
    filter: FilterNode,
    message: MessageNode,
    delay: DelayNode,
    condition: ConditionNode,
    action: ActionNode,
    listSelector: ListSelectorNode,
    sendViaApi: SendViaApiNode,
};

// Custom Edge Component with Delete and Insert Buttons
const CustomEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, selected, markerEnd, data }) => {
    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    });

    const [showControls, setShowControls] = useState(false);

    // Make edge clickable for deletion - single click to delete
    const handleEdgeClick = (e) => {
        e.stopPropagation();
        if (data?.onDelete && window.confirm('Delete this connection?')) {
            data.onDelete(id);
        }
    };

    return (
        <>
            {/* Clickable edge path - click to delete */}
            <g onClick={handleEdgeClick} style={{ cursor: 'pointer' }}>
                <BaseEdge 
                    path={edgePath} 
                    markerEnd={markerEnd}
                    style={{
                        ...style,
                        strokeWidth: selected ? 3 : 2,
                        stroke: selected ? '#3b82f6' : style.stroke || '#b1b1b7',
                    }} 
                />
            </g>
            <EdgeLabelRenderer>
                <div
                    style={{
                        position: 'absolute',
                        transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                        pointerEvents: 'none',
                    }}
                    className="nodrag nopan"
                    onMouseEnter={() => setShowControls(true)}
                    onMouseLeave={() => setShowControls(false)}
                >
                    {showControls && (
                        <div 
                            className="flex items-center gap-1 bg-white border border-gray-300 rounded-lg shadow-lg p-1"
                            style={{ pointerEvents: 'auto' }}
                        >
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (data?.onDelete && window.confirm('Delete this connection?')) {
                                        data.onDelete(id);
                                    }
                                }}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="Delete connection (or click edge)"
                            >
                                <Trash2 size={14} />
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (data?.onInsert) data.onInsert(id);
                                }}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                title="Insert node"
                            >
                                <Plus size={14} />
                            </button>
                        </div>
                    )}
                </div>
            </EdgeLabelRenderer>
        </>
    );
};

const AdminWorkflowBuilderPage = ({ canEdit = true, canEditVb = true }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [workflow, setWorkflow] = useState(null);
    const isVbWorkflow = !!workflow?.voicebox_contact_plan_id;
    const readOnly = !canEdit || (isVbWorkflow && !canEditVb);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showNodePalette, setShowNodePalette] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [showNodeConfig, setShowNodeConfig] = useState(false);
    const [workflowName, setWorkflowName] = useState('');
    const [workflowDescription, setWorkflowDescription] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [selectedLocationIds, setSelectedLocationIds] = useState([]);
    const [targetAllLocations, setTargetAllLocations] = useState(false);
    const [groups, setGroups] = useState([]);
    const [locations, setLocations] = useState([]);
    const [generatingMessageSuite, setGeneratingMessageSuite] = useState(false);
    const [messageSuiteStatus, setMessageSuiteStatus] = useState(null); // 'success' | 'error' | null
    const [migratingContactPlans, setMigratingContactPlans] = useState(false);
    const [migrationSummary, setMigrationSummary] = useState(null);
    const [generatedSql, setGeneratedSql] = useState(null);
    const [generatingSql, setGeneratingSql] = useState(false);
    const [showSqlPreview, setShowSqlPreview] = useState(false);
    const [showVariableMapping, setShowVariableMapping] = useState(false);
    const [variableMappings, setVariableMappings] = useState([]);
    const [availableFields, setAvailableFields] = useState([]);
    const [messageCatalog, setMessageCatalog] = useState([]);
    const [triggerCatalog, setTriggerCatalog] = useState([]);
    const [executing, setExecuting] = useState(false);
    const [syncingMessages, setSyncingMessages] = useState(false);
    const [syncMessagesStatus, setSyncMessagesStatus] = useState(null); // 'success' | 'error' | null
    const [executionLogs, setExecutionLogs] = useState([]);
    const [showExecutionLogs, setShowExecutionLogs] = useState(false);
    const [pendingAddAfterNode, setPendingAddAfterNode] = useState(null);
    const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
    const [availableLists, setAvailableLists] = useState([]);
    const [listsError, setListsError] = useState(null);
    const [loadingLists, setLoadingLists] = useState(false);
    const [messageFolders, setMessageFolders] = useState([]);
    const [listFolders, setListFolders] = useState([]);
    const [apiIntegrations, setApiIntegrations] = useState([]);
    const [syncingClient, setSyncingClient] = useState(false);
    const [syncStatus, setSyncStatus] = useState({ lists: null, messages: null }); // 'syncing', 'success', 'error', null
    const [workflowTemplates, setWorkflowTemplates] = useState([]);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [templateDescription, setTemplateDescription] = useState('');
    const [templateCategory, setTemplateCategory] = useState('');
    const [exportingToVb, setExportingToVb] = useState(false);
    const [vbExportResult, setVbExportResult] = useState(null);
    const [pendingModalConfig, setPendingModalConfig] = useState(null);
    const [showCopyToVbModal, setShowCopyToVbModal] = useState(false);
    const [copyingToVb, setCopyingToVb] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            loadWorkflow();
            loadVariableMappings();
        }
        loadAvailableFields();
        loadMessageCatalog();
        loadGroups();
        loadWorkflowTemplates();
        
        // Check for template parameter in URL (for new workflows)
        if (!id) {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('template') === 'true') {
                setShowTemplateModal(true);
                // Clean up URL
                window.history.replaceState({}, '', window.location.pathname);
            }
        }
    }, [id]);

    const loadWorkflowTemplates = async () => {
        try {
            const result = await apiClient.get('/workflows/templates');
            setWorkflowTemplates(result.templates || []);
        } catch (error) {
            console.error('Failed to load workflow templates:', error);
            setWorkflowTemplates([]);
        }
    };

    const loadGroups = async () => {
        try {
            const result = await apiClient.get('/groups');
            setGroups(result || []);
        } catch (error) {
            console.error('Failed to load groups:', error);
            setGroups([]);
        }
    };

    const loadLocations = async (groupId) => {
        if (!groupId) {
            setLocations([]);
            return;
        }
        try {
            const result = await apiClient.get('/dealer-locations-with-addresses');
            const groupLocations = (result || []).filter(loc => loc.group_id === groupId);
            setLocations(groupLocations);
        } catch (error) {
            console.error('Failed to load locations:', error);
            setLocations([]);
        }
    };

    useEffect(() => {
        if (selectedGroupId) {
            loadLocations(selectedGroupId);
        } else {
            setLocations([]);
        }
    }, [selectedGroupId]);

    const loadVariableMappings = async () => {
        if (!id) return;
        try {
            const result = await apiClient.get(`/workflows/${id}`);
            setVariableMappings(result.variableMappings || []);
        } catch (error) {
            console.error('Failed to load variable mappings:', error);
        }
    };

    const loadAvailableFields = async () => {
        try {
            const result = await apiClient.get('/workflows/variable-mapping-fields');
            setAvailableFields(result.fields || []);
        } catch (error) {
            console.error('Failed to load available fields:', error);
        }
    };

    const loadMessageCatalog = async () => {
        try {
            // If a group is selected, filter messages by that group's VoiceBox client
            let messages = [];
            if (selectedGroupId) {
                try {
                    // Get the VoiceBox mapping for this group
                    const mappingResult = await apiClient.get('/voicebox/client-mappings');
                    const mappings = mappingResult.mappings || [];
                    const mapping = mappings.find(m => String(m.group_id) === String(selectedGroupId));
                    
                    if (mapping && mapping.voicebox_client_id) {
                        // Get messages for this specific client
                        const result = await apiClient.get(`/voicebox/message-catalog?client_id=${mapping.voicebox_client_id}&group_id=${selectedGroupId}`);
                        messages = result.messages || [];
                        console.log(`Loaded ${messages.length} messages for client ${mapping.voicebox_client_id}`);
                    } else {
                        console.warn(`No VoiceBox mapping found for group ${selectedGroupId}`);
                    }
                } catch (e) {
                    console.error('Error loading client-specific messages:', e);
                }
            }
            
            // If no client-specific messages found, load all (only if no group selected)
            if (messages.length === 0 && !selectedGroupId) {
                const result = await apiClient.get('/voicebox/message-catalog');
                messages = result.messages || [];
            }
            
            // Debug: Log message data including tags
            if (messages.length > 0) {
                console.log('Sample message from catalog:', {
                    voicebox_message_id: messages[0].voicebox_message_id,
                    message_name: messages[0].message_name,
                    available_tags: messages[0].available_tags,
                    tags_type: typeof messages[0].available_tags,
                    tags_is_array: Array.isArray(messages[0].available_tags)
                });
            }
            
            setMessageCatalog(messages);
            console.log(`Final message catalog: ${messages.length} messages`);
            if (messages.length === 0 && selectedGroupId) {
                console.warn('No messages found for selected client. You may need to sync messages from Voicebox first.');
            }
        } catch (error) {
            console.error('Failed to load message catalog:', error);
            setMessageCatalog([]);
        }
    };

    const syncMessageCatalogForGroup = async () => {
        if (!selectedGroupId) {
            setSyncMessagesStatus('error');
            return;
        }

        setSyncingMessages(true);
        setSyncMessagesStatus(null);

        try {
            const mappingResult = await apiClient.get('/voicebox/client-mappings');
            const mappings = mappingResult.mappings || [];
            const mapping = mappings.find(m => String(m.group_id) === String(selectedGroupId));

            if (!mapping) {
                throw new Error('No VoiceBox mapping found for this group.');
            }

            await apiClient.get(`/voicebox/client-mappings/${mapping.id}/messages`, {}, 600000);
            // Reload messages filtered by group
            await loadMessageCatalog();
            setSyncMessagesStatus('success');
        } catch (error) {
            console.error('Failed to sync messages:', error);
            setSyncMessagesStatus('error');
        } finally {
            setSyncingMessages(false);
        }
    };

    const loadTriggerCatalog = async () => {
        try {
            // If a group is selected, try to get triggers for that group's VoiceBox client
            let triggers = [];
            if (selectedGroupId) {
                try {
                    // Get the VoiceBox mapping for this group
                    const mappingResult = await apiClient.get('/voicebox/client-mappings');
                    const mappings = mappingResult.mappings || [];
                    // Ensure we compare UUIDs correctly (both as strings)
                    const mapping = mappings.find(m => String(m.group_id) === String(selectedGroupId));
                    
                    console.log('Selected group ID:', selectedGroupId);
                    console.log('Available mappings:', mappings.map(m => ({ group_id: m.group_id, voicebox_client_id: m.voicebox_client_id })));
                    console.log('Found mapping:', mapping);
                    
                    if (mapping && mapping.voicebox_client_id) {
                        // Get triggers for this specific client
                        console.log(`Loading triggers for VoiceBox client ID: ${mapping.voicebox_client_id}`);
                        try {
                            const catalogResult = await apiClient.get(`/voicebox/trigger-catalog?client_id=${mapping.voicebox_client_id}`);
                            console.log('Trigger catalog API response:', catalogResult);
                            console.log('Response keys:', Object.keys(catalogResult));
                            triggers = catalogResult.triggers || [];
                            console.log(`Loaded ${triggers.length} triggers for client ${mapping.voicebox_client_id}`);
                            
                            if (triggers.length > 0) {
                                console.log('Sample trigger:', triggers[0]);
                            } else {
                                console.warn('No triggers in response array. Full response:', JSON.stringify(catalogResult, null, 2));
                            }
                        } catch (catalogError) {
                            console.error('Error fetching trigger catalog:', catalogError);
                            console.error('Error details:', catalogError.response?.data || catalogError.message);
                            throw catalogError;
                        }
                    } else {
                        console.warn(`No VoiceBox mapping found for group ${selectedGroupId}. Please sync this group on the VoiceBox Sync page.`);
                    }
                } catch (e) {
                    console.error('Error loading triggers for selected client:', e);
                    console.error('Error stack:', e.stack);
                    console.warn('Falling back to all triggers');
                }
            }
            
            // If no client-specific triggers found, load all triggers (only if no group selected)
            if (triggers.length === 0 && !selectedGroupId) {
                console.log('No group selected, loading all triggers');
                const result = await apiClient.get('/voicebox/trigger-catalog');
                triggers = result.triggers || [];
            }
            
            setTriggerCatalog(triggers);
            console.log(`Final trigger catalog: ${triggers.length} triggers`);
            if (triggers.length === 0 && selectedGroupId) {
                console.warn('No triggers found for selected client. Triggers are automatically synced when a client is selected.');
                console.warn('This might mean:');
                console.warn('1. Triggers have not been synced yet - try clicking "Sync Triggers"');
                console.warn('2. The client_id in the mapping does not match the client_id in the trigger catalog');
                console.warn('3. There are no active triggers for this client in VoiceBox');
            }
            
            // Return the number of triggers loaded so caller can check if successful
            return triggers.length;
        } catch (error) {
            console.error('Failed to load trigger catalog:', error);
            console.error('Error stack:', error.stack);
            setTriggerCatalog([]);
            return 0;
        }
    };

    // Load available lists (lst schema tables with friendly names)
    const loadAvailableLists = async () => {
        setLoadingLists(true);
        setListsError(null);
        try {
            if (!selectedGroupId) {
                setAvailableLists([]);
                setListsError(null);
                setLoadingLists(false);
                return;
            }

            // Get the VoiceBox mapping for this group
            const mappingResult = await apiClient.get('/voicebox/client-mappings');
            const mappings = mappingResult.mappings || [];
            const mapping = mappings.find(m => String(m.group_id) === String(selectedGroupId));
            
            if (!mapping) {
                setAvailableLists([]);
                setListsError('No VoiceBox client mapping found for this group. Please configure the mapping on the VoiceBox Sync page first.');
                setLoadingLists(false);
                return;
            }

            if (!mapping.voicebox_client_id) {
                setAvailableLists([]);
                setListsError('VoiceBox client mapping is incomplete. Please check the mapping configuration.');
                setLoadingLists(false);
                return;
            }
            
            // Get lists from VoiceBox for this client
            try {
                const listsResult = await apiClient.get(`/voicebox/client-mappings/${mapping.id}/lists`);
                const lists = listsResult.lists || [];
                
                if (listsResult.error) {
                    console.warn('Warning fetching lists:', listsResult.error);
                    setListsError(`Could not fetch lists from VoiceBox: ${listsResult.error}. ${listsResult.warning || 'Please check the VoiceBox connection and try again.'}`);
                } else if (lists.length === 0) {
                    setListsError(`No lists found in VoiceBox for client "${mapping.voicebox_client_name}". Lists may need to be created in VoiceBox first.`);
                } else {
                    setListsError(null); // Clear any previous errors
                }
                
                // Transform to include friendly name and table name - only show friendly names
                const transformedLists = lists.map(list => ({
                    listId: list.ListID,
                    listName: list.ListName, // Friendly name only - no IDs in display
                    description: list.Description || '',
                    tableName: `lst.Contacts_${list.ListID}`, // Actual table name
                    folderPath: list.FolderPath || '',
                })).sort((a, b) => a.listName.localeCompare(b.listName)); // Sort by friendly name
                
                setAvailableLists(transformedLists);
                console.log(`Loaded ${transformedLists.length} lists for client ${mapping.voicebox_client_name}`);
            } catch (listError) {
                console.error('Error fetching lists:', listError);
                const errorDetails = listError.response?.data || listError.message;
                console.error('List error details:', errorDetails);
                setAvailableLists([]);
                setListsError(`Failed to fetch lists from VoiceBox: ${errorDetails}. Please check the VoiceBox connection on the VoiceBox Sync page.`);
            }
        } catch (error) {
            console.error('Failed to load available lists:', error);
            setAvailableLists([]);
            setListsError('Failed to load client mapping. Please try again.');
        } finally {
            setLoadingLists(false);
        }
    };

    // Auto-sync function that syncs triggers, lists, and messages automatically
    const autoSyncClientData = async () => {
        if (!selectedGroupId) return;
        
        setSyncingClient(true);
        setSyncStatus({ lists: 'syncing', messages: 'syncing' });
        
        try {
            // Get the VoiceBox mapping for this group
            const mappingResult = await apiClient.get('/voicebox/client-mappings');
            const mappings = mappingResult.mappings || [];
            const mapping = mappings.find(m => String(m.group_id) === String(selectedGroupId));
            
            if (!mapping) {
                setSyncStatus({ 
                    lists: 'error', 
                    messages: 'error' 
                });
                setListsError('No VoiceBox client mapping found for this group. Please configure the mapping on the VoiceBox Sync page first.');
                setSyncingClient(false);
                return;
            }

            if (!mapping.voicebox_client_id) {
                setSyncStatus({ 
                    lists: 'error', 
                    messages: 'error' 
                });
                setListsError('VoiceBox client mapping is incomplete. Please check the mapping configuration.');
                setSyncingClient(false);
                return;
            }

            // Sync lists
            try {
                setSyncStatus(prev => ({ ...prev, lists: 'syncing' }));
                console.log(`Fetching lists for mapping ${mapping.id} (client: ${mapping.voicebox_client_name})`);
                const listsResult = await apiClient.get(`/voicebox/client-mappings/${mapping.id}/lists`);
                console.log('Lists API response:', listsResult);
                const lists = listsResult.lists || [];
                
                if (listsResult.error) {
                    console.error('Lists API returned error:', listsResult.error);
                    console.error('Error details:', listsResult.details);
                    setListsError(`Could not fetch lists from VoiceBox: ${listsResult.error}. ${listsResult.warning || 'Please check the VoiceBox connection and try again.'}`);
                    setSyncStatus(prev => ({ ...prev, lists: 'error' }));
                } else if (lists.length === 0) {
                    console.warn(`No lists returned for client ${mapping.voicebox_client_name}`);
                    setListsError(`No lists found in VoiceBox for client "${mapping.voicebox_client_name}". Lists may need to be created in VoiceBox first.`);
                    setSyncStatus(prev => ({ ...prev, lists: 'success' })); // Still success, just no lists
                } else {
                    console.log(`Received ${lists.length} lists, transforming...`);
                    setListsError(null);
                    setSyncStatus(prev => ({ ...prev, lists: 'success' }));
                }
                
                // Log raw lists data for debugging
                console.log(`Raw lists received: ${lists.length} lists`);
                if (lists.length > 0) {
                    console.log('Sample raw list:', JSON.stringify(lists[0], null, 2));
                    console.log('All list keys:', Object.keys(lists[0]));
                }
                
                // Transform lists - handle both ListID and listId (case variations)
                const transformedLists = lists.map((list, index) => {
                    // Try multiple possible column name variations
                    const listId = list.ListID || list.listId || list.ListId || list.LISTID || list.LIST_ID;
                    const listName = list.ListName || list.listName || list.List_Name || list.LISTNAME || list.LIST_NAME || `List ${index + 1}`;
                    const description = list.Description || list.description || list.DESCRIPTION || '';
                    const folderPath = list.FolderPath || list.folderPath || list.Folder_Path || list.FOLDERPATH || list.FolderName || list.foldername || '';
                    
                    // Convert listId to number if it's a string
                    const numericListId = listId ? (typeof listId === 'string' ? parseInt(listId, 10) : listId) : null;
                    
                    if (!numericListId || isNaN(numericListId)) {
                        console.warn(`List ${index} missing/invalid ID:`, { listId, numericListId, list });
                    }
                    
                    return {
                        listId: numericListId,
                        listName: listName,
                        description: description,
                        tableName: numericListId ? `lst.Contacts_${numericListId}` : null,
                        folderPath: folderPath,
                    };
                }).filter(list => list.listId && !isNaN(list.listId) && list.listId > 0) // Filter out any lists without valid numeric IDs
                .sort((a, b) => a.listName.localeCompare(b.listName));
                
                console.log(`Successfully transformed ${transformedLists.length} lists for client ${mapping.voicebox_client_name}`);
                if (transformedLists.length > 0) {
                    console.log('Transformed lists sample:', transformedLists.slice(0, 3));
                }
                if (lists.length > transformedLists.length) {
                    console.warn(`⚠️ Filtered out ${lists.length - transformedLists.length} lists due to missing/invalid IDs`);
                }
                setAvailableLists(transformedLists);
            } catch (listError) {
                console.error('Error syncing lists:', listError);
                console.error('Error response:', listError.response);
                console.error('Error response data:', listError.response?.data);
                const errorDetails = listError.response?.data?.error || listError.response?.data?.details || listError.message;
                setListsError(`Failed to fetch lists from VoiceBox: ${errorDetails}. Please check the VoiceBox connection on the VoiceBox Sync page.`);
                setSyncStatus(prev => ({ ...prev, lists: 'error' }));
            }

            // Fetch folders from VoiceBox (message folders and list folders)
            try {
                console.log(`Fetching folders for mapping ${mapping.id}...`);
                const [msgFoldersResult, listFoldersResult] = await Promise.all([
                    apiClient.get(`/voicebox/client-mappings/${mapping.id}/message-folders`),
                    apiClient.get(`/voicebox/client-mappings/${mapping.id}/list-folders`)
                ]);
                
                const msgFolders = (msgFoldersResult.folders || []).map(f => ({
                    folderId: f.FolderID,
                    folderName: f.FolderName,
                    folderPath: f.FolderPath || f.FolderName,
                })).sort((a, b) => a.folderName.localeCompare(b.folderName));
                
                const lstFolders = (listFoldersResult.folders || []).map(f => ({
                    folderId: f.FolderID,
                    folderName: f.FolderName,
                    folderPath: f.FolderPath || f.FolderName,
                })).sort((a, b) => a.folderName.localeCompare(b.folderName));
                
                setMessageFolders(msgFolders);
                setListFolders(lstFolders);
                console.log(`Loaded ${msgFolders.length} message folders and ${lstFolders.length} list folders`);
            } catch (folderError) {
                console.error('Error fetching folders:', folderError);
                setMessageFolders([]);
                setListFolders([]);
            }

            // Sync messages - incremental: server checks if VoiceBox has updates before full re-sync
            try {
                setSyncStatus(prev => ({ ...prev, messages: 'syncing' }));
                const syncResult = await apiClient.get(`/voicebox/client-mappings/${mapping.id}/messages`, {}, 600000);
                
                if (syncResult.skipped) {
                    console.log(`Message sync skipped — no changes since last sync (${syncResult.existingCount} messages already in catalog)`);
                } else {
                    const totalSynced = (syncResult.emailCount || 0) + (syncResult.smsCount || 0);
                    console.log(`Synced ${totalSynced} messages (${syncResult.emailCount || 0} email, ${syncResult.smsCount || 0} sms)`);
                }

                // Read from the catalog (always, whether synced or skipped)
                const result = await apiClient.get(`/voicebox/message-catalog?client_id=${mapping.voicebox_client_id}&group_id=${selectedGroupId}`);
                const messages = result.messages || [];
                setMessageCatalog(messages);
                setSyncStatus(prev => ({ ...prev, messages: 'success' }));
                console.log(`Message catalog loaded: ${messages.length} messages for client ${mapping.voicebox_client_id}`);
            } catch (messageError) {
                console.error('Error syncing messages:', messageError);
                console.error('Message error details:', messageError.response?.data || messageError.message);
                setSyncStatus(prev => ({ ...prev, messages: 'error' }));
            }

            // Load API integrations for Send Via API node
            try {
                const apiResult = await apiClient.get(`/voicebox/groups/${selectedGroupId}/api-integrations`);
                setApiIntegrations(apiResult || []);
                console.log(`Loaded ${(apiResult || []).length} API integrations for group ${selectedGroupId}`);
            } catch (apiError) {
                console.warn('Could not load API integrations:', apiError.message);
                setApiIntegrations([]);
            }
            
        } catch (error) {
            console.error('Error in auto-sync:', error);
            setSyncStatus({ 
                lists: 'error', 
                messages: 'error' 
            });
        } finally {
            setSyncingClient(false);
        }
    };

    const generateMessageSuite = async () => {
        if (!selectedGroupId) return;

        setGeneratingMessageSuite(true);
        setMessageSuiteStatus(null);

        try {
            const suiteResult = await apiClient.post(`/voicebox/groups/${selectedGroupId}/message-suite`, {
                folder_prefix: 'Onboarding',
                overwrite: false
            });

            const mappingId = suiteResult.mapping_id;
            const clientId = suiteResult.voicebox_client_id;

            if (!mappingId || !clientId) {
                throw new Error('Message suite generated, but mapping details were missing.');
            }

            // Sync messages after generation to refresh catalog
            await apiClient.get(`/voicebox/client-mappings/${mappingId}/messages`, {}, 600000);
            const catalogResult = await apiClient.get(`/voicebox/message-catalog?client_id=${clientId}`);
            setMessageCatalog(catalogResult.messages || []);
            setMessageSuiteStatus('success');
        } catch (error) {
            console.error('Failed to generate message suite:', error);
            setMessageSuiteStatus('error');
        } finally {
            setGeneratingMessageSuite(false);
        }
    };

    const migrateContactPlans = async () => {
        setMigratingContactPlans(true);
        setMigrationSummary(null);

        try {
            const result = await apiClient.post('/workflows/migrate-contact-plans', {
                overwrite: false
            });
            setMigrationSummary(result);
        } catch (error) {
            console.error('Failed to migrate contact plans:', error);
            setMigrationSummary({ error: true });
        } finally {
            setMigratingContactPlans(false);
        }
    };

    // Auto-sync lists and messages when selected group changes
    useEffect(() => {
        if (selectedGroupId) {
            autoSyncClientData();
        } else {
            setAvailableLists([]);
            setMessageCatalog([]);
            setMessageFolders([]);
            setListFolders([]);
            setApiIntegrations([]);
            setSyncStatus({ lists: null, messages: null });
        }
    }, [selectedGroupId]);

    const deleteEdge = useCallback((edgeId) => {
        if (readOnly) return;
        setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));
    }, [setEdges, readOnly]);

    const insertNodeInEdge = useCallback((edgeId) => {
        const edge = edges.find(e => e.id === edgeId);
        if (!edge) return;

        // Store the edge info for when a node type is selected
        setPendingAddAfterNode({ nodeId: edge.source, edgeId: edgeId });
        setShowNodePalette(true);
    }, [edges]);

    const loadWorkflow = async () => {
        setLoading(true);
        try {
            const result = await apiClient.get(`/workflows/${id}`);
            if (!result.workflow) {
                throw new Error('Workflow not found');
            }
            setWorkflow(result.workflow);
            setWorkflowName(result.workflow.name || '');
            setWorkflowDescription(result.workflow.description || '');
            setSelectedGroupId(result.workflow.group_id || null);
            
            // Load location targeting if it exists
            if (result.workflow.target_locations) {
                try {
                    const targetLocations = typeof result.workflow.target_locations === 'string' 
                        ? JSON.parse(result.workflow.target_locations)
                        : result.workflow.target_locations;
                    if (targetLocations === 'all') {
                        setTargetAllLocations(true);
                        setSelectedLocationIds([]);
                    } else if (Array.isArray(targetLocations)) {
                        setTargetAllLocations(false);
                        setSelectedLocationIds(targetLocations);
                    }
                } catch (e) {
                    console.error('Error parsing target_locations:', e);
                }
            }

            // Valid node types
            const validNodeTypes = ['trigger', 'trigger_manual', 'trigger_schedule', 'trigger_webhook', 'filter', 'message', 'delay', 'condition', 'action', 'listSelector', 'sendViaApi'];
            
            // Convert database nodes to ReactFlow format
            const flowNodes = (result.nodes || []).map((node, index) => {
                let nodeConfig = {};
                try {
                    nodeConfig = typeof node.node_config === 'string' 
                        ? JSON.parse(node.node_config) 
                        : (node.node_config || {});
                } catch (e) {
                    console.error('Error parsing node_config:', e, node);
                    nodeConfig = {};
                }

                // Validate and sanitize node type
                const nodeType = validNodeTypes.includes(node.node_type) 
                    ? node.node_type 
                    : 'action'; // Default to action if invalid

                // Clean nodeConfig - remove functions and non-serializable data
                // Keep all serializable properties from the stored config
                const cleanConfig = { ...nodeConfig };
                // Remove non-serializable / internal keys
                delete cleanConfig.reactFlowId;
                delete cleanConfig.onAddNode;
                // Ensure webhook GUID from database takes precedence
                if (node.webhook_guid) {
                    cleanConfig.webhookGuid = node.webhook_guid;
                }

                // Use reactFlowId from config if available, otherwise generate one
                const reactFlowId = nodeConfig.reactFlowId || `node-${node.id || index}`;

                // Ensure position values are valid numbers
                const positionX = typeof node.position_x === 'number' && !isNaN(node.position_x)
                    ? node.position_x 
                    : 0;
                const positionY = typeof node.position_y === 'number' && !isNaN(node.position_y)
                    ? node.position_y 
                    : 0;

                return {
                    id: String(reactFlowId), // Ensure ID is a string
                    type: nodeType,
                    position: { 
                        x: positionX, 
                        y: positionY 
                    },
                    data: {
                        label: String(node.node_label || cleanConfig.label || 'Untitled'),
                        ...cleanConfig,
                        webhookGuid: node.webhook_guid || cleanConfig.webhookGuid, // Include webhook GUID from database
                        dbId: node.id,
                        onAddNode: (nodeId) => {
                            setShowNodePalette(true);
                            setPendingAddAfterNode({ nodeId });
                        },
                        onDeleteNode: deleteNode
                    },
                };
            }).filter(node => node && node.id && node.type); // Filter out any invalid nodes

            // Create a map of database ID to ReactFlow ID for connections
            // Use a plain object instead of Map to avoid potential constructor issues
            const dbIdToReactFlowId = {};
            flowNodes.forEach(node => {
                if (node && node.data && node.data.dbId && node.id) {
                    dbIdToReactFlowId[node.data.dbId] = node.id;
                }
            });

            // Convert database connections to ReactFlow edges
            // Note: We'll add the callbacks after edges are set, in a useEffect
            const flowEdges = (result.connections || []).map(conn => {
                const sourceId = dbIdToReactFlowId[conn.source_node_id];
                const targetId = dbIdToReactFlowId[conn.target_node_id];
                
                if (sourceId && targetId) {
                    // Map connection_type back to sourceHandle (for condition nodes)
                    // connection_type can be 'true', 'false', or 'default'
                    const sourceHandle = conn.connection_type && conn.connection_type !== 'default' 
                        ? conn.connection_type 
                        : undefined;
                    
                    return {
                        id: `edge-${conn.id}`,
                        source: sourceId,
                        target: targetId,
                        sourceHandle: sourceHandle,
                        type: 'custom',
                        label: conn.label,
                        markerEnd: {
                            type: 'arrowclosed',
                            color: '#b1b1b7',
                            width: 20,
                            height: 20,
                        },
                        data: {} // Will be populated by useEffect
                    };
                }
                return null;
            }).filter(Boolean);

            // Validate nodes and edges before setting
            // Deep clone to avoid mutating original data
            const validNodes = flowNodes
                .map(node => {
                    if (!node || !node.id || !node.type) {
                        console.warn('Invalid node filtered out:', node);
                        return null;
                    }
                    if (!validNodeTypes.includes(node.type)) {
                        console.warn(`Invalid node type "${node.type}" for node ${node.id}, defaulting to action`);
                        return { ...node, type: 'action' };
                    }
                    // Ensure all required properties exist and are valid
                    return {
                        id: String(node.id),
                        type: String(node.type),
                        position: {
                            x: typeof node.position?.x === 'number' ? node.position.x : 0,
                            y: typeof node.position?.y === 'number' ? node.position.y : 0
                        },
                        data: {
                            ...(node.data || {}),
                            label: String(node.data?.label || 'Untitled'),
                            onDeleteNode: deleteNode
                        }
                    };
                })
                .filter(node => node !== null);

            const validEdges = flowEdges
                .map(edge => {
                    if (!edge || !edge.id || !edge.source || !edge.target) {
                        console.warn('Invalid edge filtered out:', edge);
                        return null;
                    }
                    // Ensure source and target nodes exist
                    const sourceExists = validNodes.some(n => n.id === edge.source);
                    const targetExists = validNodes.some(n => n.id === edge.target);
                    if (!sourceExists || !targetExists) {
                        console.warn(`Edge ${edge.id} references non-existent nodes, filtering out`);
                        return null;
                    }
                    // Ensure all required properties exist
                    return {
                        id: String(edge.id),
                        source: String(edge.source),
                        target: String(edge.target),
                        type: edge.type || 'custom',
                        sourceHandle: edge.sourceHandle,
                        markerEnd: edge.markerEnd,
                        data: edge.data || {}
                    };
                })
                .filter(edge => edge !== null);

            console.log(`Loaded ${validNodes.length} nodes and ${validEdges.length} edges`);
            
            // Safely set nodes and edges with error handling
            try {
                if (validNodes.length > 0) {
                    setNodes(validNodes);
                } else {
                    console.warn('No valid nodes to load, setting empty array');
                    setNodes([]);
                }
                
                if (validEdges.length > 0) {
                    setEdges(validEdges);
                } else {
                    console.warn('No valid edges to load, setting empty array');
                    setEdges([]);
                }
            } catch (error) {
                console.error('Error setting nodes/edges:', error);
                console.error('Nodes:', validNodes);
                console.error('Edges:', validEdges);
                throw error; // Re-throw to be caught by outer try-catch
            }
        } catch (error) {
            console.error('Failed to load workflow:', error);
            const errorMessage = error.message || error.response?.data?.error || error.response?.data?.details || 'Unknown error';
            alert(`Failed to load workflow: ${errorMessage}`);
            // Navigate back to workflows list on error
            navigate('/admin/workflows');
        } finally {
            setLoading(false);
        }
    };

    // Update edges with callbacks after they're loaded
    useEffect(() => {
        if (edges.length > 0 && edges.some(e => !e.data || !e.data.onDelete)) {
            setEdges((eds) => eds.map(edge => ({
                ...edge,
                type: 'custom',
                data: {
                    ...edge.data,
                    onDelete: deleteEdge,
                    onInsert: insertNodeInEdge
                }
            })));
        }
    }, [edges.length, deleteEdge, insertNodeInEdge]);

    const onConnect = useCallback(
        (params) => {
            if (readOnly) return;
            const newEdge = {
                ...params,
                type: 'custom',
                markerEnd: {
                    type: 'arrowclosed',
                    color: '#b1b1b7',
                    width: 20,
                    height: 20,
                },
                data: {
                    onDelete: deleteEdge,
                    onInsert: insertNodeInEdge
                }
            };
            setEdges((eds) => addEdge(newEdge, eds));
        },
        [setEdges, deleteEdge, insertNodeInEdge]
    );

    const nodeTypeLabels = {
        trigger: 'New Trigger',
        trigger_manual: 'Manual Start',
        trigger_schedule: 'New Schedule',
        trigger_webhook: 'New Webhook',
        sendViaApi: 'Send Via API',
        filter: 'New Filter',
        message: 'New Message',
        delay: 'New Delay',
        condition: 'New Condition',
        action: 'New Action',
        listSelector: 'New List Selector',
    };

    const addNode = (nodeType) => {
        const newNode = {
            id: `${nodeType}-${Date.now()}`,
            type: nodeType,
            position: {
                x: Math.random() * 400 + 100,
                y: Math.random() * 400 + 100,
            },
            data: {
                label: nodeTypeLabels[nodeType] || `New ${nodeType.charAt(0).toUpperCase() + nodeType.slice(1)}`,
                nodeType: nodeType,
                onAddNode: (nodeId) => {
                    setShowNodePalette(true);
                    setPendingAddAfterNode({ nodeId });
                },
                onDeleteNode: deleteNode
            },
        };

        setNodes((nds) => [...nds, newNode]);
        setShowNodePalette(false);
        setPendingAddAfterNode(null);
        setSelectedNode(newNode);
        setShowNodeConfig(true);
    };

    const onNodeClick = (event, node) => {
        setSelectedNode(node);
        setShowNodeConfig(true);
    };

    const saveNodeConfig = (updatedData) => {
        setNodes((nds) =>
            nds.map((node) => {
                if (node.id === selectedNode.id) {
                    const newData = { ...node.data, ...updatedData };
                    if (node.type === 'action' && updatedData.actionType) {
                        const actionTypeLabels = {
                            'UpdateContact': 'Update Contact',
                            'NotifyContacts': 'Notify Contacts',
                            'LogEvent': 'Log Event'
                        };
                        newData.label = actionTypeLabels[updatedData.actionType] || 'Action';
                    }
                    return { ...node, data: newData };
                }
                return node;
            })
        );
        setShowNodeConfig(false);
        setSelectedNode(null);
        setPendingModalConfig(null);
    };

    const deleteNode = (nodeId) => {
        if (readOnly) return;
        setNodes((nds) => nds.filter((node) => node.id !== nodeId));
        setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
        setShowNodeConfig(false);
        setSelectedNode(null);
    };


    // Update addNode to handle edge insertion and automatic connection
    const addNodeAfter = (nodeType, afterInfo) => {
        if (!afterInfo) {
            addNode(nodeType);
            return;
        }

        const { nodeId, edgeId } = afterInfo;
        const edge = edgeId ? edges.find(e => e.id === edgeId) : null;
        const afterNode = nodes.find(n => n.id === nodeId);

        if (!afterNode) {
            addNode(nodeType);
            return;
        }

        // Calculate position for new node - position it below the source node (vertical flow)
        let position = { x: afterNode.position.x, y: afterNode.position.y + 150 };
        
        if (edge) {
            // Position between source and target, keeping same x for vertical alignment
            const targetNode = nodes.find(n => n.id === edge.target);
            if (targetNode) {
                position = {
                    x: afterNode.position.x, // Keep same x position for vertical alignment
                    y: (afterNode.position.y + targetNode.position.y) / 2
                };
            }
        }

        const newNode = {
            id: `${nodeType}-${Date.now()}`,
            type: nodeType,
            position: position,
            data: {
                label: nodeTypeLabels[nodeType] || `New ${nodeType.charAt(0).toUpperCase() + nodeType.slice(1)}`,
                nodeType: nodeType,
                onAddNode: (nodeId) => {
                    setShowNodePalette(true);
                    setPendingAddAfterNode({ nodeId });
                },
                onDeleteNode: deleteNode
            },
        };

        setNodes((nds) => [...nds, newNode]);

        // If inserting in an edge, split the edge
        if (edge) {
            // Remove old edge
            setEdges((eds) => eds.filter(e => e.id !== edgeId));
            // Add new edges: source -> newNode and newNode -> target
            const newEdge1 = {
                id: `edge-${edge.source}-${newNode.id}`,
                source: edge.source,
                target: newNode.id,
                type: 'custom',
                markerEnd: {
                    type: 'arrowclosed',
                    color: '#b1b1b7',
                    width: 20,
                    height: 20,
                },
                data: {
                    onDelete: deleteEdge,
                    onInsert: insertNodeInEdge
                }
            };
            const newEdge2 = {
                id: `edge-${newNode.id}-${edge.target}`,
                source: newNode.id,
                target: edge.target,
                type: 'custom',
                markerEnd: {
                    type: 'arrowclosed',
                    color: '#b1b1b7',
                    width: 20,
                    height: 20,
                },
                data: {
                    onDelete: deleteEdge,
                    onInsert: insertNodeInEdge
                }
            };
            setEdges((eds) => [...eds, newEdge1, newEdge2]);
        } else {
            // If adding after a node (via + button), automatically connect source -> newNode
            // For condition nodes, use the 'true' handle (bottom)
            // For other nodes, use the default bottom handle
            const sourceHandle = afterNode.type === 'condition' ? 'true' : undefined;
            
            const newEdge = {
                id: `edge-${afterNode.id}-${newNode.id}-${Date.now()}`,
                source: afterNode.id,
                target: newNode.id,
                sourceHandle: sourceHandle,
                type: 'custom',
                markerEnd: {
                    type: 'arrowclosed',
                    color: '#b1b1b7',
                    width: 20,
                    height: 20,
                },
                data: {
                    onDelete: deleteEdge,
                    onInsert: insertNodeInEdge
                }
            };
            setEdges((eds) => [...eds, newEdge]);
        }

        setShowNodePalette(false);
        setPendingAddAfterNode(null);
        setSelectedNode(newNode);
        setShowNodeConfig(true);
    };

    // Check if client has lists created
    const checkClientHasLists = async (groupId) => {
        if (!groupId) return false;
        
        // First check if lists are already loaded in the UI (most common case)
        if (availableLists && availableLists.length > 0) {
            console.log(`Using already loaded lists: ${availableLists.length} lists available`);
            return true;
        }
        
        // If lists aren't loaded yet, try to fetch them
        try {
            const mappingResult = await apiClient.get('/voicebox/client-mappings');
            const mappings = mappingResult.mappings || [];
            const mapping = mappings.find(m => String(m.group_id) === String(groupId));
            
            if (!mapping || !mapping.id) {
                console.warn(`No mapping found for group ${groupId}`);
                return false;
            }
            
            try {
                const listsResult = await apiClient.get(`/voicebox/client-mappings/${mapping.id}/lists`);
                const lists = listsResult.lists || [];
                console.log(`Found ${lists.length} lists for client ${mapping.voicebox_client_name}`);
                return lists.length > 0;
            } catch (listError) {
                console.error('Error fetching lists from VoiceBox:', listError);
                console.error('List error details:', listError.response?.data || listError.message);
                // If lists are already loaded in the UI, allow workflow creation even if API call fails
                // This handles cases where the API might be temporarily unavailable but lists were loaded earlier
                if (availableLists && availableLists.length > 0) {
                    console.log('API call failed but lists are already loaded in UI, allowing workflow creation');
                    return true;
                }
                return false;
            }
        } catch (error) {
            console.error('Error checking lists:', error);
            console.error('Error details:', error.response?.data || error.message);
            // If lists are already loaded in the UI, allow workflow creation even if API call fails
            if (availableLists && availableLists.length > 0) {
                console.log('Error checking lists but lists are already loaded in UI, allowing workflow creation');
                return true;
            }
            return false;
        }
    };

    // Validate location details are complete
    const validateLocationDetails = async (groupId) => {
        if (!groupId) return { valid: true, errors: [] };
        
        try {
            const result = await apiClient.get('/dealer-locations-with-addresses');
            const groupLocations = (result || []).filter(loc => loc.group_id === groupId);
            
            const errors = [];
            groupLocations.forEach(loc => {
                // Check if location has address
                if (!loc.address_id && !loc.address) {
                    errors.push(`Location "${loc.name}" is missing address details`);
                }
                // Check required location fields
                if (!loc.name) {
                    errors.push(`Location "${loc.id}" is missing name`);
                }
            });
            
            return {
                valid: errors.length === 0,
                errors: errors
            };
        } catch (error) {
            console.error('Error validating locations:', error);
            return { valid: false, errors: ['Failed to validate location details'] };
        }
    };

    const saveWorkflow = async () => {
        if (!workflowName.trim()) {
            alert('Please enter a workflow name');
            return;
        }

        // Validate that client has lists before allowing workflow creation
        if (selectedGroupId) {
            const hasLists = await checkClientHasLists(selectedGroupId);
            if (!hasLists) {
                alert('This client must have at least one list created in VoiceBox before workflows can be used. Please create lists first.');
                return;
            }
        }

        setSaving(true);
        try {
            // Prepare nodes and connections for API
            const nodesToSave = nodes.map((node, index) => ({
                id: node.id,
                node_type: node.type,
                node_label: node.data.label || 'Untitled',
                node_config: {
                    ...node.data,
                    reactFlowId: node.id, // Store ReactFlow ID for reference
                },
                position: node.position,
                is_start_node: node.type === 'trigger' || node.type === 'trigger_manual' || node.type === 'trigger_schedule' || node.type === 'trigger_webhook',
                execution_order: index,
                webhook_guid: node.data?.webhookGuid || node.data?.webhook_guid || null, // Preserve webhook GUID if exists
            }));

            const connectionsToSave = edges.map((edge) => ({
                source: edge.source,
                target: edge.target,
                sourceHandle: edge.sourceHandle, // Include sourceHandle for condition nodes
                type: edge.type || 'custom',
                label: edge.label,
            }));

            if (isEditMode) {
                // Update workflow
                await apiClient.put(`/workflows/${id}`, {
                    name: workflowName,
                    description: workflowDescription,
                    group_id: selectedGroupId || null,
                    target_locations: targetAllLocations ? 'all' : selectedLocationIds,
                });

                // Save nodes and connections
                await apiClient.post(`/workflows/${id}/nodes`, {
                    nodes: nodesToSave,
                    connections: connectionsToSave,
                });

                // Save variable mappings
                await apiClient.post(`/workflows/${id}/variable-mappings`, {
                    mappings: variableMappings,
                });
            } else {
                // Create new workflow
                const result = await apiClient.post('/workflows', {
                    name: workflowName,
                    description: workflowDescription,
                    workflow_type: 'email',
                    group_id: selectedGroupId || null,
                    target_locations: targetAllLocations ? 'all' : selectedLocationIds,
                });

                const newWorkflowId = result.workflow.id;

                // Save nodes and connections
                await apiClient.post(`/workflows/${newWorkflowId}/nodes`, {
                    nodes: nodesToSave,
                    connections: connectionsToSave,
                });

                // Save variable mappings
                await apiClient.post(`/workflows/${newWorkflowId}/variable-mappings`, {
                    mappings: variableMappings,
                });

                // Navigate to edit mode
                navigate(`/admin/workflow-builder/${newWorkflowId}`);
            }

            alert('Workflow saved successfully!');
        } catch (error) {
            console.error('Failed to save workflow:', error);
            const errorMessage = error.message || error.response?.data?.error || error.response?.data?.details || 'Unknown error';
            alert(`Failed to save workflow: ${errorMessage}`);
        } finally {
            setSaving(false);
        }
    };

    const generateSql = async () => {
        if (!isEditMode || !id) {
            alert('Please save the workflow first before generating SQL');
            return;
        }

        if (nodes.length === 0) {
            alert('Workflow must have at least one node');
            return;
        }

        setGeneratingSql(true);
        try {
            console.log('Generating SQL for workflow:', id);
            console.log('Current nodes:', nodes.map(n => ({ id: n.id, type: n.type, label: n.data?.label })));
            console.log('Current edges:', edges.map(e => ({ source: e.source, target: e.target })));
            
            const result = await apiClient.post(`/workflows/${id}/generate-sql`);
            console.log('SQL generation result:', result);
            
            setGeneratedSql(result);
            setShowSqlPreview(true);
            
            // Show warnings if any
            if (result.errors && result.errors.length > 0) {
                console.warn('SQL generation had errors:', result.errors);
            }
            if (result.validationErrors && result.validationErrors.length > 0) {
                console.warn('SQL validation errors:', result.validationErrors);
                alert(`SQL generated but has validation errors:\n${result.validationErrors.join('\n')}`);
            }
            if (!result.isValid) {
                alert(`SQL generation completed but SQL is invalid:\n${(result.validationErrors || []).join('\n') || 'Unknown validation error'}`);
            }
        } catch (error) {
            console.error('Failed to generate SQL:', error);
            const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message;
            const errorDetails = error.response?.data?.details ? `\n\nDetails: ${error.response.data.details}` : '';
            alert(`Failed to generate SQL: ${errorMessage}${errorDetails}`);
        } finally {
            setGeneratingSql(false);
        }
    };

    const exportToVoicebox = async () => {
        if (!isEditMode || !id) {
            alert('Please save the workflow first');
            return;
        }
        setExportingToVb(true);
        setVbExportResult(null);
        try {
            // Apply any pending modal config changes before saving
            let finalNodes = nodes;
            if (pendingModalConfig && selectedNode) {
                finalNodes = nodes.map(n => {
                    if (n.id === pendingModalConfig.nodeId) {
                        return { ...n, data: { ...n.data, ...pendingModalConfig.config } };
                    }
                    return n;
                });
                setNodes(finalNodes);
                setShowNodeConfig(false);
                setSelectedNode(null);
                setPendingModalConfig(null);
            }

            // Auto-save current nodes to DB before exporting
            const nodesToSave = finalNodes.map((node, index) => ({
                id: node.id,
                node_type: node.type,
                node_label: node.data.label || 'Untitled',
                node_config: {
                    ...node.data,
                    reactFlowId: node.id,
                },
                position: node.position,
                is_start_node: node.type === 'trigger' || node.type === 'trigger_manual' || node.type === 'trigger_schedule' || node.type === 'trigger_webhook',
                execution_order: index,
                webhook_guid: node.data?.webhookGuid || node.data?.webhook_guid || null,
            }));
            const connectionsToSave = edges.map((edge) => ({
                source: edge.source,
                target: edge.target,
                sourceHandle: edge.sourceHandle,
                type: edge.type || 'custom',
                label: edge.label,
            }));
            await apiClient.post(`/workflows/${id}/nodes`, {
                nodes: nodesToSave,
                connections: connectionsToSave,
            });

            const result = await apiClient.post(`/workflows/${id}/export-to-voicebox`, {
                targetGroupId: selectedGroupId,
            });
            console.log('=== VOICEBOX SAVE RESULT ===');
            console.log('Trigger ID:', result.triggerId);
            console.log('Rows affected:', result.rowsAffected);
            console.log('Message:', result.message);
            console.log('=== END VOICEBOX SAVE ===');
            setVbExportResult({ success: true, message: result.message || `Trigger ${result.triggerId} saved to VoiceBox` });
            setTimeout(() => setVbExportResult(null), 5000);
        } catch (error) {
            console.error('Failed to save to VoiceBox:', error);
            const msg = error.response?.data?.error || error.message;
            setVbExportResult({ success: false, message: msg });
            setTimeout(() => setVbExportResult(null), 8000);
        } finally {
            setExportingToVb(false);
        }
    };

    const [copyDryRunResult, setCopyDryRunResult] = useState(null);

    const copyToVoicebox = async ({ name, locationIds, importToWorkflows, dryRun = false }) => {
        if (!isEditMode || !id) { alert('Please save the workflow first'); return; }
        setCopyingToVb(true);
        setVbExportResult(null);
        try {
            const nodesToSave = nodes.map((node, index) => ({
                id: node.id, node_type: node.type, node_label: node.data.label || 'Untitled',
                node_config: { ...node.data, reactFlowId: node.id },
                position: node.position, is_start_node: node.type === 'trigger' || node.type === 'trigger_manual' || node.type === 'trigger_schedule' || node.type === 'trigger_webhook',
                execution_order: index, webhook_guid: node.data?.webhookGuid || node.data?.webhook_guid || null,
            }));
            const connectionsToSave = edges.map((edge) => ({
                source: edge.source, target: edge.target, sourceHandle: edge.sourceHandle, type: edge.type || 'custom', label: edge.label,
            }));
            await apiClient.post(`/workflows/${id}/nodes`, { nodes: nodesToSave, connections: connectionsToSave });

            if (variableMappings && variableMappings.length > 0) {
                await apiClient.post(`/workflows/${id}/variable-mappings`, { mappings: variableMappings });
            }

            const result = await apiClient.post(`/workflows/${id}/copy-to-voicebox`, { name, locationIds, importToWorkflows, dry_run: dryRun, targetGroupId: selectedGroupId });
            console.log('=== VOICEBOX COPY RESULT ===', result);

            if (dryRun) {
                setCopyDryRunResult(result);
            } else {
                setShowCopyToVbModal(false);
                setCopyDryRunResult(null);
                setVbExportResult({ success: true, message: result.message || `Created ${result.triggers?.length || 0} trigger(s) in VoiceBox` });
                setTimeout(() => setVbExportResult(null), 5000);
            }
        } catch (error) {
            console.error('Failed to copy to VoiceBox:', error);
            const msg = error.response?.data?.error || error.message;
            setVbExportResult({ success: false, message: msg });
            setTimeout(() => setVbExportResult(null), 8000);
        } finally {
            setCopyingToVb(false);
        }
    };

    const executeWorkflow = async (dryRun = false) => {
        if (!isEditMode || !id) {
            alert('Please save the workflow first before executing');
            return;
        }

        if (!generatedSql || !generatedSql.isValid) {
            alert('Please generate valid SQL before executing the workflow');
            return;
        }

        if (!window.confirm(dryRun 
            ? 'This will validate the SQL without executing it. Continue?'
            : 'This will execute the workflow SQL in the VoiceBox database. Continue?'
        )) {
            return;
        }

        setExecuting(true);
        try {
            const result = await apiClient.post(`/workflows/${id}/execute`, { dry_run: dryRun });
            alert(dryRun 
                ? 'Dry run completed successfully!'
                : `Workflow executed successfully! ${result.executionResult.rowsAffected || 0} rows affected.`
            );
            loadExecutionLogs(); // Refresh execution logs
        } catch (error) {
            console.error('Failed to execute workflow:', error);
            alert('Failed to execute workflow: ' + (error.response?.data?.error || error.message));
        } finally {
            setExecuting(false);
        }
    };

    const loadExecutionLogs = async () => {
        if (!id) return;
        try {
            const result = await apiClient.get(`/workflows/${id}/execution-logs`);
            setExecutionLogs(result.logs || []);
        } catch (error) {
            console.error('Failed to load execution logs:', error);
        }
    };

    useEffect(() => {
        if (isEditMode && id) {
            loadExecutionLogs();
        }
    }, [id]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader className="animate-spin" size={32} />
                <span className="ml-3 text-gray-600">Loading workflow...</span>
            </div>
        );
    }

    return (
        <div className="h-screen flex relative">
            {/* Main Content Area */}
            <div className="flex-1 flex flex-col relative">

            {/* Right Panel Toggle Button */}
            <button
                onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
                className="fixed top-1/2 -translate-y-1/2 z-50 bg-white border-l border-t border-b border-gray-300 rounded-l-lg p-2 shadow-lg hover:bg-gray-50 transition-all duration-300"
                style={{ right: rightPanelCollapsed ? '0' : '320px' }}
                title={rightPanelCollapsed ? "Show controls" : "Hide controls"}
            >
                {rightPanelCollapsed ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
            </button>

            {/* Node Palette */}
            {showNodePalette && (
                <div className="absolute top-20 left-4 bg-white border rounded-lg shadow-lg z-10 p-4 min-w-[200px]">
                    <h3 className="font-semibold text-gray-900 mb-3">Add Node</h3>
                    <div className="space-y-2">
                        {[
                            { type: 'listSelector', icon: List, label: 'List Selector' },
                            { type: 'trigger', icon: Zap, label: 'Trigger (Event)' },
                            { type: 'trigger_manual', icon: Play, label: 'Manual Start' },
                            { type: 'trigger_schedule', icon: Clock, label: 'Schedule' },
                            { type: 'trigger_webhook', icon: Zap, label: 'Webhook' },
                            { type: 'filter', icon: Filter, label: 'Filter' },
                            { type: 'message', icon: Mail, label: 'Message' },
                            { type: 'delay', icon: Clock, label: 'Delay' },
                            { type: 'condition', icon: GitBranch, label: 'Condition' },
                            { type: 'action', icon: Settings, label: 'Action' },
                            { type: 'sendViaApi', icon: Rocket, label: 'Send Via API' },
                        ].map(({ type, icon: Icon, label }) => (
                            <button
                                key={type}
                                onClick={() => {
                                    if (pendingAddAfterNode) {
                                        addNodeAfter(type, pendingAddAfterNode);
                                    } else {
                                        addNode(type);
                                    }
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-100 rounded"
                            >
                                <Icon size={18} />
                                {label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => {
                            setShowNodePalette(false);
                            setPendingAddAfterNode(null);
                        }}
                        className="mt-2 w-full px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                    >
                        Cancel
                    </button>
                </div>
            )}

            {/* ReactFlow Canvas */}
            <div className="flex-1">
                <style>{`
                    .react-flow__handle {
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                        border: 2px solid #fff;
                        background: #555;
                    }
                    .react-flow__handle-left {
                        left: -4px;
                    }
                    .react-flow__handle-right {
                        right: -4px;
                    }
                    .react-flow__handle-top {
                        top: -4px;
                    }
                    .react-flow__handle-bottom {
                        bottom: -4px;
                    }
                    .react-flow__handle:hover {
                        background: #3b82f6;
                        border-color: #3b82f6;
                        width: 10px;
                        height: 10px;
                    }
                    .react-flow__handle-connecting {
                        background: #10b981;
                        border-color: #10b981;
                    }
                    .react-flow__handle-valid {
                        background: #10b981;
                        border-color: #10b981;
                    }
                `}</style>
                <ReactFlow
                    nodes={nodes}
                    edges={edges.map(edge => ({
                        ...edge,
                        type: 'custom',
                        markerEnd: {
                            type: 'arrowclosed',
                            color: '#b1b1b7',
                            width: 20,
                            height: 20,
                        },
                        data: {
                            onDelete: deleteEdge,
                            onInsert: insertNodeInEdge
                        }
                    }))}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onNodeClick={onNodeClick}
                    nodeTypes={nodeTypes}
                    edgeTypes={{ custom: CustomEdge }}
                    connectionLineStyle={{ stroke: '#b1b1b7', strokeWidth: 2 }}
                    connectionLineType="smoothstep"
                    isValidConnection={(connection) => {
                        // Allow all connections between different nodes
                        // ReactFlow handles source->target automatically
                        // We just need to prevent self-connections
                        if (connection.source === connection.target) {
                            return false;
                        }
                        // Allow connections from any source handle to any target handle
                        return true;
                    }}
                    connectionMode="loose"
                    fitView
                >
                    <Controls />
                    <MiniMap />
                    <Background variant="dots" gap={12} size={1} />
                </ReactFlow>
            </div>
            </div>

            {/* Right Panel */}
            <div className={`bg-white border-l shadow-lg transition-all duration-300 overflow-y-auto ${
                rightPanelCollapsed ? 'w-0 overflow-hidden' : 'w-80'
            }`}>
                <div className="p-4 space-y-4">
                    {readOnly && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2 mb-3 flex items-center gap-2">
                            <Eye size={16} className="text-yellow-600 flex-shrink-0" />
                            <span className="text-xs text-yellow-800 font-medium">
                                {isVbWorkflow && !canEditVb
                                    ? 'View-only — VoiceBox workflow requires Edit VB Workflows permission'
                                    : 'View-only mode — you don\'t have edit permissions'}
                            </span>
                        </div>
                    )}
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">Workflow Controls</h2>
                        <button
                            onClick={() => setRightPanelCollapsed(true)}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Workflow Name & Description */}
                    <div className="space-y-2">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Workflow Name
                            </label>
                            <input
                                type="text"
                                value={workflowName}
                                onChange={(e) => setWorkflowName(e.target.value)}
                                placeholder="Workflow Name"
                                disabled={readOnly}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Description
                            </label>
                            <input
                                type="text"
                                value={workflowDescription}
                                onChange={(e) => setWorkflowDescription(e.target.value)}
                                placeholder="Description (optional)"
                                disabled={readOnly}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                        </div>
                    </div>

                    {/* Client/Location Selector */}
                    <div className="space-y-2 border-t pt-4">
                        <label className="block text-sm font-medium text-gray-700">
                            Client/Group *
                        </label>
                        <div className="relative">
                            <div className="flex items-center gap-2">
                                <div className="flex-1">
                                    <SearchableSelect
                                        value={selectedGroupId || ''}
                                        onChange={(val) => {
                                            const groupId = val || null;
                                            setSelectedGroupId(groupId);
                                            setSelectedLocationIds([]);
                                            setTargetAllLocations(false);
                                        }}
                                        disabled={syncingClient}
                                        placeholder="Select a client/group..."
                                        options={groups.map((group) => ({
                                            value: group.id,
                                            label: `${group.name}${group.is_voicebox_synced !== true ? ' (Not Synced)' : ''}`
                                        }))}
                                    />
                                </div>
                                {syncingClient && (
                                    <Loader className="animate-spin text-blue-600 flex-shrink-0" size={16} />
                                )}
                                {selectedGroupId && !syncingClient && (() => {
                                    const selectedGroup = groups.find(g => g.id === selectedGroupId);
                                    return selectedGroup?.is_voicebox_synced === true ? (
                                        <CheckCircle2 size={16} className="text-green-600 flex-shrink-0" />
                                    ) : null;
                                })()}
                            </div>
                        </div>
                        {selectedGroupId && (
                            <div className="space-y-1">
                                {syncingClient ? (
                                    <div className="text-xs text-blue-600 flex items-center gap-1">
                                        <Loader className="animate-spin" size={12} />
                                        Syncing lists, folders, and messages from VoiceBox...
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        {syncStatus.lists && (
                                            <div className={`text-xs flex items-center gap-1 ${
                                                syncStatus.lists === 'success' ? 'text-green-600' :
                                                syncStatus.lists === 'error' ? 'text-red-600' : 'text-blue-600'
                                            }`}>
                                                {syncStatus.lists === 'success' && <CheckCircle size={12} />}
                                                {syncStatus.lists === 'error' && <XCircle size={12} />}
                                                {syncStatus.lists === 'syncing' && <Loader className="animate-spin" size={12} />}
                                                Lists: {syncStatus.lists === 'success' ? 'Synced' : 
                                                       syncStatus.lists === 'error' ? 'Error' : 'Syncing...'}
                                            </div>
                                        )}
                                        {syncStatus.messages && (
                                            <div className={`text-xs flex items-center gap-1 ${
                                                syncStatus.messages === 'success' ? 'text-green-600' :
                                                syncStatus.messages === 'error' ? 'text-red-600' : 'text-blue-600'
                                            }`}>
                                                {syncStatus.messages === 'success' && <CheckCircle size={12} />}
                                                {syncStatus.messages === 'error' && <XCircle size={12} />}
                                                {syncStatus.messages === 'syncing' && <Loader className="animate-spin" size={12} />}
                                                Messages: {syncStatus.messages === 'success' ? 'Synced' : 
                                                          syncStatus.messages === 'error' ? 'Error' : 'Syncing...'}
                                            </div>
                                        )}
                                        {!syncingClient && syncStatus.lists === 'success' && syncStatus.messages === 'success' && (
                                            <div className="text-xs text-green-600 flex items-center gap-1">
                                                <CheckCircle2 size={12} />
                                                All data synced successfully
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        {selectedGroupId && (
                            <div className="space-y-2">
                                <button
                                    onClick={generateMessageSuite}
                                    disabled={generatingMessageSuite || syncingClient}
                                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300"
                                >
                                    {generatingMessageSuite ? (
                                        <>
                                            <Loader className="animate-spin" size={14} />
                                            Generating Message Suite...
                                        </>
                                    ) : (
                                        <>
                                            <Mail size={14} />
                                            Generate Message Suite
                                        </>
                                    )}
                                </button>
                                {messageSuiteStatus === 'success' && (
                                    <div className="text-xs text-green-600 flex items-center gap-1">
                                        <CheckCircle size={12} />
                                        Message suite generated and synced
                                    </div>
                                )}
                                {messageSuiteStatus === 'error' && (
                                    <div className="text-xs text-red-600 flex items-center gap-1">
                                        <XCircle size={12} />
                                        Failed to generate message suite
                                    </div>
                                )}
                            </div>
                        )}
                        {selectedGroupId && (
                            <div className="space-y-2">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={targetAllLocations}
                                        onChange={(e) => {
                                            setTargetAllLocations(e.target.checked);
                                            if (e.target.checked) {
                                                setSelectedLocationIds([]);
                                            }
                                        }}
                                        className="rounded"
                                    />
                                    <span className="text-sm">All locations</span>
                                </label>
                                {!targetAllLocations && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Select Locations
                                        </label>
                                        <select
                                            multiple
                                            value={selectedLocationIds}
                                            onChange={(e) => {
                                                const selected = Array.from(e.target.selectedOptions, option => option.value);
                                                setSelectedLocationIds(selected);
                                            }}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                                            size={Math.min(locations.length, 4)}
                                        >
                                            {locations.map((location) => (
                                                <option key={location.id} value={location.id}>
                                                    {location.name} {location.trading_name ? `(${location.trading_name})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Hold Ctrl/Cmd to select multiple
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Template Actions */}
                    {!isEditMode && !readOnly && (
                        <div className="space-y-2 border-t pt-4">
                            <button
                                onClick={() => setShowTemplateModal(true)}
                                disabled={!selectedGroupId || syncingClient}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                            >
                                <Zap size={18} />
                                Create from Template
                            </button>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="space-y-2 border-t pt-4">
                        {isEditMode && (
                            <>
                                {!readOnly && (
                                    <button
                                        onClick={() => setShowSaveTemplateModal(true)}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm mb-2"
                                    >
                                        <Save size={18} />
                                        Save as Template
                                    </button>
                                )}
                                {isVbWorkflow && !readOnly && (
                                    <button
                                        onClick={() => setShowCopyToVbModal(true)}
                                        disabled={nodes.length === 0}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm mb-2"
                                    >
                                        <Copy size={18} />
                                        Save a Copy To VoiceBox
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowVariableMapping(true)}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
                                >
                                    <Map size={18} />
                                    Variables
                                </button>
                                <button
                                    onClick={() => setShowExecutionLogs(true)}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
                                >
                                    <History size={18} />
                                    Logs
                                </button>
                                {isVbWorkflow && (
                                    <button
                                        onClick={exportToVoicebox}
                                        disabled={exportingToVb || nodes.length === 0}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                                    >
                                        {exportingToVb ? (
                                            <>
                                                <Loader className="animate-spin" size={18} />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Upload size={18} />
                                                Save To VoiceBox
                                            </>
                                        )}
                                    </button>
                                )}
                                {generatedSql && generatedSql.isValid && (
                                    <>
                                        <button
                                            onClick={() => executeWorkflow(true)}
                                            disabled={readOnly || executing}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                                        >
                                            {executing ? (
                                                <>
                                                    <Loader className="animate-spin" size={18} />
                                                    Testing...
                                                </>
                                            ) : (
                                                <>
                                                    <TestTube size={18} />
                                                    Test Run
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => executeWorkflow(false)}
                                            disabled={readOnly || executing}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                                        >
                                            {executing ? (
                                                <>
                                                    <Loader className="animate-spin" size={18} />
                                                    Executing...
                                                </>
                                            ) : (
                                                <>
                                                    <Rocket size={18} />
                                                    Execute
                                                </>
                                            )}
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                        <button
                            onClick={() => setShowNodePalette(!showNodePalette)}
                            disabled={readOnly || !selectedGroupId || syncingClient}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                        >
                            <Plus size={18} />
                            Add Node
                        </button>
                        <button
                            onClick={saveWorkflow}
                            disabled={readOnly || saving || !selectedGroupId || syncingClient}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                        >
                            {saving ? (
                                <>
                                    <Loader className="animate-spin" size={18} />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save size={18} />
                                    Save
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Node Configuration Modal */}
            {showNodeConfig && selectedNode && (
                <NodeConfigModal
                    node={selectedNode}
                    nodes={nodes}
                    edges={edges}
                    onSave={saveNodeConfig}
                    onConfigChange={(cfg) => setPendingModalConfig({ nodeId: selectedNode.id, config: cfg })}
                    onDelete={() => deleteNode(selectedNode.id)}
                    readOnly={readOnly}
                    onClose={() => {
                        setShowNodeConfig(false);
                        setSelectedNode(null);
                        setPendingModalConfig(null);
                    }}
                    messageCatalog={messageCatalog}
                    onSyncMessages={syncMessageCatalogForGroup}
                    syncingMessages={syncingMessages}
                    syncMessagesStatus={syncMessagesStatus}
                    triggerCatalog={triggerCatalog}
                    availableLists={availableLists}
                    listsError={listsError}
                    loadingLists={loadingLists}
                    onRetryLists={autoSyncClientData}
                    selectedGroupId={selectedGroupId}
                    workflow={workflow}
                    messageFolders={messageFolders}
                    listFolders={listFolders}
                    apiIntegrations={apiIntegrations}
                />
            )}

            {/* SQL Preview Modal */}
            {showSqlPreview && generatedSql && (
                <SqlPreviewModal
                    sqlData={generatedSql}
                    onClose={() => setShowSqlPreview(false)}
                    onExecute={(dryRun) => {
                        setShowSqlPreview(false);
                        executeWorkflow(dryRun);
                    }}
                    executing={executing}
                />
            )}

            {/* VoiceBox Save Notification Toast */}
            {vbExportResult && (
                <div className={`fixed top-6 right-6 z-50 max-w-md shadow-lg rounded-lg border px-5 py-4 flex items-start gap-3 transition-all ${
                    vbExportResult.success
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-red-50 border-red-300 text-red-800'
                }`}>
                    <span className="text-xl mt-0.5">{vbExportResult.success ? '\u2705' : '\u274C'}</span>
                    <div className="flex-1">
                        <p className="font-semibold text-sm">{vbExportResult.success ? 'Saved to VoiceBox' : 'Save Failed'}</p>
                        <p className="text-xs mt-1">{vbExportResult.message}</p>
                    </div>
                    <button onClick={() => setVbExportResult(null)} className="text-gray-400 hover:text-gray-600 ml-2"><X size={16} /></button>
                </div>
            )}

            {/* Copy To VoiceBox Modal */}
            {showCopyToVbModal && (
                <CopyToVoiceboxModal
                    workflowName={workflow?.name || ''}
                    groupId={selectedGroupId || workflow?.group_id}
                    isCrossClient={selectedGroupId && selectedGroupId !== workflow?.group_id}
                    saving={copyingToVb}
                    onSave={copyToVoicebox}
                    onClose={() => setShowCopyToVbModal(false)}
                />
            )}

            {/* Dry Run Preview Modal */}
            {copyDryRunResult && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                <Eye size={24} />
                                Preview — {copyDryRunResult.triggers?.length || 0} trigger(s) would be created
                            </h2>
                            <button onClick={() => setCopyDryRunResult(null)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
                        </div>
                        <div className="p-6 space-y-6">
                            {(copyDryRunResult.triggers || []).map((t, idx) => (
                                <div key={idx} className="border rounded-lg overflow-hidden">
                                    <div className="bg-gray-100 px-4 py-3 border-b">
                                        <p className="font-semibold text-gray-900 text-sm">#{idx + 1} — {t.title}</p>
                                        {t.locationName && <p className="text-xs text-gray-500 mt-1">Location: {t.locationName}</p>}
                                        <p className="text-xs text-gray-500 mt-1">{t.description}</p>
                                    </div>
                                    <div className="px-4 py-3">
                                        <p className="text-xs font-medium text-gray-600 mb-1">SqlFilter:</p>
                                        <pre className="bg-gray-50 border rounded p-2 text-xs text-gray-800 whitespace-pre-wrap break-all">{t.sqlFilter || '(empty)'}</pre>
                                    </div>
                                    {t.messageCopies && t.messageCopies.length > 0 && (
                                        <div className="px-4 py-3 border-t">
                                            <p className="text-xs font-medium text-gray-600 mb-2">Message Copies ({t.messageCopies.length}):</p>
                                            {t.messageCopies.map((mc, mcIdx) => (
                                                <div key={mcIdx} className="bg-blue-50 border border-blue-200 rounded p-3 mb-2 last:mb-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="inline-block px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded uppercase">{mc.messageType}</span>
                                                        <span className="text-xs text-gray-500">ID: {mc.originalMessageId}</span>
                                                    </div>
                                                    <p className="text-xs text-gray-700"><span className="font-medium">Original:</span> {mc.originalMessageName}</p>
                                                    <p className="text-xs text-gray-700"><span className="font-medium">Copy name:</span> {mc.newMessageName}</p>
                                                    <p className="text-xs text-gray-500 mt-1"><span className="font-medium">Tags found:</span> {mc.tagsFound.map(t => `%%${t}%%`).join(', ')}</p>
                                                    {Object.keys(mc.substitutions || {}).length > 0 && (
                                                        <div className="mt-1">
                                                            <p className="text-xs font-medium text-gray-500">Substitutions:</p>
                                                            {Object.entries(mc.substitutions).map(([tag, val]) => (
                                                                <p key={tag} className="text-xs text-gray-600 ml-2">
                                                                    <code className="bg-gray-100 px-1 rounded">%%{tag}%%</code> → <span className={val.includes('(not resolved)') ? 'text-orange-500 italic' : 'text-green-700 font-medium'}>{val}</span>
                                                                </p>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {mc.newSubject && <p className="text-xs text-gray-600 mt-1"><span className="font-medium">New Subject:</span> {mc.newSubject}</p>}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {t.brandTemplateCopy && (
                                        <div className="px-4 py-3 border-t">
                                            <p className="text-xs font-medium text-gray-600 mb-2">Brand Template Message (will be created in VoiceBox):</p>
                                            <div className="bg-purple-50 border border-purple-200 rounded p-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="inline-block px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-medium rounded uppercase">{t.brandTemplateCopy.messageType}</span>
                                                    <span className="inline-block px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-medium rounded">Brand Template</span>
                                                </div>
                                                <p className="text-xs text-gray-700"><span className="font-medium">Source template:</span> {t.brandTemplateCopy.originalMessageName}</p>
                                                <p className="text-xs text-gray-700"><span className="font-medium">New message name:</span> {t.brandTemplateCopy.newMessageName}</p>
                                                {t.brandTemplateCopy.tagsFound && t.brandTemplateCopy.tagsFound.length > 0 && (
                                                    <p className="text-xs text-gray-500 mt-1"><span className="font-medium">Tags found:</span> {t.brandTemplateCopy.tagsFound.map(tag => `%%${tag}%%`).join(', ')}</p>
                                                )}
                                                {Object.keys(t.brandTemplateCopy.substitutions || {}).length > 0 && (
                                                    <div className="mt-1">
                                                        <p className="text-xs font-medium text-gray-500">Substitutions:</p>
                                                        {Object.entries(t.brandTemplateCopy.substitutions).map(([tag, val]) => (
                                                            <p key={tag} className="text-xs text-gray-600 ml-2">
                                                                <code className="bg-gray-100 px-1 rounded">%%{tag}%%</code> → <span className={val.includes('%%') ? 'text-orange-500 italic' : 'text-green-700 font-medium'}>{val}</span>
                                                            </p>
                                                        ))}
                                                    </div>
                                                )}
                                                {t.brandTemplateCopy.newSubject && <p className="text-xs text-gray-600 mt-1"><span className="font-medium">New Subject:</span> {t.brandTemplateCopy.newSubject}</p>}
                                                {t.brandTemplateCopy.newHtmlContentPreview && (
                                                    <details className="mt-2">
                                                        <summary className="text-xs text-purple-600 cursor-pointer hover:text-purple-800">HTML Content Preview</summary>
                                                        <pre className="bg-white border rounded p-2 mt-1 text-xs text-gray-700 whitespace-pre-wrap break-all max-h-32 overflow-y-auto">{t.brandTemplateCopy.newHtmlContentPreview}</pre>
                                                    </details>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <details className="px-4 pb-3">
                                        <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">Full Parameters JSON</summary>
                                        <pre className="bg-gray-50 border rounded p-2 mt-2 text-xs text-gray-800 whitespace-pre-wrap break-all max-h-64 overflow-y-auto">{t.parametersJson}</pre>
                                    </details>
                                </div>
                            ))}
                        </div>
                        <div className="p-6 border-t flex justify-end">
                            <button onClick={() => setCopyDryRunResult(null)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Variable Mapping Modal */}
            {showVariableMapping && (
                <VariableMappingModal
                    mappings={variableMappings}
                    availableFields={availableFields}
                    messageCatalog={messageCatalog}
                    readOnly={readOnly}
                    onSave={(mappings) => {
                        setVariableMappings(mappings);
                        setShowVariableMapping(false);
                    }}
                    onClose={() => setShowVariableMapping(false)}
                />
            )}

            {/* Execution Logs Modal */}
            {showExecutionLogs && (
                <ExecutionLogsModal
                    logs={executionLogs}
                    onRefresh={loadExecutionLogs}
                    onClose={() => setShowExecutionLogs(false)}
                />
            )}

            {/* Template Selection Modal */}
            {showTemplateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Select Workflow Template
                            </h2>
                            <button onClick={() => setShowTemplateModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            {workflowTemplates.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">No templates available</p>
                            ) : (
                                <div className="space-y-3">
                                    {workflowTemplates.map((template) => (
                                        <div
                                            key={template.id}
                                            onClick={async () => {
                                                try {
                                                    const result = await apiClient.post(`/workflows/templates/${template.id}/apply`, {
                                                        name: `${template.name} (Copy)`,
                                                        description: template.description,
                                                        group_id: selectedGroupId || null,
                                                        target_locations: targetAllLocations ? 'all' : selectedLocationIds
                                                    });
                                                    
                                                    if (result.workflow) {
                                                        setShowTemplateModal(false);
                                                        navigate(`/admin/workflow-builder/${result.workflow.id}`);
                                                    }
                                                } catch (error) {
                                                    alert('Failed to apply template: ' + (error.message || 'Unknown error'));
                                                }
                                            }}
                                            className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <h3 className="font-medium text-gray-900">{template.name}</h3>
                                                    {template.description && (
                                                        <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                                                    )}
                                                    <div className="flex gap-2 mt-2">
                                                        {template.category && (
                                                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                                                                {template.category}
                                                            </span>
                                                        )}
                                                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-800 rounded">
                                                            {template.workflow_type}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Save as Template Modal */}
            {showSaveTemplateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="p-6 border-b flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Save as Template
                            </h2>
                            <button onClick={() => setShowSaveTemplateModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Template Name *
                                </label>
                                <input
                                    type="text"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                    placeholder={workflowName || 'Template Name'}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={templateDescription}
                                    onChange={(e) => setTemplateDescription(e.target.value)}
                                    placeholder={workflowDescription || 'Template description'}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Category
                                </label>
                                <select
                                    value={templateCategory}
                                    onChange={(e) => setTemplateCategory(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select category...</option>
                                    <option value="sales">Sales</option>
                                    <option value="service">Service</option>
                                    <option value="welcome">Welcome</option>
                                    <option value="reminder">Reminder</option>
                                    <option value="general">General</option>
                                </select>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    onClick={() => setShowSaveTemplateModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={async () => {
                                        if (!templateName.trim()) {
                                            alert('Please enter a template name');
                                            return;
                                        }
                                        try {
                                            await apiClient.post('/workflows/templates', {
                                                name: templateName,
                                                description: templateDescription,
                                                category: templateCategory,
                                                workflow_type: 'email',
                                                workflow_id: id
                                            });
                                            alert('Template saved successfully!');
                                            setShowSaveTemplateModal(false);
                                            setTemplateName('');
                                            setTemplateDescription('');
                                            setTemplateCategory('');
                                            loadWorkflowTemplates();
                                        } catch (error) {
                                            alert('Failed to save template: ' + (error.message || 'Unknown error'));
                                        }
                                    }}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Save Template
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Reusable Searchable Select Component
const SearchableSelect = ({ value, onChange, options = [], placeholder = 'Select...', disabled = false, className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const selectedOption = options.find(o => String(o.value) === String(value));

    const filteredOptions = search
        ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
        : options;

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <button
                type="button"
                onClick={() => { if (!disabled) setIsOpen(!isOpen); }}
                disabled={disabled}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md text-left flex justify-between items-center text-sm ${
                    disabled ? 'bg-gray-100 cursor-not-allowed text-gray-400' : 'bg-white hover:border-gray-400 cursor-pointer'
                }`}
            >
                <span className={selectedOption ? 'text-gray-900 truncate' : 'text-gray-500 truncate'}>
                    {selectedOption?.label || placeholder}
                </span>
                <ChevronDown size={16} className={`text-gray-400 flex-shrink-0 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                    <div className="p-2 border-b border-gray-200">
                        <div className="relative">
                            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Type to search..."
                                className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div className="overflow-y-auto max-h-56">
                        {filteredOptions.length > 0 ? filteredOptions.map((option, idx) => (
                            <div
                                key={option.value ?? idx}
                                onClick={() => {
                                    onChange(option.value);
                                    setIsOpen(false);
                                    setSearch('');
                                }}
                                className={`px-3 py-2 cursor-pointer text-sm hover:bg-blue-50 ${
                                    String(option.value) === String(value) ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                                }`}
                            >
                                {option.label}
                            </div>
                        )) : (
                            <div className="px-3 py-3 text-sm text-gray-400 text-center">No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Update Contact Configuration Component (mirrors VoiceBox "Update Contact Values" UI)
const UpdateContactConfig = ({ config, setConfig, selectedGroupId, nodeId, nodes, edges }) => {
    const [listFields, setListFields] = useState([]);
    const [loadingFields, setLoadingFields] = useState(false);

    const upstreamList = findUpstreamListSelector(nodeId, nodes, edges);

    useEffect(() => {
        if (!upstreamList?.listId || !selectedGroupId) {
            setListFields([]);
            return;
        }
        const loadFields = async () => {
            setLoadingFields(true);
            try {
                const mappingResult = await apiClient.get('/voicebox/client-mappings');
                const mappings = mappingResult.mappings || [];
                const mapping = mappings.find(m => m.group_id === selectedGroupId);
                if (mapping) {
                    const res = await apiClient.get(`/voicebox/client-mappings/${mapping.id}/list-fields/${upstreamList.listId}`);
                    setListFields(res.fields || []);
                }
            } catch (err) {
                console.warn('Could not load list fields for Update Contact:', err.message);
                setListFields([]);
            } finally {
                setLoadingFields(false);
            }
        };
        loadFields();
    }, [upstreamList?.listId, selectedGroupId]);

    const changes = Array.isArray(config.changes) ? config.changes : [];

    const fieldOptions = listFields.map(f => ({
        value: f.FieldName,
        label: f.DisplayName || f.FieldName
    }));

    const isDateField = (fieldName) => {
        const field = listFields.find(f => f.FieldName === fieldName);
        if (!field) return false;
        const sqlType = (field.SqlType || '').toLowerCase();
        return sqlType.includes('date') || sqlType.includes('time');
    };

    const getUpdateTypeOptions = (fieldName) => {
        const base = [
            { value: 'RawValue', label: 'Set To' },
            { value: 'FieldName', label: 'Set To Field' },
            { value: 'Reset', label: 'Reset' },
        ];
        if (isDateField(fieldName)) {
            base.push({ value: 'CurrentDateTimeUtc', label: 'Current Date & Time' });
        }
        return base;
    };

    const updateTypeLabelMap = {
        'RawValue': 'Set To',
        'FieldName': 'Set To Field',
        'Reset': 'Reset',
        'CurrentDateTimeUtc': 'Current Date & Time'
    };

    const addField = () => {
        setConfig({
            ...config,
            changes: [...changes, { name: '', updateType: '', updateTypeName: '', argument: '' }]
        });
    };

    const removeField = (index) => {
        const updated = changes.filter((_, i) => i !== index);
        setConfig({ ...config, changes: updated });
    };

    const updateChange = (index, key, value) => {
        const updated = changes.map((ch, i) => {
            if (i !== index) return ch;
            const newCh = { ...ch, [key]: value };
            if (key === 'updateType') {
                newCh.updateTypeName = updateTypeLabelMap[value] || value;
                if (value === 'Reset' || value === 'CurrentDateTimeUtc') {
                    newCh.argument = '';
                }
            }
            if (key === 'name') {
                newCh.updateType = '';
                newCh.updateTypeName = '';
                newCh.argument = '';
            }
            return newCh;
        });
        setConfig({ ...config, changes: updated });
    };

    const hasDuplicates = (() => {
        const names = changes.map(c => c.name).filter(Boolean);
        return new Set(names).size !== names.length;
    })();

    return (
        <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-900">Update Contact Values</h4>
                <button
                    type="button"
                    onClick={addField}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500 text-white rounded-md hover:bg-cyan-600 text-xs font-medium"
                >
                    <Plus size={14} />
                    Add New Field
                </button>
            </div>

            {!upstreamList && (
                <div className="bg-yellow-50 border border-yellow-300 rounded-md p-3 text-sm text-yellow-800">
                    <AlertCircle size={14} className="inline mr-1" />
                    No upstream List Selector node found. Connect a List Selector node to load contact fields.
                </div>
            )}

            {upstreamList && loadingFields && (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <Loader size={14} className="animate-spin" />
                    Loading contact fields...
                </div>
            )}

            {upstreamList && !loadingFields && listFields.length === 0 && (
                <div className="text-sm text-gray-500 py-2">
                    No fields found for the selected list.
                </div>
            )}

            {changes.length > 0 && (
                <div className="space-y-2">
                    {changes.map((change, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-md p-3">
                            <div className="flex items-start gap-2">
                                <div className="flex-1 grid grid-cols-3 gap-2">
                                    {/* Contact Value */}
                                    <div>
                                        <label className="block text-xs font-medium text-orange-500 mb-1">Contact Value</label>
                                        <SearchableSelect
                                            value={change.name}
                                            onChange={(val) => updateChange(index, 'name', val)}
                                            options={fieldOptions}
                                            placeholder="Select field..."
                                            disabled={loadingFields || listFields.length === 0}
                                        />
                                    </div>

                                    {/* Update Type - shown only when field is selected */}
                                    <div>
                                        <label className="block text-xs font-medium text-orange-500 mb-1">Update Type</label>
                                        {change.name ? (
                                            <SearchableSelect
                                                value={change.updateType}
                                                onChange={(val) => updateChange(index, 'updateType', val)}
                                                options={getUpdateTypeOptions(change.name)}
                                                placeholder="Select type..."
                                            />
                                        ) : (
                                            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-400">
                                                Select field first
                                            </div>
                                        )}
                                    </div>

                                    {/* Argument - shown based on update type */}
                                    <div>
                                        <label className="block text-xs font-medium text-orange-500 mb-1">Value</label>
                                        {!change.updateType ? (
                                            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-400">
                                                &mdash;
                                            </div>
                                        ) : change.updateType === 'Reset' ? (
                                            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100 text-sm text-gray-500 italic">
                                                Field will be cleared
                                            </div>
                                        ) : change.updateType === 'CurrentDateTimeUtc' ? (
                                            <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100 text-sm text-gray-500 italic">
                                                Current date &amp; time
                                            </div>
                                        ) : change.updateType === 'FieldName' ? (
                                            <SearchableSelect
                                                value={change.argument}
                                                onChange={(val) => updateChange(index, 'argument', val)}
                                                options={fieldOptions}
                                                placeholder="Select field..."
                                            />
                                        ) : (
                                            <input
                                                type={isDateField(change.name) ? 'datetime-local' : 'text'}
                                                value={change.argument || ''}
                                                onChange={(e) => updateChange(index, 'argument', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Enter value..."
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Delete button */}
                                <button
                                    type="button"
                                    onClick={() => removeField(index)}
                                    className="mt-5 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                                    title="Remove field"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {changes.length === 0 && upstreamList && !loadingFields && listFields.length > 0 && (
                <div className="text-sm text-gray-500 py-3 text-center border border-dashed border-gray-300 rounded-md">
                    No field changes configured. Click "Add New Field" to start.
                </div>
            )}

            {hasDuplicates && (
                <div className="bg-yellow-100 border border-yellow-400 rounded-md p-2 text-sm text-yellow-800 text-center">
                    <AlertCircle size={14} className="inline mr-1" />
                    Duplicate fields selected. This may cause issues when the action is executed.
                </div>
            )}
        </div>
    );
};

// Message Configuration Component
const MessageConfig = ({ config, setConfig, messageCatalog, onSyncMessages, syncingMessages, syncMessagesStatus, selectedGroupId }) => {
    const [brandTemplates, setBrandTemplates] = useState([]);
    const [brands, setBrands] = useState([]);
    const [selectedBrandId, setSelectedBrandId] = useState(config.brandId || null);
    const [templateSource, setTemplateSource] = useState(config.templateSource || 'voicebox'); // 'voicebox' or 'brand'
    const [selectedFolder, setSelectedFolder] = useState(() => {
        if (config.messageFolder) return config.messageFolder;
        if (config.messageId && messageCatalog.length > 0) {
            const msg = messageCatalog.find(m => m.voicebox_message_id === config.messageId);
            return msg?.folder_path || '';
        }
        return '';
    });

    // Auto-resolve folder from catalog when it loads (catalog may arrive after mount)
    useEffect(() => {
        if (!selectedFolder && config.messageId && messageCatalog.length > 0) {
            const msg = messageCatalog.find(m => m.voicebox_message_id === parseInt(config.messageId));
            if (msg?.folder_path) setSelectedFolder(msg.folder_path);
        }
    }, [messageCatalog, config.messageId, selectedFolder]);
    
    // Derive folders and filtered messages from catalog
    const messageFolders = [...new Set(messageCatalog.map(m => m.folder_path).filter(Boolean))].sort();
    const filteredMessages = selectedFolder
        ? messageCatalog.filter(m => m.folder_path === selectedFolder)
        : messageCatalog;
    const sortedMessages = [...filteredMessages].sort((a, b) => (a.message_name || '').localeCompare(b.message_name || ''));
    
    // Determine selected message - either from VoiceBox catalog or brand templates
    const selectedMessage = templateSource === 'brand' && config.brandTemplateId
        ? brandTemplates.find(t => t.id === config.brandTemplateId)
        : messageCatalog.find(m => m.voicebox_message_id === config.messageId);
    
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState('');
    const [previewDesign, setPreviewDesign] = useState(null);
    const [previewHtml, setPreviewHtml] = useState('');

    // Load brands and brand templates
    useEffect(() => {
        loadBrands();
    }, []);

    useEffect(() => {
        if (selectedBrandId) {
            loadBrandTemplates(selectedBrandId);
        } else {
            setBrandTemplates([]);
        }
    }, [selectedBrandId]);

    const loadBrands = async () => {
        try {
            const result = await apiClient.get('/brands');
            setBrands(result || []);
        } catch (error) {
            console.error('Failed to load brands:', error);
        }
    };

    const loadBrandTemplates = async (brandId) => {
        try {
            const result = await apiClient.get(`/brand-templates?brand_id=${brandId}`);
            setBrandTemplates(result || []);
        } catch (error) {
            console.error('Failed to load brand templates:', error);
            setBrandTemplates([]);
        }
    };
    
    // Debug logging
    if (selectedMessage) {
        console.log('Selected message:', {
            voicebox_message_id: selectedMessage.voicebox_message_id,
            message_name: selectedMessage.message_name,
            available_tags: selectedMessage.available_tags,
            tags_type: typeof selectedMessage.available_tags,
            tags_is_array: Array.isArray(selectedMessage.available_tags)
        });
    }
    
    // Parse available_tags - handle string, array, object, or null
    let availableTags = [];
    if (selectedMessage?.available_tags) {
        if (typeof selectedMessage.available_tags === 'string') {
            try {
                const parsed = JSON.parse(selectedMessage.available_tags);
                availableTags = Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                console.debug('Failed to parse available_tags as JSON:', e);
                availableTags = [];
            }
        } else if (Array.isArray(selectedMessage.available_tags)) {
            availableTags = selectedMessage.available_tags;
        } else if (typeof selectedMessage.available_tags === 'object') {
            // If it's an object but not an array, it's likely an empty object {} or malformed data
            // This is already handled by the backend, but handle it here too for safety
            availableTags = [];
        }
    }
    
    // Only log in debug mode - messages without tags are normal for non-personalized messages
    if (selectedMessage && availableTags.length === 0 && process.env.NODE_ENV === 'development') {
        console.debug('Message has no personalization tags (this is normal for non-personalized messages):', {
            message_id: selectedMessage.voicebox_message_id,
            message_name: selectedMessage.message_name
        });
    }

    const handlePreview = async () => {
        if (!selectedGroupId && templateSource === 'voicebox') {
            setPreviewError('Select a dealership group first.');
            setPreviewOpen(true);
            return;
        }

        if (templateSource === 'voicebox' && !config.messageId) {
            setPreviewError('Select a message template first.');
            setPreviewOpen(true);
            return;
        }

        if (templateSource === 'brand' && !config.brandTemplateId) {
            setPreviewError('Select a brand template first.');
            setPreviewOpen(true);
            return;
        }

        setPreviewLoading(true);
        setPreviewError('');
        setPreviewDesign(null);
        setPreviewHtml('');

        try {
            // Handle brand templates
            if (templateSource === 'brand' && config.brandTemplateId) {
                const brandTemplateResult = await apiClient.get(`/contact-messages/${config.brandTemplateId}`);
                const brandTemplate = brandTemplateResult;

                if (brandTemplate?.unlayer_json) {
                    try {
                        const parsed = typeof brandTemplate.unlayer_json === 'string'
                            ? JSON.parse(brandTemplate.unlayer_json)
                            : brandTemplate.unlayer_json;
                        setPreviewDesign(parsed);
                    } catch (error) {
                        setPreviewHtml(brandTemplate.html_content || brandTemplate.message_content || '');
                    }
                } else {
                    setPreviewHtml(brandTemplate?.html_content || brandTemplate?.message_content || '');
                }
                setPreviewOpen(true);
                return;
            }

            // Handle VoiceBox messages (original logic)
            if (templateSource === 'voicebox' && config.messageId) {
                const mappingsResult = await apiClient.get('/voicebox/client-mappings');
                const mapping = (mappingsResult.mappings || []).find(item => String(item.group_id) === String(selectedGroupId));

                if (!mapping) {
                    throw new Error('No VoiceBox mapping found for this group.');
                }

                const messageResult = await apiClient.get(`/voicebox/client-mappings/${mapping.id}/messages/${config.messageId}`);
                const message = messageResult.message;

                if (message?.EmailContentJson) {
                    try {
                        const parsed = typeof message.EmailContentJson === 'string'
                            ? JSON.parse(message.EmailContentJson)
                            : message.EmailContentJson;
                        setPreviewDesign(parsed);
                    } catch (error) {
                        setPreviewHtml(message.HtmlContent || message.MessageContent || message.Content || '');
                    }
                } else {
                    setPreviewHtml(message?.HtmlContent || message?.MessageContent || message?.Content || '');
                }
                setPreviewOpen(true);
            }
        } catch (error) {
            setPreviewError(error.message || 'Failed to load preview.');
            setPreviewOpen(true);
        } finally {
            setPreviewLoading(false);
        }
    };

    return (
        <>
            <div className="space-y-4">
                {/* Template Source Selection */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Template Source
                    </label>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setTemplateSource('voicebox');
                                setConfig({ ...config, templateSource: 'voicebox', brandTemplateId: null });
                            }}
                            className={`px-4 py-2 rounded-md text-sm font-medium ${
                                templateSource === 'voicebox'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            VoiceBox Messages
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setTemplateSource('brand');
                                setConfig({ ...config, templateSource: 'brand', messageId: null });
                            }}
                            className={`px-4 py-2 rounded-md text-sm font-medium ${
                                templateSource === 'brand'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            Brand Templates
                        </button>
                    </div>
                </div>

                {/* Brand Selection (for brand templates) */}
                {templateSource === 'brand' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Brand *
                        </label>
                        <SearchableSelect
                            value={selectedBrandId || ''}
                            onChange={(val) => {
                                setSelectedBrandId(val);
                                setConfig({ ...config, brandId: val, brandTemplateId: null });
                            }}
                            options={brands.map(brand => ({ value: brand.id, label: brand.name }))}
                            placeholder="Select a brand..."
                        />
                    </div>
                )}

                {/* Folder Selection (for VoiceBox messages) */}
                {templateSource === 'voicebox' && messageFolders.length > 0 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Folder
                        </label>
                        <SearchableSelect
                            value={selectedFolder}
                            onChange={(val) => {
                                setSelectedFolder(val);
                                setConfig({ ...config, messageFolder: val, messageId: null, messageName: '' });
                            }}
                            options={[
                                { value: '', label: `All folders (${messageCatalog.length} messages)` },
                                ...messageFolders.map(folder => ({
                                    value: folder,
                                    label: `${folder} (${messageCatalog.filter(m => m.folder_path === folder).length})`
                                }))
                            ]}
                            placeholder={`All folders (${messageCatalog.length} messages)`}
                        />
                    </div>
                )}

                {/* Template Selection */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Message Template
                    </label>
                    {templateSource === 'brand' ? (
                        <SearchableSelect
                            value={config.brandTemplateId || ''}
                            onChange={(val) => {
                                const selectedTemplate = brandTemplates.find(t => t.id === val);
                                setConfig({
                                    ...config,
                                    brandTemplateId: val,
                                    messageName: selectedTemplate?.message_name || '',
                                    messageType: selectedTemplate?.message_type || 'email',
                                    subject: selectedTemplate?.subject || '',
                                    fromAddress: selectedTemplate?.from_address || '',
                                    fromName: selectedTemplate?.from_name || ''
                                });
                            }}
                            options={brandTemplates.map(t => ({
                                value: t.id,
                                label: `${t.message_name} (${t.message_type?.toLowerCase() || 'email'})`
                            }))}
                            placeholder={selectedBrandId ? 'Select a brand template...' : 'Select a brand first...'}
                            disabled={!selectedBrandId}
                        />
                    ) : (
                        <SearchableSelect
                            value={config.messageId || ''}
                            onChange={(val) => {
                                const messageId = parseInt(val);
                                const selectedMsg = messageCatalog.find(m => m.voicebox_message_id === messageId);
                                setConfig({
                                    ...config,
                                    messageId: messageId,
                                    messageName: selectedMsg?.message_name || '',
                                    messageType: selectedMsg?.message_type || 'email',
                                    subject: selectedMsg?.subject || '',
                                    fromAddress: selectedMsg?.from_address || '',
                                    fromName: selectedMsg?.from_name || ''
                                });
                            }}
                            options={sortedMessages.map(msg => ({
                                value: msg.voicebox_message_id,
                                label: `${msg.message_name} (${msg.message_type || 'email'})`
                            }))}
                            placeholder={selectedFolder ? `Select a message from "${selectedFolder}"...` : 'Select a message template...'}
                        />
                    )}
                </div>
                {messageCatalog.length === 0 && (
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-sm text-yellow-800 font-medium mb-1">
                            No messages available in catalog
                        </p>
                        <p className="text-xs text-yellow-700">
                            Messages need to be synced from VoiceBox first. Go to the <strong>VoiceBox Sync</strong> page 
                            to sync messages for your client mappings.
                        </p>
                    </div>
                )}
            </div>

            {config.messageId && selectedMessage && (
                <div className="mt-4 space-y-3">
                    {/* Message Details */}
                    <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                        <div className="font-medium text-green-900 text-sm mb-2">Message Details</div>
                        <div className="space-y-1 text-xs">
                            {selectedMessage.subject && (
                                <div><strong>Subject:</strong> {selectedMessage.subject}</div>
                            )}
                            {selectedMessage.from_name && (
                                <div><strong>From:</strong> {selectedMessage.from_name} &lt;{selectedMessage.from_address}&gt;</div>
                            )}
                            <div><strong>Type:</strong> {selectedMessage.message_type || 'email'}</div>
                        </div>
                    </div>

                    {selectedMessage.message_type !== 'sms' && (
                        <div className="flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={handlePreview}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm text-white bg-indigo-600 hover:bg-indigo-700"
                            >
                                <Eye size={14} />
                                Preview in Unlayer
                            </button>
                        </div>
                    )}

                    {/* Available Tags */}
                    {availableTags.length > 0 ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Available Tags in Template ({availableTags.length})
                            </label>
                            <div className="p-3 bg-gray-50 border rounded-md">
                                <div className="flex flex-wrap gap-2">
                                    {availableTags.map((tag, idx) => (
                                        <span 
                                            key={idx} 
                                            className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono"
                                            title={`Tag: ${tag}`}
                                        >
                                            %%{tag}%%
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-2 text-xs text-gray-600">
                                    <strong>Note:</strong> These tags will be replaced with actual values when the message is sent. 
                                    Configure variable mappings in the workflow settings to map Clientbox fields to these tags.
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                            <div className="text-sm text-gray-600">
                                No tags found in this template. The message may not use personalization variables.
                            </div>
                        </div>
                    )}
                    {onSyncMessages && (
                        <div className="flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={onSyncMessages}
                                disabled={syncingMessages}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300"
                            >
                                {syncingMessages ? (
                                    <>
                                        <Loader className="animate-spin" size={14} />
                                        Syncing VoiceBox Messages...
                                    </>
                                ) : (
                                    'Sync VoiceBox Messages'
                                )}
                            </button>
                            {syncMessagesStatus === 'success' && (
                                <div className="text-xs text-green-600 flex items-center gap-1">
                                    <CheckCircle size={12} />
                                    Messages synced successfully
                                </div>
                            )}
                            {syncMessagesStatus === 'error' && (
                                <div className="text-xs text-red-600 flex items-center gap-1">
                                    <XCircle size={12} />
                                    Failed to sync messages
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {previewOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between border-b px-6 py-4">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Message Preview</h3>
                                <p className="text-xs text-gray-500">
                                    {templateSource === 'brand' 
                                        ? 'Brand template rendered with Unlayer when available.' 
                                        : 'VoiceBox message rendered with Unlayer when available.'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setPreviewOpen(false)}
                                className="text-gray-500 hover:text-gray-800"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-6">
                            {previewLoading && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Loader className="animate-spin" size={16} />
                                    Loading preview...
                                </div>
                            )}
                            {!previewLoading && previewError && (
                                <div className="text-sm text-red-600">{previewError}</div>
                            )}
                            {!previewLoading && !previewError && previewDesign && (
                                <UnlayerEditor
                                    design={previewDesign}
                                    onSave={null}
                                    editorId={`preview-${config.messageId}`}
                                    displayMode="email"
                                    showSaveButton={false}
                                />
                            )}
                            {!previewLoading && !previewError && !previewDesign && previewHtml && (
                                <iframe
                                    title="Message HTML Preview"
                                    className="w-full min-h-[500px] border rounded"
                                    srcDoc={previewHtml}
                                    sandbox="allow-same-origin"
                                />
                            )}
                            {!previewLoading && !previewError && !previewDesign && !previewHtml && (
                                <div className="text-sm text-gray-600">
                                    No preview content found for this message.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// List Selector Configuration Component
const ListSelectorConfig = ({ config, setConfig, availableLists, listsError, loadingLists, onRetryLists, selectedGroupId }) => {
    const [selectedListIds, setSelectedListIds] = useState(() => {
        const raw = config.listIds ? (Array.isArray(config.listIds) ? config.listIds : [config.listIds]) : 
            (config.listId ? [config.listId] : 
            (config.vbListId ? [config.vbListId] : []));
        return raw.map(id => parseInt(id)).filter(id => !isNaN(id));
    });
    const [listSearchTerm, setListSearchTerm] = useState('');

    const handleListChange = (listId, checked) => {
        const listIdNum = parseInt(listId);
        let newListIds;
        
        if (checked) {
            newListIds = [...selectedListIds, listIdNum];
        } else {
            newListIds = selectedListIds.filter(id => id !== listIdNum);
        }
        
        setSelectedListIds(newListIds);
        
        // Update config with selected lists
        const selectedLists = availableLists.filter(l => newListIds.includes(l.listId));
        setConfig({
            ...config,
            listIds: newListIds,
            listId: newListIds.length === 1 ? newListIds[0] : null, // Keep for backward compatibility
            listName: selectedLists.map(l => l.listName).join(', '),
            tableNames: selectedLists.map(l => l.tableName),
            tableName: selectedLists.length === 1 ? selectedLists[0].tableName : null // Keep for backward compatibility
        });
    };

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Lists (Multiple Selection Supported)
            </label>
            {availableLists.length > 0 ? (
                <>
                    <input
                        type="text"
                        placeholder="Search lists..."
                        value={listSearchTerm}
                        onChange={(e) => setListSearchTerm(e.target.value)}
                        className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500"
                    />
                    <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md p-2">
                                        {availableLists.filter(list => list.listName.toLowerCase().includes(listSearchTerm.toLowerCase())).map((list) => {
                                            const isSelected = selectedListIds.includes(list.listId);
                                            return (
                                                <label
                                                    key={list.listId}
                                                    className="flex items-start gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={(e) => handleListChange(list.listId, e.target.checked)}
                                                        className="mt-1"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-gray-900">{list.listName}</div>
                                                        {list.description && (
                                                            <div className="text-xs text-gray-600 mt-1">{list.description}</div>
                                                        )}
                                                    </div>
                                                </label>
                                            );
                                        })}
                    </div>
                    {selectedListIds.length > 0 && (
                        <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-md">
                            <div className="flex items-center justify-between mb-2">
                                <div className="font-medium text-indigo-900 text-sm">
                                    Selected Lists ({selectedListIds.length})
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedListIds([]);
                                        setConfig({
                                            ...config,
                                            listIds: [],
                                            listId: null,
                                            listName: '',
                                            tableNames: [],
                                            tableName: null
                                        });
                                    }}
                                    className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded border border-red-200"
                                >
                                    <X size={12} />
                                    Clear Selection
                                </button>
                            </div>
                            <div className="space-y-1">
                                {selectedListIds.map(listId => {
                                    const list = availableLists.find(l => l.listId === listId);
                                    return list ? (
                                        <div key={listId} className="text-xs text-indigo-700">
                                            • {list.listName}
                                        </div>
                                    ) : null;
                                })}
                            </div>
                            {selectedListIds.length > 1 && (
                                <div className="mt-2 text-xs text-indigo-600">
                                    <strong>Note:</strong> Multiple lists will be combined using UNION ALL in the generated SQL.
                                </div>
                            )}
                        </div>
                    )}
                </>
            ) : (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    {loadingLists ? (
                        <div className="flex items-center gap-2">
                            <Loader className="animate-spin text-yellow-600" size={16} />
                            <p className="text-sm text-yellow-800">Loading lists from VoiceBox...</p>
                        </div>
                    ) : listsError ? (
                        <>
                            <p className="text-sm text-yellow-800 font-medium mb-1">
                                Unable to load lists
                            </p>
                            <p className="text-xs text-yellow-700 mb-2">
                                {listsError}
                            </p>
                            {onRetryLists && (
                                <button
                                    onClick={onRetryLists}
                                    className="text-xs text-yellow-800 underline hover:text-yellow-900"
                                >
                                    Retry loading lists
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-yellow-800 font-medium mb-1">
                                No lists available
                            </p>
                            <p className="text-xs text-yellow-700">
                                {selectedGroupId 
                                    ? 'No lists found for the selected client. Lists are fetched directly from VoiceBox. Please ensure lists exist in VoiceBox and the connection is working.'
                                    : 'Please select a client/group first. Lists will be fetched automatically from VoiceBox.'}
                            </p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// ===== VoiceBox Trigger Type Definitions =====
// Mirrors VoiceBox TriggerService.cs EventInfoModel structure
const VOICEBOX_TRIGGER_TYPES = {
    // List-related triggers (SubjectType = List or ListField)
    // subjectEventType mirrors TriggerService.cs for import/export mapping
    list: [
        { eventId: 1,  name: 'Time Based',              subjectType: 'ListField', subjectEventType: 1 },
        { eventId: 18, name: 'Time Based One Off Send',  subjectType: 'ListField', subjectEventType: 3 },
        { eventId: 8,  name: 'Manually Updated',         subjectType: 'List',      subjectEventType: 1 },
        { eventId: 9,  name: 'Contact Added',            subjectType: 'List',      subjectEventType: 2 },
        { eventId: 10, name: 'SFTP Bulk Upload',         subjectType: 'List',      subjectEventType: 3 },
        { eventId: 11, name: 'Manual Bulk Upload',       subjectType: 'List',      subjectEventType: 4 },
        { eventId: 12, name: 'Subscribe Email',          subjectType: 'List',      subjectEventType: 6, formTypeId: 1 },
        { eventId: 13, name: 'Subscribe Sms',            subjectType: 'List',      subjectEventType: 7 },
        { eventId: 14, name: 'Unsubscribe Email',        subjectType: 'List',      subjectEventType: 8, formTypeId: 3 },
        { eventId: 15, name: 'Unsubscribe Sms',          subjectType: 'List',      subjectEventType: 9 },
        { eventId: 16, name: 'Update Profile',           subjectType: 'List',      subjectEventType: 10, formTypeId: 2 },
        { eventId: 17, name: 'Bounced Email',            subjectType: 'List',      subjectEventType: 12 },
        { eventId: 20, name: 'URL Link Click',           subjectType: 'List',      subjectEventType: 13 },
        { eventId: 23, name: 'Unsubscribe Rcs',          subjectType: 'List',      subjectEventType: 14 },
    ],
    // Message-related triggers (SubjectType = Message), filtered by message type
    message: {
        email: [
            { eventId: 3, name: 'Open',             subjectType: 'Message', subjectEventType: 1 },
            { eventId: 4, name: 'Not Open',          subjectType: 'Message', subjectEventType: 2 },
            { eventId: 5, name: 'Link Click',        subjectType: 'Message', subjectEventType: 3 },
            { eventId: 6, name: 'Permanent Bounce',  subjectType: 'Message', subjectEventType: 4 },
        ],
        sms: [
            { eventId: 7, name: 'SMS Reply', subjectType: 'Message', subjectEventType: 5 },
        ],
        rcs: [
            { eventId: 21, name: 'RCS Reply',    subjectType: 'Message', subjectEventType: 6 },
            { eventId: 22, name: 'RCS Postback',  subjectType: 'Message', subjectEventType: 7 },
        ],
    },
};

// Trigger types that require additional forms
const TRIGGER_FORM_REQUIREMENTS = {
    1:  { type: 'timeBased' },          // Time Based → field, frequency, number, period, before/after, time
    18: { type: 'timeBasedOneOff' },     // Time Based One Off Send → datetime picker
    5:  { type: 'linkClick' },           // Link Click → select a link dropdown
    12: { type: 'webForm', formTypeId: 1 },  // Subscribe Email → select a form
    14: { type: 'webForm', formTypeId: 3 },  // Unsubscribe Email → select a form
    16: { type: 'webForm', formTypeId: 2 },  // Update Profile → select a form
    20: { type: 'targetUrl' },           // URL Link Click → target URL input
};

// VoiceBox Trigger Configuration — "Which will be triggered by" dropdown + sub-forms
// List/Message selection is handled by preceding nodes (List Selector / other nodes)
// For message-type triggers we show message selection inline
const TriggerMessageConfig = ({
    config, setConfig,
    messageCatalog = [], availableLists = [],
    messageFolders: loadedMsgFolders = [], listFolders: loadedListFolders = [],
    selectedGroupId = null, nodes = [], edges = [], nodeId = null,
}) => {
    const SMS_CATALOG_OFFSET = 1000000;

    const findCatalogMessage = (id, type, catalog) => {
        if (!id || !catalog.length) return null;
        const numId = typeof id === 'string' ? parseInt(id) : id;
        let msg = catalog.find(m => m.voicebox_message_id === numId);
        if (!msg && type === 'sms' && numId < SMS_CATALOG_OFFSET) {
            msg = catalog.find(m => m.voicebox_message_id === numId + SMS_CATALOG_OFFSET);
        }
        return msg;
    };

    // === Core State ===
    const [triggerEventId, setTriggerEventId] = useState(config.triggerEventId || '');
    const [messageType, setMessageType] = useState(config.messageType || 'email');
    const resolvedMsg = findCatalogMessage(config.messageId, config.messageType || 'email', messageCatalog);
    const resolvedMessageId = resolvedMsg?.voicebox_message_id || config.messageId || '';
    const [selectedFolder, setSelectedFolder] = useState(() => {
        if (config.messageFolderPath) return config.messageFolderPath;
        return resolvedMsg?.folder_path || '';
    });
    const [selectedMessageId, setSelectedMessageId] = useState(resolvedMessageId);

    // Auto-resolve folder from catalog when it loads (catalog may arrive after mount)
    useEffect(() => {
        if (!selectedFolder && selectedMessageId && messageCatalog.length > 0) {
            const msg = findCatalogMessage(selectedMessageId, messageType, messageCatalog);
            if (msg) {
                if (msg.folder_path) setSelectedFolder(msg.folder_path);
                if (msg.voicebox_message_id !== parseInt(selectedMessageId)) {
                    setSelectedMessageId(msg.voicebox_message_id);
                    setConfig(prev => ({ ...prev, messageId: msg.voicebox_message_id }));
                }
            }
        }
    }, [messageCatalog, selectedMessageId, selectedFolder, messageType]);

    // === Dynamic data for sub-forms ===
    const [listFields, setListFields] = useState([]);
    const [webForms, setWebForms] = useState([]);
    const [messageLinks, setMessageLinks] = useState([]);
    const [loadingSubForm, setLoadingSubForm] = useState(false);
    const [mappingIdRef, setMappingIdRef] = useState(null);

    // === Time Based form state ===
    const [tbField, setTbField] = useState(config.timeBasedField || '');
    const [tbFrequency, setTbFrequency] = useState(config.timeBasedFrequency || 'Once');
    const [tbNumberOf, setTbNumberOf] = useState(config.timeBasedNumberOf || '');
    const [tbPeriod, setTbPeriod] = useState(config.timeBasedPeriod || '');
    const [tbBeforeAfter, setTbBeforeAfter] = useState(config.timeBasedBeforeAfter || '');
    const [tbTime, setTbTime] = useState(config.timeBasedTime || '');
    const [oosDateTime, setOosDateTime] = useState(config.oneOffSendDateTime || '');
    const [selectedLinkId, setSelectedLinkId] = useState(config.messageLinkId || '');
    const [selectedFormId, setSelectedFormId] = useState(config.webFormId || '');
    const [targetUrl, setTargetUrl] = useState(config.targetUrl || '');

    // Resolve connected list from upstream List Selector node (recursive graph traversal)
    const upstreamListInfo = findUpstreamListSelector(nodeId, nodes, edges);
    const connectedListId = upstreamListInfo?.listId || null;
    const connectedListName = upstreamListInfo?.listName || null;

    // Resolve VoiceBox mapping ID
    useEffect(() => {
        const fetchMappingId = async () => {
            if (!selectedGroupId) return;
            try {
                const mappingResult = await apiClient.get('/voicebox/client-mappings');
                const mappings = mappingResult.mappings || [];
                const mapping = mappings.find(m => String(m.group_id) === String(selectedGroupId));
                if (mapping) setMappingIdRef(mapping.id);
            } catch (err) {
                console.error('Error fetching mapping for trigger config:', err);
            }
        };
        fetchMappingId();
    }, [selectedGroupId]);

    // === Derived data for message selection ===
    const messageFoldersFromCatalog = [...new Set(messageCatalog.filter(m => m.message_type === messageType).map(m => m.folder_path).filter(Boolean))].sort();
    const messageFolders = loadedMsgFolders.length > 0 ? loadedMsgFolders.map(f => f.folderName) : messageFoldersFromCatalog;
    const filteredMessages = messageCatalog
        .filter(m => m.message_type === messageType)
        .filter(m => !selectedFolder || m.folder_path === selectedFolder)
        .sort((a, b) => (a.message_name || '').localeCompare(b.message_name || ''));

    // Determine if selected trigger is a message trigger
    const allTriggerTypes = [...VOICEBOX_TRIGGER_TYPES.list, ...Object.values(VOICEBOX_TRIGGER_TYPES.message).flat()];
    const selectedTrigger = allTriggerTypes.find(t => t.eventId === parseInt(triggerEventId));
    const isMessageTrigger = selectedTrigger?.subjectType === 'Message';

    // Current trigger form requirement
    const triggerForm = triggerEventId ? TRIGGER_FORM_REQUIREMENTS[parseInt(triggerEventId)] : null;

    // Get the effective listId for sub-forms (from connected node)
    const effectiveListId = connectedListId;

    // ========== Load dynamic sub-form data ==========
    useEffect(() => {
        if (!triggerForm || !mappingIdRef) return;

        const loadSubFormData = async () => {
            setLoadingSubForm(true);
            try {
                if (triggerForm.type === 'timeBased' && effectiveListId) {
                    const res = await apiClient.get(`/voicebox/client-mappings/${mappingIdRef}/list-fields/${effectiveListId}?datetime_only=true`);
                    setListFields((res.fields || []).map(f => ({ fieldId: f.FieldID, columnName: f.ColumnName, displayName: f.DisplayName || f.FieldName, sqlType: f.SqlType })));
                } else if (triggerForm.type === 'webForm' && effectiveListId) {
                    const res = await apiClient.get(`/voicebox/client-mappings/${mappingIdRef}/web-forms/${effectiveListId}/${triggerForm.formTypeId}`);
                    setWebForms((res.forms || []).map(f => ({ formId: f.WebFormID, formName: f.WebFormName })));
                } else if (triggerForm.type === 'linkClick' && selectedMessageId) {
                    const res = await apiClient.get(`/voicebox/client-mappings/${mappingIdRef}/message-links/${selectedMessageId}`);
                    setMessageLinks((res.links || []).map(l => ({ linkId: l.LinkID, linkTitle: l.LinkTitle || l.URL, url: l.URL })));
                }
            } catch (err) {
                console.error('Error loading sub-form data:', err);
            } finally {
                setLoadingSubForm(false);
            }
        };
        loadSubFormData();
    }, [triggerForm?.type, triggerForm?.formTypeId, effectiveListId, selectedMessageId, mappingIdRef]);

    // ========== Handlers ==========
    const updateConfig = (patch) => setConfig(prev => ({ ...prev, ...patch }));

    const handleTriggerEventChange = (evtId) => {
        const numId = parseInt(evtId);
        setTriggerEventId(numId || '');
        const trigType = allTriggerTypes.find(t => t.eventId === numId);
        resetSubFormState();
        // If switching away from message trigger, reset message state
        if (trigType?.subjectType !== 'Message') {
            setSelectedFolder('');
            setSelectedMessageId('');
            setMessageType('email');
        }
        updateConfig({
            triggerEventId: numId || null,
            triggerEventName: trigType?.name || '',
            triggerSubjectType: trigType?.subjectType || '',
            timeBasedField: null, timeBasedFrequency: null, timeBasedNumberOf: null,
            timeBasedPeriod: null, timeBasedBeforeAfter: null, timeBasedTime: null,
            oneOffSendDateTime: null, messageLinkId: null, webFormId: null, webFormName: null, targetUrl: null,
        });
    };

    const handleMessageTypeChange = (newType) => {
        setMessageType(newType);
        setSelectedFolder('');
        setSelectedMessageId('');
        setMessageLinks([]);
        setSelectedLinkId('');
        updateConfig({ messageType: newType, messageFolderPath: '', messageId: null, messageName: null, messageLinkId: null });
    };

    const handleMessageFolderChange = (folderPath) => {
        setSelectedFolder(folderPath);
        setSelectedMessageId('');
        updateConfig({ messageFolderPath: folderPath, messageId: null, messageName: null });
    };

    const handleMessageChange = (msgId) => {
        const numId = parseInt(msgId);
        setSelectedMessageId(numId || '');
        const message = messageCatalog.find(m => m.voicebox_message_id === numId);
        setSelectedLinkId('');
        setMessageLinks([]);
        updateConfig({ messageId: numId || null, messageName: message?.message_name || '', messageLinkId: null });
    };

    const resetSubFormState = () => {
        setTbField(''); setTbFrequency('Once'); setTbNumberOf(''); setTbPeriod(''); setTbBeforeAfter(''); setTbTime('');
        setOosDateTime(''); setSelectedLinkId(''); setSelectedFormId(''); setTargetUrl('');
        setListFields([]); setWebForms([]); setMessageLinks([]);
    };

    const handleTimeBasedChange = (field, value) => {
        const setters = { field: setTbField, frequency: setTbFrequency, numberOf: setTbNumberOf, period: setTbPeriod, beforeAfter: setTbBeforeAfter, time: setTbTime };
        const configKeys = { field: 'timeBasedField', frequency: 'timeBasedFrequency', numberOf: 'timeBasedNumberOf', period: 'timeBasedPeriod', beforeAfter: 'timeBasedBeforeAfter', time: 'timeBasedTime' };
        if (setters[field]) setters[field](value);
        if (configKeys[field]) updateConfig({ [configKeys[field]]: value });
    };

    // ========== No data guard ==========
    if (messageCatalog.length === 0 && availableLists.length === 0) {
        return (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800 font-medium">No data available</p>
                <p className="text-xs text-yellow-700 mt-1">
                    Please select a client/group first. Messages and lists will be synced automatically from VoiceBox.
                </p>
            </div>
        );
    }

    // ========== Summary ==========
    const buildSummary = () => {
        if (!selectedTrigger) return null;
        let parts = [`Trigger: ${selectedTrigger.name}`];
        if (isMessageTrigger && config.messageName) {
            const typeLabel = messageType === 'rcs' ? 'RCS' : messageType === 'sms' ? 'SMS' : 'Email';
            parts.push(`${typeLabel} message: "${config.messageName}"`);
        }
        if (connectedListName) parts.push(`List: "${connectedListName}"`);
        return parts.join(' | ');
    };

    const selectClassName = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-teal-500 focus:border-teal-500";
    const labelClassName = "block text-sm font-medium text-gray-700 mb-1";

    return (
        <div className="space-y-4">
            {/* Connected list info */}
            {connectedListName && (
                <div className="p-2 bg-indigo-50 border border-indigo-200 rounded-md text-xs text-indigo-700">
                    Connected list: <strong>{connectedListName}</strong> (from List Selector node)
                </div>
            )}

            {/* ====== Trigger type dropdown ====== */}
            <div>
                <label className="text-sm font-semibold text-teal-800 mb-2 pb-1 border-b border-teal-200 block">
                    Which will be triggered by:
                </label>
                <SearchableSelect
                    value={triggerEventId}
                    onChange={(val) => handleTriggerEventChange(val)}
                    className="mt-2"
                    placeholder="-- Select A Trigger --"
                    options={[
                        ...VOICEBOX_TRIGGER_TYPES.list.map(t => ({ value: t.eventId, label: `[List] ${t.name}` })),
                        ...VOICEBOX_TRIGGER_TYPES.message.email.map(t => ({ value: t.eventId, label: `[Email] ${t.name}` })),
                        ...VOICEBOX_TRIGGER_TYPES.message.sms.map(t => ({ value: t.eventId, label: `[SMS] ${t.name}` })),
                        ...VOICEBOX_TRIGGER_TYPES.message.rcs.map(t => ({ value: t.eventId, label: `[RCS] ${t.name}` })),
                    ]}
                />
            </div>

            {/* ====== Message selection (for message-type triggers) ====== */}
            {isMessageTrigger && (
                <div className="border border-teal-200 rounded-md p-3 bg-teal-50 space-y-3">
                    <p className="text-xs font-medium text-teal-800">Select the message this trigger applies to:</p>
                    {/* Message Type */}
                    <div>
                        <label className={labelClassName}>Message Type</label>
                        <div className="flex items-center gap-4">
                            {['email', 'sms', 'rcs'].map(t => (
                                <label key={t} className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" name="triggerMessageType" value={t}
                                        checked={messageType === t}
                                        onChange={(e) => handleMessageTypeChange(e.target.value)}
                                        className="text-teal-600 focus:ring-teal-500" />
                                    <span className="text-sm text-gray-700">{t === 'rcs' ? 'RCS' : t === 'sms' ? 'SMS' : 'Email'}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    {/* Message Folder */}
                    <div>
                        <label className={labelClassName}>Message Folder</label>
                        <SearchableSelect
                            value={selectedFolder}
                            onChange={(val) => handleMessageFolderChange(val)}
                            placeholder="-- All Folders --"
                            options={[
                                { value: '', label: '-- All Folders --' },
                                ...messageFolders.map(f => ({ value: f, label: f }))
                            ]}
                        />
                    </div>
                    {/* Message */}
                    <div>
                        <label className={labelClassName}>Select Message</label>
                        <SearchableSelect
                            value={selectedMessageId}
                            onChange={(val) => handleMessageChange(val)}
                            placeholder="-- Select Message --"
                            options={filteredMessages.map(m => ({
                                value: m.voicebox_message_id,
                                label: m.message_name
                            }))}
                        />
                        {filteredMessages.length === 0 && messageCatalog.length > 0 && (
                            <p className="text-xs text-gray-500 mt-1">No {messageType} messages found{selectedFolder ? ` in folder "${selectedFolder}"` : ''}.</p>
                        )}
                    </div>
                </div>
            )}

            {/* ====== Trigger-specific sub-forms ====== */}
            {triggerForm && (
                <div className="border border-gray-200 rounded-md p-3 bg-gray-50 space-y-3">
                    {loadingSubForm && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Loader className="animate-spin" size={14} /> Loading additional options...
                        </div>
                    )}

                    {/* Time Based (eventId 1) */}
                    {triggerForm.type === 'timeBased' && (
                        <>
                            {!effectiveListId && (
                                <p className="text-xs text-amber-600">Connect a List Selector node to load datetime fields.</p>
                            )}
                            <div>
                                <label className={labelClassName}>Field (datetime)</label>
                                <SearchableSelect
                                    value={tbField}
                                    onChange={(val) => handleTimeBasedChange('field', val)}
                                    placeholder="-- Select Field --"
                                    options={listFields.map(f => ({
                                        value: f.columnName,
                                        label: f.displayName
                                    }))}
                                />
                                {!loadingSubForm && listFields.length === 0 && effectiveListId && (
                                    <p className="text-xs text-gray-500 mt-1">No datetime fields found for this list.</p>
                                )}
                            </div>
                            <div>
                                <label className={labelClassName}>Frequency</label>
                                <select value={tbFrequency} onChange={(e) => handleTimeBasedChange('frequency', e.target.value)} className={selectClassName}>
                                    <option value="Once">Once</option>
                                </select>
                            </div>
                            <p className="text-sm font-medium text-gray-700">Delivery Time</p>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className={labelClassName}>No. Of</label>
                                    <input type="number" min="0" value={tbNumberOf}
                                        onChange={(e) => handleTimeBasedChange('numberOf', e.target.value)}
                                        className={selectClassName} placeholder="0" />
                                </div>
                                <div>
                                    <label className={labelClassName}>Days/Weeks/Months</label>
                                    <select value={tbPeriod} onChange={(e) => handleTimeBasedChange('period', e.target.value)} className={selectClassName}>
                                        <option value="">-- Select --</option>
                                        <option value="Day(s)">Day(s)</option>
                                        <option value="Week(s)">Week(s)</option>
                                        <option value="Month(s)">Month(s)</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className={labelClassName}>Before/After</label>
                                    <select value={tbBeforeAfter} onChange={(e) => handleTimeBasedChange('beforeAfter', e.target.value)} className={selectClassName}>
                                        <option value="">-- Select --</option>
                                        <option value="Before Today">Before Today</option>
                                        <option value="After Today">After Today</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClassName}>at</label>
                                    <input type="time" value={tbTime}
                                        onChange={(e) => handleTimeBasedChange('time', e.target.value)}
                                        className={selectClassName} />
                                </div>
                            </div>
                        </>
                    )}


                    {/* Time Based One Off Send (eventId 18) */}
                    {triggerForm.type === 'timeBasedOneOff' && (
                        <div className="flex items-center gap-3">
                            <div className="text-sm font-medium text-gray-600">at</div>
                            <div className="flex-1">
                                <label className={labelClassName}>Date & Time</label>
                                <input type="datetime-local" value={oosDateTime}
                                    onChange={(e) => { setOosDateTime(e.target.value); updateConfig({ oneOffSendDateTime: e.target.value }); }}
                                    className={selectClassName} />
                            </div>
                        </div>
                    )}

                    {/* Link Click (eventId 5) */}
                    {triggerForm.type === 'linkClick' && (
                        <div>
                            <label className={labelClassName}>Select A Link</label>
                            <SearchableSelect
                                value={selectedLinkId}
                                onChange={(val) => { setSelectedLinkId(val); updateConfig({ messageLinkId: parseInt(val) || null }); }}
                                placeholder="-- Select Link --"
                                options={messageLinks.map(l => ({
                                    value: l.linkId,
                                    label: l.linkTitle
                                }))}
                            />
                            {!loadingSubForm && messageLinks.length === 0 && selectedMessageId && (
                                <p className="text-xs text-gray-500 mt-1">The selected message has no links.</p>
                            )}
                            {!selectedMessageId && (
                                <p className="text-xs text-amber-600 mt-1">Select a message above first to load links.</p>
                            )}
                        </div>
                    )}

                    {/* Web Form (eventId 12, 14, 16) */}
                    {triggerForm.type === 'webForm' && (
                        <div>
                            {!effectiveListId && (
                                <p className="text-xs text-amber-600 mb-2">Connect a List Selector node to load forms.</p>
                            )}
                            <label className={labelClassName}>Select A Form</label>
                            <SearchableSelect
                                value={selectedFormId}
                                onChange={(val) => {
                                    const fId = parseInt(val);
                                    setSelectedFormId(fId || '');
                                    const form = webForms.find(f => f.formId === fId);
                                    updateConfig({ webFormId: fId || null, webFormName: form?.formName || '' });
                                }}
                                placeholder="-- Select Form --"
                                options={webForms.map(f => ({
                                    value: f.formId,
                                    label: f.formName
                                }))}
                            />
                            {!loadingSubForm && webForms.length === 0 && effectiveListId && (
                                <p className="text-xs text-gray-500 mt-1">No forms found for this list.</p>
                            )}
                        </div>
                    )}

                    {/* URL Link Click (eventId 20) */}
                    {triggerForm.type === 'targetUrl' && (
                        <div>
                            <label className={labelClassName}>Target URL</label>
                            <input type="url" value={targetUrl} pattern="https://.*"
                                placeholder="https://example.com"
                                onChange={(e) => { setTargetUrl(e.target.value); updateConfig({ targetUrl: e.target.value }); }}
                                className={selectClassName} />
                            <p className="text-xs text-gray-500 mt-1">URL must start with https://</p>
                        </div>
                    )}
                </div>
            )}

            {/* Summary */}
            {buildSummary() && (
                <div className="p-3 bg-teal-50 border border-teal-200 rounded-md">
                    <div className="font-medium text-teal-900 text-sm mb-1">Trigger Summary</div>
                    <div className="text-xs text-teal-700">{buildSummary()}</div>
                </div>
            )}
        </div>
    );
};

// Send Via API — configure external API integration (equivalent to VoiceBox Send Via API)
const SendViaApiConfig = ({ config, setConfig, apiIntegrations = [], selectedGroupId, nodeId, nodes = [], edges = [] }) => {
    const [selectedApiName, setSelectedApiName] = useState(config.apiName || '');
    const [selectedLocation, setSelectedLocation] = useState(config.locationName || '');
    const [selectedEndpoint, setSelectedEndpoint] = useState(config.endpointName || '');
    const [fieldMappings, setFieldMappings] = useState(config.fieldMappings || []);
    const [listFields, setListFields] = useState([]);
    const [loadingFields, setLoadingFields] = useState(false);

    const sel = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500";

    // Find upstream List Selector (recursive graph traversal)
    const upstreamList = findUpstreamListSelector(nodeId, nodes, edges);

    // Load list fields when upstream list is found
    useEffect(() => {
        if (!upstreamList?.listId || !selectedGroupId) {
            setListFields([]);
            return;
        }
        const loadFields = async () => {
            setLoadingFields(true);
            try {
                const mappingResult = await apiClient.get('/voicebox/client-mappings');
                const mappings = mappingResult.mappings || [];
                const mapping = mappings.find(m => m.group_id === selectedGroupId);
                if (mapping) {
                    const res = await apiClient.get(`/voicebox/client-mappings/${mapping.id}/list-fields/${upstreamList.listId}`);
                    setListFields(res.fields || []);
                }
            } catch (err) {
                console.warn('Could not load list fields for Send Via API:', err.message);
                setListFields([]);
            } finally {
                setLoadingFields(false);
            }
        };
        loadFields();
    }, [upstreamList?.listId, selectedGroupId]);

    const selectedApi = apiIntegrations.find(a => a.name === selectedApiName);
    const availableLocations = selectedApi
        ? [...new Set((selectedApi.parameters || []).map(p => p.location_name).filter(Boolean))]
        : [];
    const availableEndpoints = selectedApi?.endpoints || [];

    const handleApiChange = (apiName) => {
        setSelectedApiName(apiName);
        setSelectedLocation('');
        setSelectedEndpoint('');
        setFieldMappings([]);
        const api = apiIntegrations.find(a => a.name === apiName);
        setConfig({
            ...config,
            apiName: apiName,
            apiDisplayName: api?.display_name || apiName,
            queueName: api?.queue_name || '',
            locationName: '',
            endpointName: '',
            endpointDisplayName: '',
            fieldMappings: []
        });
    };

    const handleLocationChange = (loc) => {
        setSelectedLocation(loc);
        setConfig({ ...config, locationName: loc });
    };

    const handleEndpointChange = (epName) => {
        setSelectedEndpoint(epName);
        const ep = availableEndpoints.find(e => e.name === epName);
        const epFields = (ep?.fields || []).map(f => ({
            ...f,
            value: ''
        }));
        setFieldMappings(epFields);
        setConfig({
            ...config,
            endpointName: epName,
            endpointDisplayName: ep?.display_name || epName,
            fieldMappings: epFields
        });
    };

    const handleFieldValueChange = (fieldIndex, value) => {
        const updated = [...fieldMappings];
        updated[fieldIndex] = { ...updated[fieldIndex], value };
        setFieldMappings(updated);
        setConfig({ ...config, fieldMappings: updated });
    };

    if (apiIntegrations.length === 0) {
        return (
            <div className="space-y-4">
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800 font-medium">No API integrations configured</p>
                    <p className="text-xs text-yellow-700 mt-1">
                        API integrations need to be configured for this client. You can enter values manually below.
                    </p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Name</label>
                    <input type="text" value={config.apiName || ''} placeholder="e.g., ImpelIntegration"
                        onChange={(e) => setConfig({ ...config, apiName: e.target.value, apiDisplayName: e.target.value })}
                        className={sel} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <input type="text" value={config.locationName || ''} placeholder="e.g., Main Branch"
                        onChange={(e) => setConfig({ ...config, locationName: e.target.value })}
                        className={sel} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Section / Endpoint</label>
                    <input type="text" value={config.endpointName || ''} placeholder="e.g., SendLead"
                        onChange={(e) => setConfig({ ...config, endpointName: e.target.value, endpointDisplayName: e.target.value })}
                        className={sel} />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select API</label>
                <SearchableSelect
                    value={selectedApiName}
                    onChange={(val) => handleApiChange(val)}
                    placeholder="-- Select API --"
                    options={apiIntegrations.map(api => ({
                        value: api.name,
                        label: api.display_name || api.name
                    }))}
                />
            </div>

            {selectedApiName && availableLocations.length > 0 && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Location</label>
                    <SearchableSelect
                        value={selectedLocation}
                        onChange={(val) => handleLocationChange(val)}
                        placeholder="-- Select Location --"
                        options={availableLocations.map(loc => ({
                            value: loc,
                            label: loc
                        }))}
                    />
                </div>
            )}

            {selectedApiName && availableEndpoints.length > 0 && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API Section</label>
                    <SearchableSelect
                        value={selectedEndpoint}
                        onChange={(val) => handleEndpointChange(val)}
                        placeholder="-- Select Section --"
                        options={availableEndpoints.map(ep => ({
                            value: ep.name,
                            label: ep.display_name || ep.name
                        }))}
                    />
                </div>
            )}

            {selectedEndpoint && fieldMappings.length > 0 && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Field Mappings</label>
                    {!upstreamList ? (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-800 font-medium">No list selected in preceding nodes</p>
                            <p className="text-xs text-red-700 mt-1">
                                Add a List Selector node before this node and select a list to map fields.
                            </p>
                        </div>
                    ) : loadingFields ? (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-500">
                            Loading list fields...
                        </div>
                    ) : (
                        <>
                            {upstreamList.listName && (
                                <div className="text-xs text-gray-500 mb-2">
                                    List: <strong>{upstreamList.listName}</strong> (ID: {upstreamList.listId})
                                </div>
                            )}
                            <div className="space-y-2 border border-gray-200 rounded-md p-3 bg-gray-50">
                                {fieldMappings.map((field, idx) => (
                                    <div key={field.id || idx} className="flex items-center gap-2">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-1">
                                                <span className="text-sm text-gray-700">{field.label || field.api_value}</span>
                                                {field.mandatory && <span className="text-red-500 text-xs">*</span>}
                                            </div>
                                            {field.notes && <div className="text-xs text-gray-500">{field.notes}</div>}
                                        </div>
                                        {listFields.length > 0 ? (
                                            <SearchableSelect
                                                value={field.value || ''}
                                                onChange={(val) => handleFieldValueChange(idx, val)}
                                                placeholder="-- Select field --"
                                                className="flex-1"
                                                options={listFields.map(lf => ({
                                                    value: lf.ColumnName || lf.columnName || lf.name,
                                                    label: lf.FriendlyName || lf.friendlyName || lf.displayName || lf.ColumnName || lf.columnName || lf.name
                                                }))}
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                value={field.value || ''}
                                                placeholder={field.api_value || ''}
                                                onChange={(e) => handleFieldValueChange(idx, e.target.value)}
                                                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {config.parameters && config.parameters.length > 0 && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">API Parameters</label>
                    <div className="space-y-2 border border-gray-200 rounded-md p-3 bg-gray-50">
                        {config.parameters.map((param, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                                <span className="text-sm text-gray-700 flex-1 font-medium">{param.name}</span>
                                <input
                                    type="text"
                                    value={param.value || ''}
                                    onChange={(e) => {
                                        const updated = [...config.parameters];
                                        updated[idx] = { ...updated[idx], value: e.target.value };
                                        setConfig({ ...config, parameters: updated });
                                    }}
                                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {config.apiName && (
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-md">
                    <div className="font-medium text-indigo-900 text-sm mb-1">API Configuration Summary</div>
                    <div className="text-xs text-indigo-700 space-y-0.5">
                        <div>API: <strong>{config.apiDisplayName || config.apiName}</strong></div>
                        {config.locationName && <div>Location: <strong>{config.locationName}</strong></div>}
                        {config.endpointDisplayName && <div>Section: <strong>{config.endpointDisplayName}</strong></div>}
                        {config.queueName && <div className="text-indigo-500">Queue: {config.queueName}</div>}
                    </div>
                </div>
            )}
        </div>
    );
};

// Filter Configuration Component with Field-Aware Builder
const FilterConfig = ({ 
    config, 
    setConfig, 
    queryMode, 
    setQueryMode, 
    naturalQuery, 
    setNaturalQuery, 
    processNaturalLanguageQuery, 
    isProcessing, 
    sqlError,
    availableLists,
    listsError,
    loadingLists,
    onRetryLists,
    selectedGroupId // Pass the selected group ID to get the correct mapping
}) => {
    // Sync filterMode with queryMode for backward compatibility
    const [filterMode, setFilterMode] = useState(
        config.filterMode || (queryMode === 'natural' ? 'natural' : queryMode === 'sql' ? 'sql' : 'visual')
    );
    const [selectedListIds, setSelectedListIds] = useState(
        config.listIdsForFilter || (config.listId ? [config.listId] : [])
    );

    // Auto-populate listIds from config if available (from previous list selector node)
    useEffect(() => {
        const listIds = config.listIdsForFilter || (config.listId ? [config.listId] : []);
        if (listIds.length > 0 && JSON.stringify(listIds.sort()) !== JSON.stringify(selectedListIds.sort())) {
            console.log('Filter node: Auto-populating list IDs from config:', listIds);
            setSelectedListIds(listIds);
        }
    }, [config.listIdsForFilter, config.listId]);
    
    const [listFields, setListFields] = useState([]);
    const [loadingFields, setLoadingFields] = useState(false);
    const [conditions, setConditions] = useState(config.conditions || []);
    const [logicalOperator, setLogicalOperator] = useState(config.logicalOperator || 'AND');
    const [mappingId, setMappingId] = useState(null);
    const [sqlParsedOnce, setSqlParsedOnce] = useState(false);

    // Parse imported SQL into visual conditions when conditions are empty but sqlFilter exists
    useEffect(() => {
        if (sqlParsedOnce || conditions.length > 0 || !config.sqlFilter || listFields.length === 0) return;
        setSqlParsedOnce(true);

        const sql = config.sqlFilter;
        const parsed = [];
        const opMatch = sql.match(/\s+(AND|OR)\s+/i);
        const detectedOp = opMatch ? opMatch[1].toUpperCase() : 'AND';

        const parts = sql.split(/\s+(?:AND|OR)\s+/i);
        for (const part of parts) {
            const t = part.trim();
            let m;
            m = t.match(/ISNULL\((?:c\.)?(\w+),\s*'[^']*'\)\s*(!=|<>|=|>=|<=|>|<|LIKE|NOT\s+LIKE)\s*'((?:[^']|'')*?)'/i);
            if (m) {
                const fi = listFields.find(f => f.ColumnName === m[1] || f.FieldName === m[1]);
                parsed.push({ field: fi ? String(fi.FieldID) : m[1], operator: m[2], value: m[3].replace(/''/g, "'"), fieldType: fi?.FieldType?.toLowerCase() || 'text' });
                continue;
            }
            m = t.match(/(?:c\.)?(\w+)\s*(!=|<>|=|>=|<=|>|<|LIKE|NOT\s+LIKE)\s*'((?:[^']|'')*?)'/i);
            if (m) {
                const fi = listFields.find(f => f.ColumnName === m[1] || f.FieldName === m[1]);
                parsed.push({ field: fi ? String(fi.FieldID) : m[1], operator: m[2], value: m[3].replace(/''/g, "'"), fieldType: fi?.FieldType?.toLowerCase() || 'text' });
                continue;
            }
            m = t.match(/(?:c\.)?(\w+)\s+IN\s*\(([^)]+)\)/i);
            if (m) {
                const fi = listFields.find(f => f.ColumnName === m[1] || f.FieldName === m[1]);
                parsed.push({ field: fi ? String(fi.FieldID) : m[1], operator: 'IN', value: m[2].trim(), fieldType: fi?.FieldType?.toLowerCase() || 'text' });
                continue;
            }
            m = t.match(/(?:c\.)?(\w+)\s*(!=|<>|=|>=|<=|>|<)\s*(\d+(?:\.\d+)?)/i);
            if (m) {
                const fi = listFields.find(f => f.ColumnName === m[1] || f.FieldName === m[1]);
                parsed.push({ field: fi ? String(fi.FieldID) : m[1], operator: m[2], value: m[3], fieldType: 'number' });
                continue;
            }
        }

        if (parsed.length > 0) {
            console.log('Filter: Parsed SQL into', parsed.length, 'visual conditions');
            setConditions(parsed);
            setLogicalOperator(detectedOp);
            setConfig(prev => ({ ...prev, conditions: parsed, logicalOperator: detectedOp }));
        }
    }, [listFields, config.sqlFilter]);

    // Get mapping ID from selected group
    useEffect(() => {
        const getMappingId = async () => {
            try {
                const result = await apiClient.get('/voicebox/client-mappings');
                const mappings = result.mappings || [];
                
                // If we have a selected group ID, find the mapping for that group
                // Otherwise, use the first mapping
                let mapping = null;
                if (selectedGroupId) {
                    // Find mapping that matches the group
                    // Note: This assumes the mapping has a group_id or similar field
                    // Adjust based on your actual data structure
                    mapping = mappings.find(m => m.group_id === selectedGroupId) || mappings[0];
                } else {
                    mapping = mappings[0];
                }
                
                if (mapping) {
                    console.log('Filter node: Using mapping ID:', mapping.id, 'for group:', selectedGroupId);
                    setMappingId(mapping.id);
                }
            } catch (error) {
                console.error('Failed to get mapping ID:', error);
            }
        };
        getMappingId();
    }, [selectedGroupId]);

    // Load fields when lists are selected - automatically trigger when list IDs change
    // Load fields regardless of filter mode so they're available when user switches to visual mode
    useEffect(() => {
        if (selectedListIds.length > 0 && mappingId) {
            console.log('Filter node: Loading fields for lists:', selectedListIds, 'using mapping:', mappingId);
            loadListFields(selectedListIds);
        } else if (selectedListIds.length > 0 && !mappingId) {
            console.log('Filter node: Waiting for mapping ID to load fields');
        } else if (selectedListIds.length === 0) {
            // Clear fields if no lists selected
            setListFields([]);
        }
    }, [selectedListIds, mappingId]);

    const loadListFields = async (listIds) => {
        if (!mappingId || listIds.length === 0) return;
        
        setLoadingFields(true);
        try {
            const result = await apiClient.get(`/voicebox/client-mappings/${mappingId}/list-fields?list_ids=${listIds.join(',')}`);
            setListFields(result.fields || []);
        } catch (error) {
            console.error('Failed to load list fields:', error);
            setListFields([]);
        } finally {
            setLoadingFields(false);
        }
    };

    const addCondition = () => {
        setConditions([...conditions, {
            field: '',
            operator: '=',
            value: '',
            fieldType: 'text'
        }]);
    };

    const updateCondition = (index, field, value) => {
        const updated = [...conditions];
        updated[index] = { ...updated[index], [field]: value };
        setConditions(updated);
        
        // Update config immediately to persist the change
        setConfig({ 
            ...config, 
            conditions: updated,
            logicalOperator: logicalOperator
        });
        
        // Generate SQL from conditions
        generateSqlFromConditions(updated);
    };

    const removeCondition = (index) => {
        const updated = conditions.filter((_, i) => i !== index);
        setConditions(updated);
        generateSqlFromConditions(updated);
    };

    const generateSqlFromConditions = (conds) => {
        const validConditions = conds.filter(c => c.field && c.value !== '');
        if (validConditions.length === 0) {
            setConfig(prev => ({ 
                ...prev, 
                sqlFilter: '', 
                conditions: conds, 
                logicalOperator,
                listIdsForFilter: selectedListIds
            }));
            return;
        }

        // Get field column names from listFields
        const sqlParts = validConditions.map(cond => {
            const fieldInfo = listFields.find(f => 
                String(f.FieldID) === String(cond.field) || 
                f.DisplayName === cond.field ||
                f.FieldName === cond.field
            );
            
            const columnName = fieldInfo?.ColumnName || cond.field;
            const operator = cond.operator || '=';
            let value = cond.value;

            // Format value based on field type
            if (fieldInfo?.FieldType === 'Date' || fieldInfo?.FieldType === 'DateTime') {
                if (operator === '>=' || operator === '<=' || operator === '>' || operator === '<') {
                    value = `'${value}'`;
                } else {
                    value = `'${value}'`;
                }
            } else if (fieldInfo?.FieldType === 'Number' || fieldInfo?.FieldType === 'Integer') {
                // Keep as-is for numbers
            } else {
                // String values
                value = `'${value.replace(/'/g, "''")}'`;
            }

            return `c.${columnName} ${operator} ${value}`;
        });

        const sql = sqlParts.join(` ${logicalOperator} `);
        setConfig(prev => ({ 
            ...prev, 
            sqlFilter: sql,
            conditions: conds,
            logicalOperator,
            listIdsForFilter: selectedListIds
        }));
    };

    useEffect(() => {
        if (filterMode === 'visual' && conditions.length > 0) {
            generateSqlFromConditions(conditions);
        }
    }, [logicalOperator]);

    // Group fields by list for display
    const fieldsByList = {};
    listFields.forEach(field => {
        if (!fieldsByList[field.ListID]) {
            fieldsByList[field.ListID] = [];
        }
        fieldsByList[field.ListID].push(field);
    });

    return (
        <div>
            <div className="flex gap-2 mb-4">
                <button
                    type="button"
                    onClick={() => {
                        setFilterMode('visual');
                        setQueryMode('visual');
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${
                        filterMode === 'visual'
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    <Filter size={16} />
                    Visual Builder
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setFilterMode('natural');
                        setQueryMode('natural');
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${
                        filterMode === 'natural'
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    <Brain size={16} />
                    Natural Language
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setFilterMode('sql');
                        setQueryMode('sql');
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${
                        filterMode === 'sql'
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    <Code size={16} />
                    SQL Query
                </button>
            </div>

            {filterMode === 'visual' ? (
                <div className="space-y-4">
                    {/* List Selection for Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Lists to Filter {selectedListIds.length > 0 && '(Auto-selected from List Selector node)'}
                        </label>
                        {selectedListIds.length > 0 ? (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                <div className="text-sm text-blue-900 mb-2">
                                    Using lists from previous node:
                                </div>
                                <div className="space-y-1">
                                    {selectedListIds.map(listId => {
                                        const list = availableLists.find(l => l.listId === listId);
                                        return list ? (
                                            <div key={listId} className="text-sm text-blue-700 flex items-center gap-2">
                                                <CheckCircle size={14} />
                                                {list.listName}
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedListIds([]);
                                        setConfig({ ...config, listIdsForFilter: [], listId: null });
                                    }}
                                    className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                                >
                                    Change lists
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                                    {availableLists.length > 0 ? (
                                        availableLists.map((list) => {
                                            const isSelected = selectedListIds.includes(list.listId);
                                            return (
                                                <label
                                                    key={list.listId}
                                                    className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                const newIds = [...selectedListIds, list.listId];
                                                                setSelectedListIds(newIds);
                                                                setConfig({ ...config, listIdsForFilter: newIds });
                                                            } else {
                                                                const newIds = selectedListIds.filter(id => id !== list.listId);
                                                                setSelectedListIds(newIds);
                                                                setConfig({ ...config, listIdsForFilter: newIds, listId: newIds.length === 1 ? newIds[0] : null });
                                                            }
                                                        }}
                                                    />
                                                    <span className="text-sm">{list.listName}</span>
                                                </label>
                                            );
                                        })
                                    ) : (
                                        <div className="text-sm text-gray-500">
                                            {loadingLists ? (
                                                <div className="flex items-center gap-2">
                                                    <Loader className="animate-spin" size={14} />
                                                    Loading lists from VoiceBox...
                                                </div>
                                            ) : listsError ? (
                                                <div>
                                                    <p className="text-red-600 mb-1 text-xs">{listsError}</p>
                                                    {onRetryLists && (
                                                        <button
                                                            onClick={onRetryLists}
                                                            className="text-xs text-blue-600 underline hover:text-blue-800"
                                                        >
                                                            Retry
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-xs">No lists available. Lists are fetched automatically from VoiceBox when a client is selected.</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Tip: Connect a List Selector or Filter node before this filter to auto-select lists
                                </p>
                            </>
                        )}
                    </div>

                    {/* Conditions Builder */}
                    {selectedListIds.length > 0 && (
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Filter Conditions
                                </label>
                                <button
                                    type="button"
                                    onClick={addCondition}
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                >
                                    <Plus size={14} />
                                    Add Condition
                                </button>
                            </div>

                            {conditions.length === 0 ? (
                                <div className="p-4 border border-dashed border-gray-300 rounded-md text-center text-sm text-gray-500">
                                    No conditions added. Click "Add Condition" to start building your filter.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {conditions.map((condition, index) => (
                                        <div key={index} className="border rounded-md p-3 bg-gray-50">
                                            <div className="grid grid-cols-12 gap-2 items-end">
                                                <div className="col-span-4">
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Field
                                                    </label>
                                                    <SearchableSelect
                                                        value={condition.field || ''}
                                                        onChange={(selectedValue) => {
                                                            if (!selectedValue) {
                                                                updateCondition(index, 'field', '');
                                                                return;
                                                            }
                                                            
                                                            const fieldInfo = listFields.find(f => 
                                                                String(f.FieldID) === String(selectedValue) ||
                                                                f.DisplayName === selectedValue ||
                                                                f.FieldName === selectedValue
                                                            );
                                                            
                                                            const updated = [...conditions];
                                                            updated[index] = { 
                                                                ...updated[index], 
                                                                field: fieldInfo ? String(fieldInfo.FieldID || fieldInfo.FieldName) : selectedValue,
                                                                fieldType: fieldInfo ? (fieldInfo.FieldType || 'text') : updated[index].fieldType
                                                            };
                                                            setConditions(updated);
                                                            
                                                            setConfig(prev => ({ 
                                                                ...prev, 
                                                                conditions: updated,
                                                                logicalOperator: logicalOperator,
                                                                listIdsForFilter: selectedListIds
                                                            }));
                                                            
                                                            generateSqlFromConditions(updated);
                                                        }}
                                                        placeholder="Select field..."
                                                        options={Object.entries(fieldsByList).flatMap(([listId, fields]) =>
                                                            fields.map(field => ({
                                                                value: String(field.FieldID || field.FieldName),
                                                                label: `${field.DisplayName || field.FieldName} (${field.ColumnName})`
                                                            }))
                                                        )}
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Operator
                                                    </label>
                                                    <select
                                                        value={condition.operator}
                                                        onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                                    >
                                                        <option value="=">=</option>
                                                        <option value="!=">≠</option>
                                                        <option value=">">&gt;</option>
                                                        <option value="<">&lt;</option>
                                                        <option value=">=">≥</option>
                                                        <option value="<=">≤</option>
                                                        <option value="LIKE">Contains</option>
                                                        <option value="NOT LIKE">Not Contains</option>
                                                        <option value="IN">In</option>
                                                        <option value="NOT IN">Not In</option>
                                                    </select>
                                                </div>
                                                <div className="col-span-5">
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Value
                                                    </label>
                                                    <input
                                                        type={condition.fieldType === 'Date' || condition.fieldType === 'DateTime' ? 'date' : 'text'}
                                                        value={condition.value}
                                                        onChange={(e) => updateCondition(index, 'value', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                                        placeholder="Enter value..."
                                                    />
                                                </div>
                                                <div className="col-span-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => removeCondition(index)}
                                                        className="w-full p-1 text-red-600 hover:text-red-800"
                                                        title="Remove condition"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {conditions.length > 1 && (
                                <div className="mt-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Logical Operator
                                    </label>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setLogicalOperator('AND')}
                                            className={`px-4 py-2 rounded-md text-sm font-medium ${
                                                logicalOperator === 'AND'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                        >
                                            AND (All conditions must match)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setLogicalOperator('OR')}
                                            className={`px-4 py-2 rounded-md text-sm font-medium ${
                                                logicalOperator === 'OR'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                        >
                                            OR (Any condition can match)
                                        </button>
                                    </div>
                                </div>
                            )}

                            {config.sqlFilter && (
                                <div className="mt-3">
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Generated SQL:
                                    </label>
                                    <div className="p-2 bg-gray-50 border rounded text-xs font-mono">
                                        {config.sqlFilter}
                                    </div>
                                </div>
                            )}

                            {loadingFields && (
                                <div className="text-sm text-gray-500 mt-2">
                                    Loading fields...
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : filterMode === 'natural' ? (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Describe your filter in plain English
                    </label>
                    <textarea
                        value={naturalQuery}
                        onChange={(e) => setNaturalQuery(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        rows={3}
                        placeholder="e.g., Show contacts where the vehicle model is Toyota and the purchase date is in the last 30 days"
                    />
                    <button
                        type="button"
                        onClick={processNaturalLanguageQuery}
                        disabled={isProcessing || !naturalQuery.trim()}
                        className="mt-2 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                    >
                        {isProcessing ? (
                            <>
                                <Loader className="animate-spin" size={16} />
                                Processing...
                            </>
                        ) : (
                            <>
                                <Brain size={16} />
                                Generate SQL
                            </>
                        )}
                    </button>
                    {sqlError && (
                        <div className="mt-2 p-2 bg-red-50 text-red-800 rounded text-sm">
                            {sqlError}
                        </div>
                    )}
                    {config.sqlFilter && (
                        <div className="mt-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Generated SQL:
                            </label>
                            <div className="p-2 bg-gray-50 border rounded text-xs font-mono">
                                {config.sqlFilter}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        SQL Filter
                    </label>
                    <textarea
                        value={config.sqlFilter || ''}
                        onChange={(e) => setConfig({ ...config, sqlFilter: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        rows={4}
                        placeholder="WHERE clause SQL"
                    />
                </div>
            )}
        </div>
    );
};

// Condition Configuration Component with Field-Aware Builder
const ConditionConfig = ({ 
    config, 
    setConfig, 
    queryMode, 
    setQueryMode, 
    naturalQuery, 
    setNaturalQuery, 
    processNaturalLanguageQuery, 
    isProcessing, 
    sqlError,
    availableLists,
    listsError,
    loadingLists,
    onRetryLists,
    selectedGroupId // Pass the selected group ID to get the correct mapping
}) => {
    // Sync conditionMode with queryMode for backward compatibility
    const [conditionMode, setConditionMode] = useState(
        config.conditionMode || (queryMode === 'natural' ? 'natural' : queryMode === 'sql' ? 'sql' : 'visual')
    );
    const [selectedListIds, setSelectedListIds] = useState(
        config.listIdsForCondition || (config.listId ? [config.listId] : [])
    );

    // Auto-populate listIds from config if available (from previous list selector node)
    useEffect(() => {
        const listIds = config.listIdsForCondition || (config.listId ? [config.listId] : []);
        if (listIds.length > 0 && JSON.stringify(listIds.sort()) !== JSON.stringify(selectedListIds.sort())) {
            console.log('Condition node: Auto-populating list IDs from config:', listIds);
            setSelectedListIds(listIds);
        }
    }, [config.listIdsForCondition, config.listId]);
    
    const [listFields, setListFields] = useState([]);
    const [loadingFields, setLoadingFields] = useState(false);
    const [conditions, setConditions] = useState(config.conditions || []);
    const [logicalOperator, setLogicalOperator] = useState(config.logicalOperator || 'AND');
    const [mappingId, setMappingId] = useState(null);

    // Get mapping ID from selected group
    useEffect(() => {
        const getMappingId = async () => {
            try {
                const result = await apiClient.get('/voicebox/client-mappings');
                const mappings = result.mappings || [];
                
                // If we have a selected group ID, find the mapping for that group
                // Otherwise, use the first mapping
                let mapping = null;
                if (selectedGroupId) {
                    // Find mapping that matches the group
                    // Note: This assumes the mapping has a group_id or similar field
                    // Adjust based on your actual data structure
                    mapping = mappings.find(m => m.group_id === selectedGroupId) || mappings[0];
                } else {
                    mapping = mappings[0];
                }
                
                if (mapping) {
                    console.log('Condition node: Using mapping ID:', mapping.id, 'for group:', selectedGroupId);
                    setMappingId(mapping.id);
                }
            } catch (error) {
                console.error('Failed to get mapping ID:', error);
            }
        };
        getMappingId();
    }, [selectedGroupId]);

    // Load fields when lists are selected - automatically trigger when list IDs change
    // Load fields regardless of condition mode so they're available when user switches to visual mode
    useEffect(() => {
        if (selectedListIds.length > 0 && mappingId) {
            console.log('Condition node: Loading fields for lists:', selectedListIds, 'using mapping:', mappingId);
            loadListFields(selectedListIds);
        } else if (selectedListIds.length > 0 && !mappingId) {
            console.log('Condition node: Waiting for mapping ID to load fields');
        } else if (selectedListIds.length === 0) {
            // Clear fields if no lists selected
            console.log('Condition node: No list IDs selected, clearing fields');
            setListFields([]);
        }
    }, [selectedListIds, mappingId]);

    const loadListFields = async (listIds) => {
        if (!mappingId || listIds.length === 0) {
            console.log('Condition node: Cannot load fields - mappingId:', mappingId, 'listIds:', listIds);
            return;
        }
        
        console.log('Condition node: Loading fields for lists:', listIds, 'using mapping:', mappingId);
        setLoadingFields(true);
        try {
            const url = `/voicebox/client-mappings/${mappingId}/list-fields?list_ids=${listIds.join(',')}`;
            console.log('Condition node: Fetching fields from:', url);
            const result = await apiClient.get(url);
            
            const fields = result.fields || [];
            console.log('Condition node: Response data structure:', {
                hasFields: !!result.fields,
                fieldsLength: fields.length,
                fieldsByList: result.fieldsByList ? Object.keys(result.fieldsByList).length : 0,
                listIds: result.listIds
            });
            console.log('Condition node: Loaded', fields.length, 'fields:', fields.slice(0, 5).map(f => ({ 
                FieldID: f.FieldID, 
                DisplayName: f.DisplayName, 
                FieldName: f.FieldName,
                ColumnName: f.ColumnName,
                ListID: f.ListID
            })));
            
            if (fields.length === 0) {
                console.warn('Condition node: No fields returned from API. Full response:', result);
            } else {
                console.log('Condition node: Setting listFields state with', fields.length, 'fields');
                // Log first field structure to verify format
                if (fields[0]) {
                    console.log('Condition node: First field structure:', {
                        FieldID: fields[0].FieldID,
                        FieldName: fields[0].FieldName,
                        DisplayName: fields[0].DisplayName,
                        ColumnName: fields[0].ColumnName,
                        ListID: fields[0].ListID,
                        listId: fields[0].listId,
                        allKeys: Object.keys(fields[0])
                    });
                }
            }
            
            setListFields(fields);
        } catch (error) {
            console.error('Condition node: Failed to load list fields:', error);
            console.error('Condition node: Error stack:', error.stack);
            setListFields([]);
        } finally {
            setLoadingFields(false);
        }
    };

    const addCondition = () => {
        setConditions([...conditions, {
            field: '',
            operator: '=',
            value: '',
            fieldType: 'text'
        }]);
    };

    const updateCondition = (index, field, value) => {
        const updated = [...conditions];
        updated[index] = { ...updated[index], [field]: value };
        setConditions(updated);
        
        // Update config immediately to persist the change
        setConfig({ 
            ...config, 
            conditions: updated,
            logicalOperator: logicalOperator
        });
        
        // Generate SQL from conditions
        generateSqlFromConditions(updated);
    };

    const removeCondition = (index) => {
        const updated = conditions.filter((_, i) => i !== index);
        setConditions(updated);
        generateSqlFromConditions(updated);
    };

    const generateSqlFromConditions = (conds) => {
        const validConditions = conds.filter(c => c.field && c.value !== '');
        if (validConditions.length === 0) {
            setConfig(prev => ({ 
                ...prev, 
                condition: '', 
                conditions: conds, 
                logicalOperator,
                listIdsForCondition: selectedListIds
            }));
            return;
        }

        // Get field column names from listFields
        const sqlParts = validConditions.map(cond => {
            const fieldInfo = listFields.find(f => 
                String(f.FieldID) === String(cond.field) || 
                f.DisplayName === cond.field ||
                f.FieldName === cond.field
            );
            
            const columnName = fieldInfo?.ColumnName || cond.field;
            const operator = cond.operator || '=';
            let value = cond.value;

            // Format value based on field type
            if (fieldInfo?.FieldType === 'Date' || fieldInfo?.FieldType === 'DateTime') {
                if (operator === '>=' || operator === '<=' || operator === '>' || operator === '<') {
                    value = `'${value}'`;
                } else {
                    value = `'${value}'`;
                }
            } else if (fieldInfo?.FieldType === 'Number' || fieldInfo?.FieldType === 'Integer') {
                // Keep as-is for numbers
            } else {
                // String values
                value = `'${value.replace(/'/g, "''")}'`;
            }

            return `c.${columnName} ${operator} ${value}`;
        });

        const sql = sqlParts.join(` ${logicalOperator} `);
        setConfig(prev => ({ 
            ...prev, 
            condition: sql,
            conditions: conds,
            logicalOperator,
            listIdsForCondition: selectedListIds
        }));
    };

    // Condition Groups Support - allows (A AND B) OR (C AND D) type logic
    const [conditionGroups, setConditionGroups] = useState(
        config.conditionGroups || (config.conditions && config.conditions.length > 0 
            ? [{
                id: 'group-1',
                conditions: config.conditions,
                logicalOperator: config.logicalOperator || 'AND'
            }]
            : [{
                id: 'group-1',
                conditions: [],
                logicalOperator: 'AND'
            }])
    );
    const [groupOperator, setGroupOperator] = useState(config.groupOperator || 'AND');

    const addConditionGroup = () => {
        const newGroup = {
            id: `group-${Date.now()}`,
            conditions: [],
            logicalOperator: 'AND'
        };
        setConditionGroups([...conditionGroups, newGroup]);
    };

    const removeConditionGroup = (groupId) => {
        if (conditionGroups.length > 1) {
            setConditionGroups(conditionGroups.filter(g => g.id !== groupId));
        }
    };

    const addConditionToGroup = (groupId) => {
        const updated = conditionGroups.map(group => {
            if (group.id === groupId) {
                return {
                    ...group,
                    conditions: [...group.conditions, {
                        field: '',
                        operator: '=',
                        value: '',
                        fieldType: 'text'
                    }]
                };
            }
            return group;
        });
        setConditionGroups(updated);
        generateSqlFromConditionGroups(updated);
    };

    const updateConditionInGroup = (groupId, conditionIndex, field, value) => {
        const updated = conditionGroups.map(group => {
            if (group.id === groupId) {
                const updatedConditions = [...group.conditions];
                updatedConditions[conditionIndex] = { ...updatedConditions[conditionIndex], [field]: value };
                return { ...group, conditions: updatedConditions };
            }
            return group;
        });
        setConditionGroups(updated);
        generateSqlFromConditionGroups(updated);
    };

    const removeConditionFromGroup = (groupId, conditionIndex) => {
        const updated = conditionGroups.map(group => {
            if (group.id === groupId) {
                return {
                    ...group,
                    conditions: group.conditions.filter((_, i) => i !== conditionIndex)
                };
            }
            return group;
        });
        setConditionGroups(updated);
        generateSqlFromConditionGroups(updated);
    };

    const updateGroupOperator = (groupId, operator) => {
        const updated = conditionGroups.map(group => {
            if (group.id === groupId) {
                return { ...group, logicalOperator: operator };
            }
            return group;
        });
        setConditionGroups(updated);
        generateSqlFromConditionGroups(updated);
    };

    const generateSqlFromConditionGroups = (groups) => {
        const groupSqlParts = groups.map(group => {
            const validConditions = group.conditions.filter(c => c.field && c.value !== '');
            if (validConditions.length === 0) return null;

            const sqlParts = validConditions.map(cond => {
                const fieldInfo = listFields.find(f => 
                    String(f.FieldID) === String(cond.field) || 
                    f.DisplayName === cond.field ||
                    f.FieldName === cond.field
                );
                
                const columnName = fieldInfo?.ColumnName || cond.field;
                const operator = cond.operator || '=';
                let value = cond.value;

                if (fieldInfo?.FieldType === 'Date' || fieldInfo?.FieldType === 'DateTime') {
                    value = `'${value}'`;
                } else if (fieldInfo?.FieldType === 'Number' || fieldInfo?.FieldType === 'Integer') {
                    // Keep as-is for numbers
                } else {
                    value = `'${value.replace(/'/g, "''")}'`;
                }

                return `c.${columnName} ${operator} ${value}`;
            });

            if (sqlParts.length === 0) return null;
            if (sqlParts.length === 1) return sqlParts[0];
            return `(${sqlParts.join(` ${group.logicalOperator} `)})`;
        }).filter(Boolean);

        if (groupSqlParts.length === 0) {
            setConfig(prev => ({ 
                ...prev, 
                condition: '', 
                conditionGroups: groups,
                groupOperator,
                listIdsForCondition: selectedListIds
            }));
            return;
        }

        const finalSql = groupSqlParts.length === 1 
            ? groupSqlParts[0]
            : groupSqlParts.join(` ${groupOperator} `);

        setConfig(prev => ({ 
            ...prev, 
            condition: finalSql,
            conditionGroups: groups,
            groupOperator,
            listIdsForCondition: selectedListIds
        }));
    };

    useEffect(() => {
        if (conditionMode === 'visual' && conditionGroups.length > 0) {
            generateSqlFromConditionGroups(conditionGroups);
        }
    }, [groupOperator]);

    useEffect(() => {
        if (conditionMode === 'visual' && conditions.length > 0) {
            generateSqlFromConditions(conditions);
        }
    }, [logicalOperator]);

    // Group fields by list for display
    const fieldsByList = {};
    console.log('Condition node: Grouping', listFields.length, 'fields. listFields state:', listFields);
    
    listFields.forEach((field, index) => {
        // Handle both ListID and listId (case variations)
        const listId = field.ListID || field.listId || field.ListId;
        if (listId) {
            if (!fieldsByList[listId]) {
                fieldsByList[listId] = [];
            }
            fieldsByList[listId].push(field);
        } else {
            console.warn(`Condition node: Field ${index} missing ListID:`, field);
            // Fallback: use first selected list ID if available
            if (selectedListIds.length > 0) {
                const fallbackListId = selectedListIds[0];
                if (!fieldsByList[fallbackListId]) {
                    fieldsByList[fallbackListId] = [];
                }
                fieldsByList[fallbackListId].push(field);
            }
        }
    });
    
    // Debug: Log fieldsByList structure
    if (listFields.length > 0) {
        const groupedCount = Object.values(fieldsByList).reduce((sum, fields) => sum + fields.length, 0);
        console.log('Condition node: Fields grouped by list:', {
            totalFields: listFields.length,
            groupedFields: groupedCount,
            groups: Object.keys(fieldsByList).map(listId => ({
                listId,
                fieldCount: fieldsByList[listId].length,
                sampleFields: fieldsByList[listId].slice(0, 3).map(f => ({
                    FieldID: f.FieldID,
                    DisplayName: f.DisplayName || f.FieldName,
                    ColumnName: f.ColumnName
                }))
            }))
        });
    } else if (selectedListIds.length > 0 && !loadingFields) {
        console.warn('Condition node: listFields is empty but lists are selected. listFields:', listFields);
    }

    return (
        <div>
            <div className="flex gap-2 mb-4">
                <button
                    type="button"
                    onClick={() => {
                        setConditionMode('visual');
                        setQueryMode('visual');
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${
                        conditionMode === 'visual'
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    <Filter size={16} />
                    Visual Builder
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setConditionMode('natural');
                        setQueryMode('natural');
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${
                        conditionMode === 'natural'
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    <Brain size={16} />
                    Natural Language
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setConditionMode('sql');
                        setQueryMode('sql');
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${
                        conditionMode === 'sql'
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    <Code size={16} />
                    SQL Query
                </button>
            </div>

            {conditionMode === 'visual' ? (
                <div className="space-y-4">
                    {/* List Selection for Condition */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Lists to Check {selectedListIds.length > 0 && '(Auto-selected from List Selector node)'}
                        </label>
                        {selectedListIds.length > 0 ? (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                <div className="text-sm text-blue-900 mb-2">
                                    Using lists from previous node:
                                </div>
                                <div className="space-y-1">
                                    {selectedListIds.map(listId => {
                                        const list = availableLists.find(l => l.listId === listId);
                                        return list ? (
                                            <div key={listId} className="text-sm text-blue-700 flex items-center gap-2">
                                                <CheckCircle size={14} />
                                                {list.listName}
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedListIds([]);
                                        setConfig({ ...config, listIdsForCondition: [], listId: null });
                                    }}
                                    className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                                >
                                    Change lists
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                                    {availableLists.length > 0 ? (
                                        availableLists.map((list) => {
                                            const isSelected = selectedListIds.includes(list.listId);
                                            return (
                                                <label
                                                    key={list.listId}
                                                    className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                const newIds = [...selectedListIds, list.listId];
                                                                setSelectedListIds(newIds);
                                                                setConfig({ ...config, listIdsForCondition: newIds });
                                                            } else {
                                                                const newIds = selectedListIds.filter(id => id !== list.listId);
                                                                setSelectedListIds(newIds);
                                                                setConfig({ ...config, listIdsForCondition: newIds, listId: newIds.length === 1 ? newIds[0] : null });
                                                            }
                                                        }}
                                                    />
                                                    <span className="text-sm">{list.listName}</span>
                                                </label>
                                            );
                                        })
                                    ) : (
                                        <div className="text-sm text-gray-500">
                                            {loadingLists ? (
                                                <div className="flex items-center gap-2">
                                                    <Loader className="animate-spin" size={14} />
                                                    Loading lists from VoiceBox...
                                                </div>
                                            ) : listsError ? (
                                                <div>
                                                    <p className="text-red-600 mb-1 text-xs">{listsError}</p>
                                                    {onRetryLists && (
                                                        <button
                                                            onClick={onRetryLists}
                                                            className="text-xs text-blue-600 underline hover:text-blue-800"
                                                        >
                                                            Retry
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-xs">No lists available. Lists are fetched automatically from VoiceBox when a client is selected.</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Tip: Connect a List Selector or Filter node before this condition to auto-select lists
                                </p>
                            </>
                        )}
                    </div>

                    {/* Conditions Builder */}
                    {selectedListIds.length > 0 && (
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Condition Criteria
                                </label>
                                <button
                                    type="button"
                                    onClick={addCondition}
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                >
                                    <Plus size={14} />
                                    Add Condition
                                </button>
                            </div>

                            {/* Condition Groups - New grouped conditions feature */}
                            {conditionGroups.some(g => g.conditions.length > 0) ? (
                                <div className="space-y-4 mb-4">
                                    {conditionGroups.map((group, groupIndex) => (
                                        <div key={group.id} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                                            <div className="flex items-center justify-between mb-3">
                                                <h4 className="text-sm font-semibold text-gray-700">
                                                    Group {groupIndex + 1}
                                                </h4>
                                                {conditionGroups.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeConditionGroup(group.id)}
                                                        className="text-red-600 hover:text-red-800 text-xs"
                                                    >
                                                        Remove Group
                                                    </button>
                                                )}
                                            </div>

                                            {group.conditions.map((condition, condIndex) => (
                                                <div key={condIndex} className="mb-3 grid grid-cols-12 gap-2 items-end">
                                                    <div className="col-span-4">
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">Field</label>
                                                        <SearchableSelect
                                                            value={condition.field || ''}
                                                            onChange={(val) => {
                                                                const fieldInfo = listFields.find(f => String(f.FieldID) === String(val));
                                                                updateConditionInGroup(group.id, condIndex, 'field', val);
                                                                if (fieldInfo) {
                                                                    updateConditionInGroup(group.id, condIndex, 'fieldType', fieldInfo.FieldType || 'text');
                                                                }
                                                            }}
                                                            placeholder="Select field..."
                                                            options={Object.keys(fieldsByList).length > 0
                                                                ? Object.entries(fieldsByList).flatMap(([listId, fields]) =>
                                                                    fields.map(field => ({
                                                                        value: String(field.FieldID),
                                                                        label: field.DisplayName || field.FieldName
                                                                    }))
                                                                )
                                                                : listFields.map(field => ({
                                                                    value: String(field.FieldID),
                                                                    label: field.DisplayName || field.FieldName
                                                                }))
                                                            }
                                                        />
                                                    </div>
                                                    <div className="col-span-2">
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">Operator</label>
                                                        <select
                                                            value={condition.operator}
                                                            onChange={(e) => updateConditionInGroup(group.id, condIndex, 'operator', e.target.value)}
                                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                                        >
                                                            <option value="=">=</option>
                                                            <option value="!=">≠</option>
                                                            <option value=">">&gt;</option>
                                                            <option value="<">&lt;</option>
                                                            <option value=">=">≥</option>
                                                            <option value="<=">≤</option>
                                                            <option value="LIKE">Contains</option>
                                                            <option value="NOT LIKE">Not Contains</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-span-5">
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">Value</label>
                                                        <input
                                                            type={condition.fieldType === 'Date' ? 'date' : 'text'}
                                                            value={condition.value}
                                                            onChange={(e) => updateConditionInGroup(group.id, condIndex, 'value', e.target.value)}
                                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                                        />
                                                    </div>
                                                    <div className="col-span-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => removeConditionFromGroup(group.id, condIndex)}
                                                            className="w-full p-1 text-red-600 hover:text-red-800"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}

                                            <div className="flex items-center justify-between mt-3">
                                                <button
                                                    type="button"
                                                    onClick={() => addConditionToGroup(group.id)}
                                                    className="text-sm text-blue-600 hover:text-blue-800"
                                                >
                                                    + Add Condition
                                                </button>
                                                {group.conditions.length > 1 && (
                                                    <div className="flex gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => updateGroupOperator(group.id, 'AND')}
                                                            className={`px-3 py-1 rounded text-xs ${group.logicalOperator === 'AND' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                                        >
                                                            AND
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => updateGroupOperator(group.id, 'OR')}
                                                            className={`px-3 py-1 rounded text-xs ${group.logicalOperator === 'OR' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                                        >
                                                            OR
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                    <div className="flex items-center justify-between pt-2">
                                        <button
                                            type="button"
                                            onClick={addConditionGroup}
                                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                        >
                                            + Add Condition Group
                                        </button>
                                        {conditionGroups.length > 1 && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-gray-700">Between groups:</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setGroupOperator('AND')}
                                                    className={`px-3 py-1 rounded text-xs ${groupOperator === 'AND' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                                >
                                                    AND
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setGroupOperator('OR')}
                                                    className={`px-3 py-1 rounded text-xs ${groupOperator === 'OR' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                                                >
                                                    OR
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : conditions.length === 0 ? (
                                <div className="p-4 border border-dashed border-gray-300 rounded-md text-center text-sm text-gray-500 mb-4">
                                    No conditions added. Click "Add Condition" or "Add Condition Group" to start.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {conditions.map((condition, index) => (
                                        <div key={index} className="border rounded-md p-3 bg-gray-50">
                                            <div className="grid grid-cols-12 gap-2 items-end">
                                                <div className="col-span-4">
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Field
                                                        {loadingFields && <span className="ml-2 text-xs text-blue-600">(Loading...)</span>}
                                                        {!loadingFields && listFields.length > 0 && <span className="ml-2 text-xs text-green-600">({listFields.length} available)</span>}
                                                    </label>
                                                    <SearchableSelect
                                                        value={condition.field || ''}
                                                        onChange={(selectedValue) => {
                                                            if (!selectedValue) {
                                                                updateCondition(index, 'field', '');
                                                                return;
                                                            }
                                                            
                                                            const fieldInfo = listFields.find(f => 
                                                                String(f.FieldID) === String(selectedValue) ||
                                                                String(f.FieldName) === String(selectedValue) ||
                                                                f.DisplayName === selectedValue
                                                            );
                                                            
                                                            const updated = [...conditions];
                                                            updated[index] = { 
                                                                ...updated[index], 
                                                                field: fieldInfo ? String(fieldInfo.FieldID || fieldInfo.FieldName) : selectedValue,
                                                                fieldType: fieldInfo ? (fieldInfo.FieldType || 'text') : updated[index].fieldType
                                                            };
                                                            setConditions(updated);
                                                            
                                                            setConfig(prev => ({ 
                                                                ...prev, 
                                                                conditions: updated,
                                                                logicalOperator: logicalOperator,
                                                                listIdsForCondition: selectedListIds
                                                            }));
                                                            
                                                            generateSqlFromConditions(updated);
                                                        }}
                                                        disabled={loadingFields || (selectedListIds.length > 0 && listFields.length === 0)}
                                                        placeholder={loadingFields 
                                                            ? 'Loading fields...' 
                                                            : selectedListIds.length === 0
                                                                ? 'Select lists first'
                                                                : listFields.length === 0
                                                                    ? 'No fields found'
                                                                    : 'Select field...'}
                                                        options={(Object.entries(fieldsByList).length > 0
                                                            ? Object.entries(fieldsByList).flatMap(([listId, fields]) =>
                                                                fields.map(field => ({
                                                                    value: String(field.FieldID || field.FieldName),
                                                                    label: `${field.DisplayName || field.FieldName || 'Unknown Field'}${field.ColumnName ? ` (${field.ColumnName})` : ''}`
                                                                }))
                                                            )
                                                            : listFields.map(field => ({
                                                                value: String(field.FieldID || field.FieldName),
                                                                label: `${field.DisplayName || field.FieldName || 'Unknown Field'}${field.ColumnName ? ` (${field.ColumnName})` : ''}`
                                                            }))
                                                        )}
                                                    />
                                                    {selectedListIds.length > 0 && listFields.length === 0 && !loadingFields && (
                                                        <p className="text-xs text-red-600 mt-1">
                                                            No fields loaded. Check console for details. mappingId={mappingId ? 'set' : 'null'}, listIds={selectedListIds.join(',')}
                                                        </p>
                                                    )}
                                                    {listFields.length > 0 && Object.entries(fieldsByList).length === 0 && (
                                                        <p className="text-xs text-yellow-600 mt-1">
                                                            Fields loaded ({listFields.length}) but couldn't be grouped. Check console for field structure.
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Operator
                                                    </label>
                                                    <select
                                                        value={condition.operator}
                                                        onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                                    >
                                                        <option value="=">=</option>
                                                        <option value="!=">≠</option>
                                                        <option value=">">&gt;</option>
                                                        <option value="<">&lt;</option>
                                                        <option value=">=">≥</option>
                                                        <option value="<=">≤</option>
                                                        <option value="LIKE">Contains</option>
                                                        <option value="NOT LIKE">Not Contains</option>
                                                        <option value="IN">In</option>
                                                        <option value="NOT IN">Not In</option>
                                                    </select>
                                                </div>
                                                <div className="col-span-5">
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Value
                                                    </label>
                                                    <input
                                                        type={condition.fieldType === 'Date' || condition.fieldType === 'DateTime' ? 'date' : 'text'}
                                                        value={condition.value}
                                                        onChange={(e) => updateCondition(index, 'value', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                                        placeholder="Enter value..."
                                                    />
                                                </div>
                                                <div className="col-span-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => removeCondition(index)}
                                                        className="w-full p-1 text-red-600 hover:text-red-800"
                                                        title="Remove condition"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {conditions.length > 1 && (
                                <div className="mt-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Logical Operator
                                    </label>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setLogicalOperator('AND')}
                                            className={`px-4 py-2 rounded-md text-sm font-medium ${
                                                logicalOperator === 'AND'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                        >
                                            AND (All conditions must match)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setLogicalOperator('OR')}
                                            className={`px-4 py-2 rounded-md text-sm font-medium ${
                                                logicalOperator === 'OR'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                        >
                                            OR (Any condition can match)
                                        </button>
                                    </div>
                                </div>
                            )}

                            {config.condition && (
                                <div className="mt-3">
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Generated SQL:
                                    </label>
                                    <div className="p-2 bg-gray-50 border rounded text-xs font-mono">
                                        {config.condition}
                                    </div>
                                </div>
                            )}

                            {loadingFields && (
                                <div className="text-sm text-gray-500 mt-2">
                                    Loading fields...
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : conditionMode === 'natural' ? (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Describe your condition in plain English
                    </label>
                    <textarea
                        value={naturalQuery}
                        onChange={(e) => setNaturalQuery(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        rows={3}
                        placeholder="e.g., Contact age is greater than 25 and has purchased a vehicle in the last year"
                    />
                    <button
                        type="button"
                        onClick={processNaturalLanguageQuery}
                        disabled={isProcessing || !naturalQuery.trim()}
                        className="mt-2 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                    >
                        {isProcessing ? (
                            <>
                                <Loader className="animate-spin" size={16} />
                                Processing...
                            </>
                        ) : (
                            <>
                                <Brain size={16} />
                                Generate SQL
                            </>
                        )}
                    </button>
                    {sqlError && (
                        <div className="mt-2 p-2 bg-red-50 text-red-800 rounded text-sm">
                            {sqlError}
                        </div>
                    )}
                    {config.condition && (
                        <div className="mt-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Generated SQL:
                            </label>
                            <div className="p-2 bg-gray-50 border rounded text-xs font-mono">
                                {config.condition}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Condition Expression
                    </label>
                    <textarea
                        value={config.condition || ''}
                        onChange={(e) => setConfig({ ...config, condition: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        rows={3}
                        placeholder="SQL condition or expression"
                    />
                </div>
            )}
        </div>
    );
};

// Helper function to extract list ID from activationSourceId
const extractListIdFromActivationSource = (activationSourceId) => {
    if (!activationSourceId) return null;
    
    // Pattern: ListField_{ListId}_{FieldId}
    const listFieldMatch = activationSourceId.match(/ListField_(\d+)_\d+/);
    if (listFieldMatch) {
        return parseInt(listFieldMatch[1]);
    }
    
    // Pattern: List_{ListId}
    const listMatch = activationSourceId.match(/List_(\d+)/);
    if (listMatch) {
        return parseInt(listMatch[1]);
    }
    
    return null;
};

// Helper function to get list name from list ID
const getListName = (listId, availableLists) => {
    if (!listId || !availableLists) return null;
    const list = availableLists.find(l => l.listId === listId);
    return list ? list.listName : null;
};

// Helper function to get selected list IDs from preceding nodes
const getSelectedListIdsFromPrecedingNodes = (node, nodes, edges) => {
    const selectedListIds = new Set();
    
    // Find edges that connect to this node
    const incomingEdges = edges.filter(e => e.target === node.id);
    
    for (const edge of incomingEdges) {
        const sourceNode = nodes.find(n => n.id === edge.source);
        if (sourceNode && sourceNode.data) {
            // Check for listIds (array) from listSelector
            if (sourceNode.data.listIds && Array.isArray(sourceNode.data.listIds)) {
                sourceNode.data.listIds.forEach(id => selectedListIds.add(id));
            }
            // Check for listIdsForFilter (from another filter)
            if (sourceNode.data.listIdsForFilter && Array.isArray(sourceNode.data.listIdsForFilter)) {
                sourceNode.data.listIdsForFilter.forEach(id => selectedListIds.add(id));
            }
            // Check for listIdsForCondition (from another condition)
            if (sourceNode.data.listIdsForCondition && Array.isArray(sourceNode.data.listIdsForCondition)) {
                sourceNode.data.listIdsForCondition.forEach(id => selectedListIds.add(id));
            }
            // Check for listId (single value, backward compatibility)
            if (sourceNode.data.listId) {
                selectedListIds.add(sourceNode.data.listId);
            }
        }
    }
    
    return Array.from(selectedListIds);
};

/**
 * Recursively traverse the workflow graph backwards to find the nearest
 * List Selector node upstream of the given node.
 * Returns { listId, listName, listIds } or null if none found.
 */
const findUpstreamListSelector = (nodeId, nodes, edges, visited = new Set()) => {
    if (!nodeId || visited.has(nodeId)) return null;
    visited.add(nodeId);

    const incomingEdges = edges.filter(e => e.target === nodeId);
    for (const edge of incomingEdges) {
        const sourceNode = nodes.find(n => n.id === edge.source);
        if (!sourceNode) continue;

        // Direct List Selector found
        if (sourceNode.type === 'listSelector' && (sourceNode.data?.listId || sourceNode.data?.listIds?.length)) {
            return {
                listId: sourceNode.data.listId || sourceNode.data.listIds?.[0],
                listName: sourceNode.data.listName || null,
                listIds: sourceNode.data.listIds || (sourceNode.data.listId ? [sourceNode.data.listId] : [])
            };
        }

        // Check if any other upstream node inherited list data
        if (sourceNode.data?.listId || sourceNode.data?.listIds?.length || sourceNode.data?.listIdsForFilter?.length) {
            return {
                listId: sourceNode.data.listId || sourceNode.data.listIds?.[0] || sourceNode.data.listIdsForFilter?.[0],
                listName: sourceNode.data.listName || null,
                listIds: sourceNode.data.listIds || sourceNode.data.listIdsForFilter || (sourceNode.data.listId ? [sourceNode.data.listId] : [])
            };
        }

        // Recurse further up the graph
        const upstream = findUpstreamListSelector(edge.source, nodes, edges, visited);
        if (upstream) return upstream;
    }
    return null;
};

// Node Configuration Modal Component
const NodeConfigModal = ({ node, nodes = [], edges = [], onSave, onConfigChange, onDelete, onClose, messageCatalog = [], triggerCatalog = [], availableLists = [], listsError = null, loadingLists = false, onRetryLists = null, selectedGroupId = null, onSyncMessages = null, syncingMessages = false, syncMessagesStatus = null, workflow = null, messageFolders = [], listFolders = [], apiIntegrations = [], readOnly = false }) => {
    const [config, setConfig] = useState(node.data || {});
    const [setNodes] = useState(null); // Will be set from parent if available
    const [queryMode, setQueryMode] = useState('sql'); // 'sql' or 'natural'
    const [naturalQuery, setNaturalQuery] = useState(config.naturalQuery || '');
    const [isProcessing, setIsProcessing] = useState(false);
    const [sqlError, setSqlError] = useState('');

    useEffect(() => {
        if (onConfigChange) onConfigChange(config);
    }, [config]);

    // Find previous node with list information if this is a filter or condition node
    useEffect(() => {
        if (node.type === 'filter' || node.type === 'condition') {
            // Find edges that connect to this node
            const incomingEdges = edges.filter(e => e.target === node.id);
            if (incomingEdges.length > 0) {
                // Find the source node of the first incoming edge
                const sourceNodeId = incomingEdges[0].source;
                const sourceNode = nodes.find(n => n.id === sourceNodeId);
                
                if (sourceNode && sourceNode.data) {
                    // Check for list information in the source node
                    // Could be from: listSelector, filter, or any node that has list data
                    let listIds = [];
                    
                    // Check for listIds (array) - from listSelector node
                    if (sourceNode.data.listIds && Array.isArray(sourceNode.data.listIds)) {
                        listIds = sourceNode.data.listIds;
                    }
                    // Check for listIdsForFilter (from another filter)
                    else if (sourceNode.data.listIdsForFilter && Array.isArray(sourceNode.data.listIdsForFilter)) {
                        listIds = sourceNode.data.listIdsForFilter;
                    }
                    // Check for listIdsForCondition (from another condition)
                    else if (sourceNode.data.listIdsForCondition && Array.isArray(sourceNode.data.listIdsForCondition)) {
                        listIds = sourceNode.data.listIdsForCondition;
                    }
                    // Check for single listId
                    else if (sourceNode.data.listId) {
                        listIds = [sourceNode.data.listId];
                    }
                    
                    // Always update if we found list IDs from a connected node, even if config already has some
                    // This ensures the filter gets the latest list selection from the list selector
                    if (listIds.length > 0) {
                        const currentListIds = node.type === 'filter' 
                            ? (config.listIdsForFilter || (config.listId ? [config.listId] : []))
                            : (config.listIdsForCondition || (config.listId ? [config.listId] : []));
                        
                        // Only update if the list IDs are different
                        if (JSON.stringify(listIds.sort()) !== JSON.stringify(currentListIds.sort())) {
                            setConfig(prev => {
                                const configUpdate = {
                                    ...prev,
                                    listId: listIds.length === 1 ? listIds[0] : null,
                                    // Also inherit table names if available
                                    tableNames: sourceNode.data.tableNames || (sourceNode.data.tableName ? [sourceNode.data.tableName] : []),
                                    tableName: sourceNode.data.tableName || (listIds.length === 1 ? `lst.Contacts_${listIds[0]}` : null)
                                };
                                
                                // Set the appropriate listIds property based on node type
                                if (node.type === 'filter') {
                                    configUpdate.listIdsForFilter = listIds;
                                } else if (node.type === 'condition') {
                                    configUpdate.listIdsForCondition = listIds;
                                }
                                
                                return configUpdate;
                            });
                        }
                    }
                }
            }
        }
    }, [node.id, node.type, edges, nodes, config.listIdsForFilter, config.listIdsForCondition, config.listId]);

    // Check if node has SQL fields that support natural language
    const supportsNaturalLanguage = node.type === 'filter' || node.type === 'condition';

    const processNaturalLanguageQuery = async () => {
        if (!naturalQuery.trim()) {
            setSqlError('Please enter a query');
            return;
        }

        setIsProcessing(true);
        setSqlError('');

        try {
            // Get the workflow's database config - for now use a default or get from workflow
            // In a real scenario, you'd get this from the workflow's group's database config
            const response = await fetch('/api/natural-language/convert', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query: naturalQuery,
                    configId: 'onboarding_db' // This should come from workflow's database config
                })
            });

            const result = await response.json();

            if (response.ok && result.success) {
                // Extract WHERE clause from SQL if it's a full query
                let sql = result.sql;
                if (sql.toLowerCase().includes('where')) {
                    sql = sql.substring(sql.toLowerCase().indexOf('where') + 5).trim();
                }
                
                if (node.type === 'filter') {
                    setConfig({ ...config, sqlFilter: sql, naturalQuery: naturalQuery });
                } else if (node.type === 'condition') {
                    setConfig({ ...config, condition: sql, naturalQuery: naturalQuery });
                }
                setSqlError('');
            } else {
                setSqlError(result.error || 'Failed to convert natural language to SQL');
            }
        } catch (error) {
            console.error('Error processing natural language query:', error);
            setSqlError('Network error: Could not process natural language query');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSave = () => {
        onSave(config);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900">
                        Configure {({
                            trigger: 'Trigger',
                            trigger_manual: 'Manual Start',
                            trigger_schedule: 'Schedule',
                            trigger_webhook: 'Webhook',
                            sendViaApi: 'Send Via API',
                            filter: 'Filter',
                            message: 'Message',
                            delay: 'Delay',
                            condition: 'Condition',
                            action: 'Action',
                            listSelector: 'List Selector',
                        })[node.type] || node.type.charAt(0).toUpperCase() + node.type.slice(1)} Node
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {/* Common fields */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Label *
                        </label>
                        <input
                            type="text"
                            value={config.label || ''}
                            onChange={(e) => setConfig({ ...config, label: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            placeholder="Node label"
                        />
                    </div>

                    {/* Type-specific fields */}
                    {node.type === 'trigger' && (
                        <div className="space-y-6">
                            {/* VoiceBox Trigger Types — "Which will be triggered by" */}
                            <TriggerMessageConfig
                                config={config}
                                setConfig={setConfig}
                                messageCatalog={messageCatalog}
                                availableLists={availableLists}
                                messageFolders={messageFolders}
                                listFolders={listFolders}
                                selectedGroupId={selectedGroupId}
                                nodes={nodes}
                                edges={edges}
                                nodeId={node.id}
                            />

                        </div>
                    )}

                    {/* Manual Start Trigger Configuration */}
                    {node.type === 'trigger_manual' && (
                        <div className="space-y-4">
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                <p className="text-sm text-blue-800">
                                    <strong>Manual Start Trigger:</strong> This workflow can be executed on-demand by users or administrators.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description (Optional)
                                </label>
                                <textarea
                                    value={config.description || ''}
                                    onChange={(e) => setConfig({ ...config, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                    placeholder="Describe when this workflow should be manually triggered..."
                                    rows={3}
                                />
                            </div>
                            {availableLists.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Optional: Start from List
                                    </label>
                                    <SearchableSelect
                                        value={config.listId || ''}
                                        onChange={(val) => setConfig({ ...config, listId: val || null })}
                                        placeholder="No specific list (all contacts)"
                                        options={[
                                            { value: '', label: 'No specific list (all contacts)' },
                                            ...availableLists.map(list => ({
                                                value: list.listId,
                                                label: list.listName
                                            }))
                                        ]}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Schedule Trigger Configuration */}
                    {node.type === 'trigger_schedule' && (
                        <div className="space-y-4">
                            <div className="p-3 bg-purple-50 border border-purple-200 rounded-md">
                                <p className="text-sm text-purple-800">
                                    <strong>Schedule Trigger:</strong> This workflow will execute automatically on a schedule defined by a cron expression.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Cron Expression *
                                </label>
                                <input
                                    type="text"
                                    value={config.cronExpression || ''}
                                    onChange={(e) => setConfig({ ...config, cronExpression: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 font-mono"
                                    placeholder="0 9 * * * (Daily at 9:00 AM)"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Format: minute hour day month weekday. Example: "0 9 * * *" = Daily at 9:00 AM
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Schedule Description
                                </label>
                                <input
                                    type="text"
                                    value={config.scheduleDescription || ''}
                                    onChange={(e) => setConfig({ ...config, scheduleDescription: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., Daily at 9:00 AM"
                                />
                            </div>
                            {availableLists.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Optional: Start from List
                                    </label>
                                    <SearchableSelect
                                        value={config.listId || ''}
                                        onChange={(val) => setConfig({ ...config, listId: val || null })}
                                        placeholder="No specific list (all contacts)"
                                        options={[
                                            { value: '', label: 'No specific list (all contacts)' },
                                            ...availableLists.map(list => ({
                                                value: list.listId,
                                                label: list.listName
                                            }))
                                        ]}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Webhook Trigger Configuration */}
                    {node.type === 'trigger_webhook' && (
                        <div className="space-y-4">
                            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                                <p className="text-sm text-green-800">
                                    <strong>Webhook Trigger:</strong> This workflow can be triggered via HTTP webhook from VoiceBox Lambda functions or external systems.
                                </p>
                            </div>
                            
                            {/* Webhook GUID - Auto-generated */}
                            {(() => {
                                const webhookGuid = config.webhookGuid || node.data?.webhookGuid || null;
                                const workflowStatus = workflow?.status || 'draft';
                                const isActive = workflowStatus === 'active';
                                const environment = isActive ? 'live' : 'test';
                                const baseUrl = window.location.origin;
                                const testUrl = webhookGuid ? `${baseUrl}/api/workflows/webhook/test/${webhookGuid}` : '';
                                const liveUrl = webhookGuid ? `${baseUrl}/api/workflows/webhook/live/${webhookGuid}` : '';
                                
                                return (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Webhook GUID
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    value={webhookGuid || 'Not generated yet - save workflow to generate'}
                                                    readOnly
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-mono text-sm"
                                                />
                                                {webhookGuid && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(webhookGuid);
                                                            alert('Webhook GUID copied to clipboard!');
                                                        }}
                                                        className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
                                                    >
                                                        Copy GUID
                                                    </button>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Unique identifier for this webhook. Generated automatically when you save the workflow.
                                            </p>
                                        </div>
                                        
                                        {/* Test URL */}
                                        {testUrl && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Test Webhook URL
                                                    <span className="ml-2 text-xs font-normal text-gray-500">(for draft/paused workflows)</span>
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="text"
                                                        value={testUrl}
                                                        readOnly
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-yellow-50 font-mono text-sm"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(testUrl);
                                                            alert('Test webhook URL copied to clipboard!');
                                                        }}
                                                        className="px-3 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm"
                                                    >
                                                        Copy
                                                    </button>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Use this URL when the workflow is in draft or paused status.
                                                </p>
                                            </div>
                                        )}
                                        
                                        {/* Live URL */}
                                        {liveUrl && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Live Webhook URL
                                                    <span className="ml-2 text-xs font-normal text-gray-500">(for active workflows)</span>
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="text"
                                                        value={liveUrl}
                                                        readOnly
                                                        className={`flex-1 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm ${
                                                            isActive ? 'bg-green-50' : 'bg-gray-50'
                                                        }`}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(liveUrl);
                                                            alert('Live webhook URL copied to clipboard!');
                                                        }}
                                                        className={`px-3 py-2 text-white rounded-md text-sm ${
                                                            isActive ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'
                                                        }`}
                                                    >
                                                        Copy
                                                    </button>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {isActive 
                                                        ? 'Use this URL when the workflow is active. This is the production endpoint.'
                                                        : 'This URL will be active when the workflow status is set to "active".'}
                                                </p>
                                            </div>
                                        )}
                                        
                                        {!webhookGuid && (
                                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                                <p className="text-xs text-blue-800">
                                                    <strong>Note:</strong> The webhook GUID will be automatically generated when you save this workflow. 
                                                    After saving, you'll see both test and live URLs here.
                                                </p>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description (Optional)
                                </label>
                                <textarea
                                    value={config.description || ''}
                                    onChange={(e) => setConfig({ ...config, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                    placeholder="Describe what triggers this webhook..."
                                    rows={3}
                                />
                            </div>
                            {availableLists.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Optional: Start from List
                                    </label>
                                    <SearchableSelect
                                        value={config.listId || ''}
                                        onChange={(val) => setConfig({ ...config, listId: val || null })}
                                        placeholder="No specific list (all contacts)"
                                        options={[
                                            { value: '', label: 'No specific list (all contacts)' },
                                            ...availableLists.map(list => ({
                                                value: list.listId,
                                                label: list.listName
                                            }))
                                        ]}
                                    />
                                </div>
                            )}
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-xs text-yellow-800">
                                    <strong>Note:</strong> Webhook payload can include parameters that will be used in the workflow SQL. 
                                    Parameters should be passed as JSON in the request body: {"{"}"parameters": {"{"}"fieldName": "value"{"}"}{"}"}
                                </p>
                            </div>
                        </div>
                    )}

                    {node.type === 'filter' && (
                        <FilterConfig
                            config={config}
                            setConfig={setConfig}
                            queryMode={queryMode}
                            setQueryMode={setQueryMode}
                            naturalQuery={naturalQuery}
                            setNaturalQuery={setNaturalQuery}
                            processNaturalLanguageQuery={processNaturalLanguageQuery}
                            isProcessing={isProcessing}
                            sqlError={sqlError}
                            availableLists={availableLists}
                            listsError={listsError}
                            loadingLists={loadingLists}
                            onRetryLists={onRetryLists}
                            selectedGroupId={selectedGroupId}
                        />
                    )}

                    {node.type === 'message' && (
                        <MessageConfig
                            config={config}
                            setConfig={setConfig}
                            messageCatalog={messageCatalog}
                            onSyncMessages={onSyncMessages}
                            syncingMessages={syncingMessages}
                            syncMessagesStatus={syncMessagesStatus}
                            selectedGroupId={selectedGroupId}
                        />
                    )}

                    {node.type === 'delay' && (
                        <>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Delay Amount
                                    </label>
                                    <input
                                        type="number"
                                        value={config.delayAmount || ''}
                                        onChange={(e) => setConfig({ ...config, delayAmount: parseInt(e.target.value) })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        placeholder="1"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Delay Unit
                                    </label>
                                    <select
                                        value={config.delayUnit || 'days'}
                                        onChange={(e) => {
                                            const newUnit = e.target.value;
                                            const updated = { ...config, delayUnit: newUnit };
                                            if (newUnit === 'minutes' || newUnit === 'hours') {
                                                delete updated.delayTime;
                                            }
                                            setConfig(updated);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="minutes">Minutes</option>
                                        <option value="hours">Hours</option>
                                        <option value="days">Days</option>
                                        <option value="weeks">Weeks</option>
                                    </select>
                                </div>
                            </div>
                            {(config.delayUnit === 'days' || config.delayUnit === 'weeks') && (
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Delivery Time <span className="text-gray-400 font-normal">(optional — send at a specific time of day)</span>
                                    </label>
                                    <input
                                        type="time"
                                        value={config.delayTime || ''}
                                        onChange={(e) => setConfig({ ...config, delayTime: e.target.value })}
                                        className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                    />
                                    {config.delayTime && (
                                        <button
                                            type="button"
                                            onClick={() => { const c = { ...config }; delete c.delayTime; setConfig(c); }}
                                            className="ml-2 text-xs text-red-500 hover:text-red-700"
                                        >
                                            Clear
                                        </button>
                                    )}
                                    <p className="text-xs text-gray-500 mt-1">
                                        If set, the message will be sent at this time after the delay period
                                    </p>
                                </div>
                            )}
                        </>
                    )}

                    {node.type === 'condition' && (
                        <ConditionConfig
                            config={config}
                            setConfig={setConfig}
                            queryMode={queryMode}
                            setQueryMode={setQueryMode}
                            naturalQuery={naturalQuery}
                            setNaturalQuery={setNaturalQuery}
                            processNaturalLanguageQuery={processNaturalLanguageQuery}
                            isProcessing={isProcessing}
                            sqlError={sqlError}
                            availableLists={availableLists}
                            listsError={listsError}
                            loadingLists={loadingLists}
                            onRetryLists={onRetryLists}
                            selectedGroupId={selectedGroupId}
                        />
                    )}

                    {node.type === 'action' && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Action Type
                                </label>
                                <select
                                    value={config.actionType || 'UpdateContact'}
                                    onChange={(e) => {
                                        const newActionType = e.target.value;
                                        // Reset action-specific config when changing type
                                        const baseConfig = {
                                            ...config,
                                            actionType: newActionType
                                        };
                                        // Clear action-specific fields
                                        delete baseConfig.updateFields;
                                        delete baseConfig.notificationType;
                                        delete baseConfig.notificationRecipients;
                                        delete baseConfig.notificationMessage;
                                        delete baseConfig.eventName;
                                        delete baseConfig.eventData;
                                        setConfig(baseConfig);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="UpdateContact">Update Contact</option>
                                    <option value="NotifyContacts">Notify Contacts</option>
                                    <option value="LogEvent">Log Event</option>
                                </select>
                            </div>

                            {/* Update Contact Configuration */}
                            {(!config.actionType || config.actionType === 'UpdateContact') && (
                                <UpdateContactConfig
                                    config={config}
                                    setConfig={setConfig}
                                    selectedGroupId={selectedGroupId}
                                    nodeId={node.id}
                                    nodes={nodes}
                                    edges={edges}
                                />
                            )}

                            {/* Notify Contacts Configuration */}
                            {config.actionType === 'NotifyContacts' && (
                                <div className="space-y-3 p-4 bg-green-50 border border-green-200 rounded-md">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Notification Settings</h4>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Notification Type
                                        </label>
                                        <select
                                            value={config.notificationType || 'email'}
                                            onChange={(e) => setConfig({ ...config, notificationType: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="email">Email</option>
                                            <option value="sms">SMS</option>
                                            <option value="internal">Internal Notification</option>
                                            <option value="webhook">Webhook</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Recipients
                                        </label>
                                        <input
                                            type="text"
                                            value={config.notificationRecipients || ''}
                                            onChange={(e) => setConfig({ ...config, notificationRecipients: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                            placeholder="Email addresses, phone numbers, or contact IDs (comma-separated)"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Enter recipients separated by commas. Use contact field names (e.g., Email, Phone) for dynamic values.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Notification Message/Subject
                                        </label>
                                        <textarea
                                            value={config.notificationMessage || ''}
                                            onChange={(e) => setConfig({ ...config, notificationMessage: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                            rows={3}
                                            placeholder="Enter notification message or email subject"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Notification Body/Content (Optional)
                                        </label>
                                        <textarea
                                            value={config.notificationBody || ''}
                                            onChange={(e) => setConfig({ ...config, notificationBody: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                            rows={4}
                                            placeholder="Enter detailed notification content (for email body, etc.)"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Log Event Configuration */}
                            {config.actionType === 'LogEvent' && (
                                <div className="space-y-3 p-4 bg-purple-50 border border-purple-200 rounded-md">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Event Logging Settings</h4>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Event Name
                                        </label>
                                        <input
                                            type="text"
                                            value={config.eventName || ''}
                                            onChange={(e) => setConfig({ ...config, eventName: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                            placeholder="e.g., ContactQualified, WorkflowStepCompleted"
                                            required
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            A unique identifier for this event type
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Event Data (JSON format)
                                        </label>
                                        <textarea
                                            value={config.eventData ? JSON.stringify(config.eventData, null, 2) : ''}
                                            onChange={(e) => {
                                                try {
                                                    const parsed = e.target.value ? JSON.parse(e.target.value) : {};
                                                    setConfig({ ...config, eventData: parsed });
                                                } catch (error) {
                                                    // Invalid JSON, but keep the text for user to fix
                                                    setConfig({ ...config, eventData: e.target.value });
                                                }
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                            rows={6}
                                            placeholder='{"step": "qualification", "value": "high", "timestamp": "auto"}'
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Additional data to log with the event. Use JSON format.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Log Level
                                        </label>
                                        <select
                                            value={config.logLevel || 'info'}
                                            onChange={(e) => setConfig({ ...config, logLevel: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="info">Info</option>
                                            <option value="warning">Warning</option>
                                            <option value="error">Error</option>
                                            <option value="debug">Debug</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {node.type === 'listSelector' && (
                        <ListSelectorConfig
                            config={config}
                            setConfig={setConfig}
                            availableLists={availableLists}
                            listsError={listsError}
                            loadingLists={loadingLists}
                            onRetryLists={onRetryLists}
                            selectedGroupId={selectedGroupId}
                        />
                    )}

                    {node.type === 'sendViaApi' && (
                        <SendViaApiConfig
                            config={config}
                            setConfig={setConfig}
                            apiIntegrations={apiIntegrations}
                            selectedGroupId={selectedGroupId}
                            nodeId={node.id}
                            nodes={nodes}
                            edges={edges}
                        />
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                        </label>
                        <textarea
                            value={config.description || ''}
                            onChange={(e) => setConfig({ ...config, description: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            rows={2}
                            placeholder="Optional description"
                        />
                    </div>
                </div>

                <div className="p-6 border-t flex justify-between">
                    {!readOnly ? (
                        <button
                            onClick={onDelete}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                            <Trash2 size={18} />
                            Delete Node
                        </button>
                    ) : <div />}
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            {readOnly ? 'Close' : 'Cancel'}
                        </button>
                        {!readOnly && (
                            <button
                                onClick={handleSave}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                <CheckCircle size={18} />
                                Save
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// SQL Preview Modal Component
const SqlPreviewModal = ({ sqlData, onClose, onExecute, executing = false }) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                        <Code size={24} />
                        Generated SQL
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {/* Validation Status */}
                    {sqlData.isValid ? (
                        <div className="flex items-center gap-2 p-3 bg-green-50 text-green-800 rounded-md">
                            <CheckCircle2 size={20} />
                            <span>SQL is valid and ready to use</span>
                        </div>
                    ) : (
                        <div className="p-3 bg-red-50 text-red-800 rounded-md">
                            <div className="flex items-center gap-2 mb-2">
                                <AlertCircle size={20} />
                                <span className="font-medium">SQL Validation Errors:</span>
                            </div>
                            <ul className="list-disc list-inside ml-6">
                                {sqlData.validationErrors?.map((error, idx) => (
                                    <li key={idx}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Execution Plan */}
                    {sqlData.executionPlan && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Execution Plan</h3>
                            <div className="space-y-2">
                                {sqlData.executionPlan.paths?.map((path, idx) => (
                                    <div key={idx} className="p-2 bg-gray-50 rounded text-sm">
                                        <span className="font-medium">Path {idx + 1}:</span>{' '}
                                        {path.map(n => n.data?.label || n.type).join(' → ')}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* SQL Query */}
                    <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-2">Generated SQL</h3>
                        <pre className="p-4 bg-gray-900 text-green-400 rounded-md overflow-x-auto text-xs font-mono">
                            {sqlData.sql || 'No SQL generated'}
                        </pre>
                    </div>

                    {/* Errors */}
                    {sqlData.errors && sqlData.errors.length > 0 && (
                        <div>
                            <h3 className="text-sm font-medium text-red-700 mb-2">Errors</h3>
                            <div className="space-y-1">
                                {sqlData.errors.map((error, idx) => (
                                    <div key={idx} className="p-2 bg-red-50 text-red-800 rounded text-sm">
                                        Path {error.path.join(' → ')}: {error.error}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 border-t flex justify-between">
                    <div className="flex gap-2">
                        {sqlData.isValid && onExecute && (
                            <>
                                <button
                                    onClick={() => onExecute(true)}
                                    disabled={executing}
                                    className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-gray-400"
                                >
                                    {executing ? (
                                        <>
                                            <Loader className="animate-spin" size={18} />
                                            Testing...
                                        </>
                                    ) : (
                                        <>
                                            <TestTube size={18} />
                                            Test Run
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={() => onExecute(false)}
                                    disabled={executing}
                                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400"
                                >
                                    {executing ? (
                                        <>
                                            <Loader className="animate-spin" size={18} />
                                            Executing...
                                        </>
                                    ) : (
                                        <>
                                            <Rocket size={18} />
                                            Execute
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// Copy To VoiceBox Modal Component
const CopyToVoiceboxModal = ({ workflowName, groupId, saving, onSave, onClose, isCrossClient }) => {
    const [name, setName] = useState(workflowName ? `${workflowName} (copy)` : '');
    const [locations, setLocations] = useState([]);
    const [selectedLocationIds, setSelectedLocationIds] = useState([]);
    const [importToWorkflows, setImportToWorkflows] = useState(false);
    const [loadingLocations, setLoadingLocations] = useState(false);

    useEffect(() => {
        if (groupId) {
            setLoadingLocations(true);
            apiClient.get('/dealer-locations-with-addresses')
                .then(result => {
                    const groupLocs = (result || []).filter(loc => loc.group_id === groupId);
                    setLocations(groupLocs);
                })
                .catch(() => setLocations([]))
                .finally(() => setLoadingLocations(false));
        }
    }, [groupId]);

    const toggleLocation = (locId) => {
        setSelectedLocationIds(prev =>
            prev.includes(locId) ? prev.filter(id => id !== locId) : [...prev, locId]
        );
    };

    const selectAll = () => {
        if (selectedLocationIds.length === locations.length) setSelectedLocationIds([]);
        else setSelectedLocationIds(locations.map(l => l.id));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                        <Copy size={24} />
                        Save a Copy To VoiceBox
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
                </div>
                <div className="p-6 space-y-5">
                    {isCrossClient && (
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800 flex items-center gap-2">
                            <AlertTriangle size={16} className="flex-shrink-0" />
                            Cross-client copy — trigger(s) will be created in a different VoiceBox client. A matching Contact Plan will be found or created automatically.
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                            placeholder="Trigger name..."
                        />
                    </div>
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label className="block text-sm font-medium text-gray-700">Locations</label>
                            {locations.length > 0 && (
                                <button onClick={selectAll} className="text-xs text-cyan-600 hover:text-cyan-800">
                                    {selectedLocationIds.length === locations.length ? 'Deselect All' : 'Select All'}
                                </button>
                            )}
                        </div>
                        <p className="text-xs text-gray-500 mb-2">
                            Select locations to create a separate trigger per location. Leave empty for a single copy.
                        </p>
                        {loadingLocations ? (
                            <div className="flex items-center gap-2 py-4 justify-center text-gray-500 text-sm">
                                <Loader className="animate-spin" size={16} /> Loading locations...
                            </div>
                        ) : locations.length === 0 ? (
                            <p className="text-gray-400 text-sm text-center py-4">No locations found for this group.</p>
                        ) : (
                            <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
                                {locations.map(loc => (
                                    <label key={loc.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedLocationIds.includes(loc.id)}
                                            onChange={() => toggleLocation(loc.id)}
                                            className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                                        />
                                        <span className="text-sm text-gray-800">{loc.name}</span>
                                        {loc.trading_name && loc.trading_name !== loc.name && (
                                            <span className="text-xs text-gray-400">({loc.trading_name})</span>
                                        )}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={importToWorkflows}
                            onChange={(e) => setImportToWorkflows(e.target.checked)}
                            className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                        />
                        <span className="text-sm text-gray-700">Import to the workflows</span>
                    </label>
                </div>
                <div className="p-6 border-t flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                        Cancel
                    </button>
                    <button
                        onClick={() => onSave({ name: name.trim(), locationIds: selectedLocationIds, importToWorkflows, dryRun: true })}
                        disabled={!name.trim() || saving}
                        className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-md hover:bg-amber-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        {saving ? <><Loader className="animate-spin" size={16} /> Generating...</> : <><Eye size={16} /> Preview</>}
                    </button>
                    <button
                        onClick={() => onSave({ name: name.trim(), locationIds: selectedLocationIds, importToWorkflows, dryRun: false })}
                        disabled={!name.trim() || saving}
                        className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        {saving ? <><Loader className="animate-spin" size={16} /> Saving...</> : <><Copy size={16} /> Save Copy</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Variable Mapping Modal Component
const VariableMappingModal = ({ mappings, availableFields, messageCatalog, onSave, onClose, readOnly = false }) => {
    const [localMappings, setLocalMappings] = useState(mappings || []);
    const [templateSource, setTemplateSource] = useState('voicebox');
    const [selectedMessageId, setSelectedMessageId] = useState(null);
    const [selectedFolder, setSelectedFolder] = useState('');
    const [brands, setBrands] = useState([]);
    const [selectedBrandId, setSelectedBrandId] = useState(null);
    const [brandTemplates, setBrandTemplates] = useState([]);
    const [selectedBrandTemplateId, setSelectedBrandTemplateId] = useState(null);

    useEffect(() => {
        const loadBrands = async () => {
            try {
                const result = await apiClient.get('/brands');
                setBrands(result || []);
            } catch (e) { console.error('Failed to load brands:', e); }
        };
        loadBrands();
    }, []);

    useEffect(() => {
        if (selectedBrandId) {
            const loadBrandTemplates = async () => {
                try {
                    const result = await apiClient.get(`/brand-templates?brand_id=${selectedBrandId}`);
                    setBrandTemplates(result || []);
                } catch (e) {
                    console.error('Failed to load brand templates:', e);
                    setBrandTemplates([]);
                }
            };
            loadBrandTemplates();
        } else {
            setBrandTemplates([]);
        }
    }, [selectedBrandId]);

    const messageFolders = [...new Set(messageCatalog.map(m => m.folder_path).filter(Boolean))].sort();
    const filteredMessages = selectedFolder
        ? messageCatalog.filter(m => m.folder_path === selectedFolder)
        : messageCatalog;
    const sortedMessages = [...filteredMessages].sort((a, b) => (a.message_name || '').localeCompare(b.message_name || ''));

    const selectedMessage = templateSource === 'brand'
        ? brandTemplates.find(t => t.id === selectedBrandTemplateId)
        : messageCatalog.find(m => m.voicebox_message_id === selectedMessageId);

    let availableTags = [];
    if (selectedMessage?.available_tags) {
        if (typeof selectedMessage.available_tags === 'string') {
            try {
                const parsed = JSON.parse(selectedMessage.available_tags);
                availableTags = Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                console.debug('Failed to parse available_tags as JSON:', e);
                availableTags = [];
            }
        } else if (Array.isArray(selectedMessage.available_tags)) {
            availableTags = selectedMessage.available_tags;
        } else if (typeof selectedMessage.available_tags === 'object') {
            availableTags = [];
        }
    }

    const addMapping = () => {
        setLocalMappings([...localMappings, {
            onboarding_field_path: '',
            voicebox_tag_name: '',
            data_source: 'onboarding_app',
            default_value: '',
        }]);
    };

    const updateMapping = (index, field, value) => {
        const updated = [...localMappings];
        updated[index] = { ...updated[index], [field]: value };
        setLocalMappings(updated);
    };

    const removeMapping = (index) => {
        setLocalMappings(localMappings.filter((_, i) => i !== index));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                        <Map size={24} />
                        Variable Mappings
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Template Source Toggle */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Template Source (to see available tags)
                        </label>
                        <div className="flex gap-2 mb-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setTemplateSource('voicebox');
                                    setSelectedBrandTemplateId(null);
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium ${
                                    templateSource === 'voicebox'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                VoiceBox Messages
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setTemplateSource('brand');
                                    setSelectedMessageId(null);
                                    setSelectedFolder('');
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium ${
                                    templateSource === 'brand'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                Brand Templates
                            </button>
                        </div>

                        {templateSource === 'voicebox' && (
                            <div className="space-y-3">
                                {messageFolders.length > 0 && (
                                    <SearchableSelect
                                        value={selectedFolder}
                                        onChange={(val) => {
                                            setSelectedFolder(val);
                                            setSelectedMessageId(null);
                                        }}
                                        options={[
                                            { value: '', label: `All folders (${messageCatalog.length} messages)` },
                                            ...messageFolders.map(folder => ({
                                                value: folder,
                                                label: `${folder} (${messageCatalog.filter(m => m.folder_path === folder).length})`
                                            }))
                                        ]}
                                        placeholder={`All folders (${messageCatalog.length} messages)`}
                                    />
                                )}
                                <SearchableSelect
                                    value={selectedMessageId || ''}
                                    onChange={(val) => setSelectedMessageId(parseInt(val) || null)}
                                    placeholder="Select a message template..."
                                    options={sortedMessages.map(msg => ({
                                        value: msg.voicebox_message_id,
                                        label: `${msg.message_name} (ID: ${msg.voicebox_message_id})`
                                    }))}
                                />
                            </div>
                        )}

                        {templateSource === 'brand' && (
                            <div className="space-y-3">
                                <SearchableSelect
                                    value={selectedBrandId || ''}
                                    onChange={(val) => {
                                        setSelectedBrandId(val);
                                        setSelectedBrandTemplateId(null);
                                    }}
                                    options={brands.map(brand => ({ value: brand.id, label: brand.name }))}
                                    placeholder="Select a brand..."
                                />
                                <SearchableSelect
                                    value={selectedBrandTemplateId || ''}
                                    onChange={(val) => setSelectedBrandTemplateId(val)}
                                    options={brandTemplates.map(t => ({
                                        value: t.id,
                                        label: `${t.message_name} (${t.message_type?.toLowerCase() || 'email'})`
                                    }))}
                                    placeholder={selectedBrandId ? 'Select a brand template...' : 'Select a brand first...'}
                                    disabled={!selectedBrandId}
                                />
                            </div>
                        )}

                        {availableTags.length > 0 && (
                            <div className="mt-3">
                                <p className="text-xs text-gray-600 mb-1">Available tags in this template:</p>
                                <div className="flex flex-wrap gap-2">
                                    {availableTags.map((tag, idx) => (
                                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                            %%{tag}%%
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Mappings List */}
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Mappings</h3>
                            {!readOnly && (
                                <button
                                    onClick={addMapping}
                                    className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                                >
                                    <Plus size={16} />
                                    Add Mapping
                                </button>
                            )}
                        </div>

                        {localMappings.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">
                                No variable mappings.{!readOnly && ' Click "Add Mapping" to create one.'}
                            </p>
                        ) : (
                            <div className="space-y-4">
                                {localMappings.map((mapping, index) => (
                                    <div key={index} className="border rounded-lg p-4">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {mapping.data_source !== 'static' ? (
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Clientbox Field
                                                    </label>
                                                    <select
                                                        value={mapping.onboarding_field_path}
                                                        onChange={(e) => updateMapping(index, 'onboarding_field_path', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                                                    >
                                                        <option value="">Select field...</option>
                                                        {(() => {
                                                            const generalFields = availableFields.filter(f => !f.path.startsWith('dealer_locations.') && !f.path.startsWith('location_factfind.'));
                                                            const locationFields = availableFields.filter(f => f.path.startsWith('dealer_locations.'));
                                                            const factfindFields = availableFields.filter(f => f.path.startsWith('location_factfind.'));
                                                            return (
                                                                <>
                                                                    {generalFields.length > 0 && (
                                                                        <optgroup label="General">
                                                                            {generalFields.map(f => <option key={f.path} value={f.path}>{f.label}</option>)}
                                                                        </optgroup>
                                                                    )}
                                                                    {locationFields.length > 0 && (
                                                                        <optgroup label="Location Fields (dealer_locations)">
                                                                            {locationFields.map(f => <option key={f.path} value={f.path}>{f.label}</option>)}
                                                                        </optgroup>
                                                                    )}
                                                                    {factfindFields.length > 0 && (
                                                                        <optgroup label="Fact Find (location_template_data)">
                                                                            {factfindFields.map(f => <option key={f.path} value={f.path}>{f.label}</option>)}
                                                                        </optgroup>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                    </select>
                                                </div>
                                            ) : (
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Static Value
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={mapping.default_value || ''}
                                                        onChange={(e) => updateMapping(index, 'default_value', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                                                        placeholder="Enter static value..."
                                                    />
                                                </div>
                                            )}
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    VoiceBox Tag Name (CamelCase)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={mapping.voicebox_tag_name}
                                                    onChange={(e) => updateMapping(index, 'voicebox_tag_name', e.target.value)}
                                                    className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                                                    placeholder="e.g., DealershipName"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Data Source
                                                </label>
                                                <select
                                                    value={mapping.data_source}
                                                    onChange={(e) => updateMapping(index, 'data_source', e.target.value)}
                                                    className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                                                >
                                                    <option value="onboarding_app">Onboarding App</option>
                                                    <option value="static">Static Value</option>
                                                </select>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => removeMapping(index)}
                                            className="mt-2 text-red-600 hover:text-red-800 text-sm"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-6 border-t flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                        {readOnly ? 'Close' : 'Cancel'}
                    </button>
                    {!readOnly && (
                        <button
                            onClick={() => onSave(localMappings)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            <CheckCircle size={18} />
                            Save Mappings
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// Execution Logs Modal Component
const ExecutionLogsModal = ({ logs, onRefresh, onClose }) => {
    const getStatusBadge = (status) => {
        const badges = {
            running: { color: 'bg-blue-100 text-blue-800', label: 'Running' },
            completed: { color: 'bg-green-100 text-green-800', label: 'Completed' },
            failed: { color: 'bg-red-100 text-red-800', label: 'Failed' },
            cancelled: { color: 'bg-gray-100 text-gray-800', label: 'Cancelled' }
        };

        const badge = badges[status] || badges.running;
        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                {badge.label}
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                        <History size={24} />
                        Execution Logs
                    </h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onRefresh}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                            title="Refresh"
                        >
                            <Loader size={20} />
                        </button>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <X size={24} />
                        </button>
                    </div>
                </div>

                <div className="p-6">
                    {logs.length === 0 ? (
                        <div className="text-center py-12">
                            <History className="mx-auto text-gray-400" size={48} />
                            <p className="text-gray-600 mt-4">No execution logs found</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {logs.map((log) => (
                                <div key={log.id} className="border rounded-lg p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="flex items-center gap-3">
                                            {getStatusBadge(log.status)}
                                            <span className="text-sm text-gray-600">
                                                {log.execution_type === 'dry_run' ? 'Test Run' : 'Live Execution'}
                                            </span>
                                        </div>
                                        <span className="text-xs text-gray-500">
                                            {formatDate(log.started_at)}
                                        </span>
                                    </div>
                                    
                                    {log.executed_by_name && (
                                        <div className="text-sm text-gray-600 mb-2">
                                            Executed by: {log.executed_by_name}
                                        </div>
                                    )}

                                    {(log.records_processed !== null || log.records_succeeded !== null) && (
                                        <div className="text-sm text-gray-700 mb-2">
                                            Records processed: <span className="font-semibold">{log.records_processed || 0}</span>
                                            {log.records_succeeded !== null && (
                                                <span className="ml-4 text-green-600">
                                                    Succeeded: <span className="font-semibold">{log.records_succeeded}</span>
                                                </span>
                                            )}
                                            {log.records_failed !== null && log.records_failed > 0 && (
                                                <span className="ml-4 text-red-600">
                                                    Failed: <span className="font-semibold">{log.records_failed}</span>
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {log.completed_at && (
                                        <div className="text-xs text-gray-500 mb-2">
                                            Completed: {formatDate(log.completed_at)}
                                        </div>
                                    )}

                                    {log.error_message && (
                                        <div className="mt-2 p-2 bg-red-50 text-red-800 rounded text-sm">
                                            <strong>Error:</strong> {log.error_message}
                                        </div>
                                    )}

                                    {log.execution_details && (() => {
                                        try {
                                            const details = typeof log.execution_details === 'string' 
                                                ? JSON.parse(log.execution_details) 
                                                : log.execution_details;
                                            
                                            if (details.message) {
                                                return (
                                                    <div className="mt-2 text-sm text-gray-700">
                                                        {details.message}
                                                    </div>
                                                );
                                            }
                                        } catch (e) {
                                            return null;
                                        }
                                    })()}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="p-6 border-t flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminWorkflowBuilderPage;

